/**
 * Pinia productEditor store (Boulders card "Product editor (hi-fi redesign)",
 * pebble 3 — Track 1: STORE; pebble 12 — Save orchestration).
 *
 * Backs the new hi-fi product editor shell. Owns the JSON:API server-side
 * view of a single campaign_product (plus included relationships), per-field
 * dirty tracking (Map keyed by attribute name), pending buckets for staged
 * decorations / variants / skip-color toggles, and the load/save lifecycle.
 *
 * Style: setup-style Pinia (matches `account.js`). Plain JS with JSDoc.
 *
 * Reactivity note: Vue 3 does not deeply track Map mutations through a `ref`
 * holding a Map. To trigger reactivity on add/delete, we REPLACE the ref
 * value with a freshly-cloned Map rather than mutating in place. See
 * memory: feedback on Vue 3 reactivity around Map/Set.
 *
 * Empty relationships: SAFRS sometimes returns `{ data: [] }` or
 * `{ data: null }` for to-many / to-one relationships. We leave the shape
 * as-is in `serverState`; consumers must guard. See
 * `project_empty-relationship-objects.md`.
 *
 * Save orchestration (pebble 12): `save()` walks every pending bucket in two
 * groups — Group A (PATCH campaign_products + POST new decorations) runs in
 * parallel; Group B (POST variants with artwork uploads, POST/DELETE skip
 * colors) runs after A so it can resolve pending decoration _tempIds to
 * server ids. On completion, `loadServerState()` refetches the canonical
 * state. SIMPLIFICATION: on partial failure, all pending buckets are still
 * cleared after reload — the user sees `saveErrors[]` listing what failed
 * and can redo any item manually. This trades surgical retry for state-
 * machine simplicity.
 */
import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import axiosInstance from '@/axios'
import {
    notSellableColorKeys,
    planUnsellableColorDrop,
    unsellableColorMessage,
    unsellableKeysToHide,
    withoutUnsellable,
} from '@/utils/unsellableColors'
import {
    withColorUpload,
    withoutColorUploads,
    withoutRenderImages,
} from '@/utils/storefrontImages'
import { NON_STANDARD_SIZES } from '@/pages/distributor/store-editor/StoreProducts/Editor/sizeConstants'
import { resolveShadeColorwayId } from '@/pages/distributor/store-editor/StoreProducts/Editor/defaultColorway'

/**
 * Artwork upload validation (pebble 8a /gate — security Low).
 *
 * Cheap pre-flight guard before we mutate store state with a user-supplied
 * file. The server-side upload path lands in pebble 12; until then this
 * helper enforces the same envelope on the client so blob URLs aren't
 * created for oversized files or hostile MIME types. Returns `null` on a
 * valid file, or a human-readable error string otherwise.
 *
 * Why client-side validation despite "server is the trust boundary":
 * pebble 8a creates a blob preview via `URL.createObjectURL` for the file
 * BEFORE any server round-trip. A 2 GB drop would burn browser memory and
 * a `.exe` masquerading as artwork would still render harmlessly but
 * cement bad UX. Pebble 12 will repeat these checks server-side.
 *
 * The 25 MB cap is generous enough for high-DPI PNG/JPEG mockups while
 * still rejecting accidental video drops. SVG, PNG, JPEG are the three
 * formats the file input's `accept` attribute already advertises; this
 * helper enforces the same list against `file.type` (which the file
 * input's accept attribute does NOT — accept is a UX hint, not a guard).
 *
 * Exported so the test suite can exercise the boundary independently of
 * store-state setup.
 *
 * @param {Blob|null|undefined} file
 * @returns {string|null}
 */
export const MAX_ARTWORK_BYTES = 25 * 1024 * 1024
export const ALLOWED_ARTWORK_MIME = new Set([
    'image/svg+xml',
    'image/png',
    'image/jpeg',
])
export function validateArtworkFile(file) {
    if (!file) return 'No file selected'
    if (file.size > MAX_ARTWORK_BYTES) {
        return `File too large (${(file.size / 1024 / 1024).toFixed(1)} MB; max 25 MB)`
    }
    if (!ALLOWED_ARTWORK_MIME.has(file.type)) {
        return `Unsupported file type: ${file.type || 'unknown'}`
    }
    return null
}

/**
 * Read a dot-path from a nested object. Returns undefined if any segment is
 * missing. No lodash dependency — pebble 12 may swap in a more thorough
 * implementation when relationship paths land.
 *
 * @param {object|null|undefined} obj
 * @param {string} path
 * @returns {*}
 */
function getByPath(obj, path) {
    if (obj == null) return undefined
    const segments = path.split('.')
    let cursor = obj
    for (const seg of segments) {
        if (cursor == null) return undefined
        cursor = cursor[seg]
    }
    return cursor
}

/**
 * Resolve the parent decoration id for a JSON:API included entry (Variant,
 * DecorationSkipColor, or PersonalizationField). Prefers the canonical
 * `relationships.decoration.data.id` path, but falls back to
 * `attributes.decoration_id` (coerced to string) when the relationship
 * object is null/missing.
 *
 * Why the fallback: SAFRS does not always populate the reverse-relationship
 * `relationships.decoration.data` on included child entries — only the
 * `attributes.decoration_id` foreign-key value is reliably present. Reading
 * only the relationship path silently dropped variants and skip-colors
 * from the editor view, which in turn caused the empty-upload UI to render
 * over a decoration that secretly already had a server-side variant. The
 * user then dropped artwork and hit a duplicate-key UniqueViolation on
 * `(decoration_id, variant_key)` at save time. See PR fix(product-editor):
 * P0 variant_key collision + SAFRS reverse-relationship workaround.
 *
 * Returns `null` when neither path yields a value (orphaned row).
 *
 * @param {object|null|undefined} entry — JSON:API included entry
 * @returns {string|null}
 */
function getVariantParentId(entry) {
    // P5.5 PR-3.5 (task #195): prefer the placement-rooted parent
    // pointer (canonical post-cleanup-chain). Fall back to the legacy
    // decoration-rooted pointer so existing test fixtures and any
    // straggler dual-read payloads still resolve. After P5.5 PR-6
    // (decoration_id -> decoration_id rename) and P6 PR-2 (legacy
    // table drop), the `decoration` branches become dead aliases.
    const fromPlacementRel
        = entry?.relationships?.placement?.data?.id
    if (fromPlacementRel != null) return String(fromPlacementRel)
    const fromPlacementAttr = entry?.attributes?.placement_id
    if (fromPlacementAttr != null) return String(fromPlacementAttr)
    const fromRel = entry?.relationships?.decoration?.data?.id
    if (fromRel != null) return String(fromRel)
    const fromAttr = entry?.attributes?.decoration_id
    if (fromAttr != null) return String(fromAttr)
    return null
}

/**
 * Coerce a JSON:API scalar to a finite Number, or null.
 *
 * SAFRS serializes foreign keys as numbers on `attributes` but as STRINGS
 * inside `relationships.<rel>.data.id`, and several consumers type-check
 * rather than duck-type -- DecorationInspector's `boundDecorationId`
 * requires `typeof id === "number"` and its `dimensionsLabel` requires
 * `Number.isFinite` on both dims. Normalizing here means the view-model
 * carries one shape whichever wire form arrived.
 */
function _asFiniteNumber(value) {
    if (value == null || value === '') return null
    const n = Number(value)
    return Number.isFinite(n) ? n : null
}

/**
 * The related resource's id off a JSON:API entry, or null. Mirrors the
 * `entry?.relationships?.<name>?.data?.id` walk used throughout the
 * include resolution above.
 */
function _relationshipId(entry, name) {
    return entry?.relationships?.[name]?.data?.id ?? null
}

/**
 * A placement's per-color approval override as the badge reads it --
 * `{id, status}` -- or null when the placement inherits its parent record.
 * The API serves it as the derived Placement attribute `per_color_approval`
 * (GH #3833); anything malformed reads as "no override" rather than as an
 * override with no status.
 */
function _perColorApproval(value) {
    const id = _asFiniteNumber(value?.id)
    if (id == null || typeof value?.status !== 'string') return null
    return { id, status: value.status }
}

/**
 * Cheap deep-equal good enough for primitives, plain objects, and arrays
 * shaped like JSON. Functions / Dates / Maps not in scope (server state is
 * JSON:API, all JSON-shaped). Avoids pulling in lodash.isEqual.
 */
function deepEqual(a, b) {
    if (a === b) return true
    if (a == null || b == null) return a === b
    if (typeof a !== typeof b) return false
    if (typeof a !== 'object') return false
    if (Array.isArray(a) !== Array.isArray(b)) return false
    if (Array.isArray(a)) {
        if (a.length !== b.length) return false
        for (let i = 0; i < a.length; i++) {
            if (!deepEqual(a[i], b[i])) return false
        }
        return true
    }
    const aKeys = Object.keys(a)
    const bKeys = Object.keys(b)
    if (aKeys.length !== bKeys.length) return false
    for (const k of aKeys) {
        if (!deepEqual(a[k], b[k])) return false
    }
    return true
}

/**
 * Diff two color_overrides maps and return the per-sku changes that need
 * to be forwarded to the F2 ``product_color_variant_routing`` writer.
 *
 * The legacy ``color_overrides`` jsonb shape:
 *   ``{<color_sku>: "light" | "dark"}``  (the "auto" sentinel is stripped
 *   on the way to the server -- absence == auto).
 *
 * F2 ``variant_key`` mapping:
 *   ``"light"`` -> ``"A"``
 *   ``"dark"``  -> ``"B"``
 *   absent      -> null (delete the routing row)
 *
 * Returns an array of ``{color_sku, variant_key}`` entries -- one per
 * sku that differs between prev and next. Each entry maps directly to a
 * single PATCH /api/campaign_products/<id>/color_routing call.
 *
 * Wave 2 PR-1 (F2.d). The triple-write is best-effort -- the bespoke
 * endpoint failing must NOT fail the user's save; the legacy
 * ``manual_ld`` + ``color_overrides`` writers stay authoritative until
 * PR-2 collapses the resolver onto F2 + PR-3 drops ``color_overrides``.
 *
 * @param {object} prev -- previous (server) color_overrides map
 * @param {object} next -- next (dirty) color_overrides map
 * @returns {Array<{color_sku: string, variant_key: 'A' | 'B' | null}>}
 */
export function diffColorOverridesToF2(prev, next) {
    const safePrev = (prev && typeof prev === 'object') ? prev : {}
    const safeNext = (next && typeof next === 'object') ? next : {}
    const verdictToKey = (v) => {
        if (v === 'light' || v === 'L') return 'A'
        if (v === 'dark' || v === 'D') return 'B'
        return null
    }
    const changes = []
    const allSkus = new Set([
        ...Object.keys(safePrev),
        ...Object.keys(safeNext),
    ])
    for (const sku of allSkus) {
        const prevKey = verdictToKey(safePrev[sku])
        const nextKey = verdictToKey(safeNext[sku])
        if (prevKey === nextKey) continue
        changes.push({ color_sku: sku, variant_key: nextKey })
    }
    return changes
}


/**
 * Campaign-product attributes the SERVER computes and the editor only reads.
 * They are never written through updateField (so never sent in a PATCH);
 * after a successful field autosave their value is taken from the PATCH
 * response, because the write just changed what they are derived from.
 *
 * `price_preview` ({size: cents}) is the server's per-size quote -- the
 * editor's only source of a Store-pricing price (2026-09-29).
 */
export const SERVER_DERIVED_ATTRIBUTES = Object.freeze(['price_preview'])

// The server's refusal to make a product visible while a placement's
// location isn't valid for its SKU (card #545; SAFRS errors[0].detail).
// Pinned against the server by api/tests/fixtures/contracts/unhide_refused.json.
export const UNHIDE_REFUSED_DETAIL
    = "This product can't be shown on the storefront while a placement's "
    + "location isn't valid for this SKU. Choose a new location for the "
    + 'flagged placement, or remove it, then try again.'

// The saved attributes a price breakdown depends on (the inputs of the
// server's price_for: mode, sizes, supplier cost, store, supplier and
// style) and the included
// rows that set the decoration cost. See `priceBreaksKey`.
const PRICE_BREAK_ATTRIBUTES = Object.freeze([
    'pricing_mode',
    'price_basis',
    'selected_sizes',
    'per_size_supplier_cost',
    'blank_cost_basis_snapshot',
    'store_id',
    'campaign_id',
    // Which remembered manual blank cost applies (manual_blank_costs is
    // keyed on the supplier catalog and the style).
    'supplier_id',
    'product_item_number',
    'supplier_style_id',
])
const PRICE_BREAK_INCLUDED_TYPES = new Set([
    'Placement',
    'placements',
    'Decoration',
    'decorations',
])

/**
 * Build the JSON:API PATCH body for campaign_products. Pebble 12 — emits
 * proper {type, id, attributes} shape. Dirty Map is flat (attribute names).
 *
 * @param {Map<string, *>} dirty
 * @param {number|string} campaignProductId
 * @returns {object}
 */
export function buildCpPatchBody(dirty, campaignProductId) {
    return {
        data: {
            type: 'CampaignProduct',
            id: String(campaignProductId),
            attributes: Object.fromEntries(dirty),
        },
    }
}

/**
 * Build the JSON:API POST body for a new decoration. Pebble 12.
 *
 * @param {object} pendingDeco — entry from pendingDecorations
 * @param {number|string} campaignProductId
 * @param {number} displayOrder
 * @returns {object}
 */
export function buildDecorationPostBody(
    pendingDeco,
    campaignProductId,
    displayOrder
) {
    return {
        data: {
            type: 'Decoration',
            attributes: {
                campaign_product_id: campaignProductId,
                placement_id: pendingDeco.placement_id,
                name: pendingDeco.name || '',
                description: pendingDeco.description || null,
                display_order: displayOrder,
                // decorations.decoration_type is NOT NULL on the backend and
                // restricted to {embroidery, dtf, silk_screen, dtg,
                // sublimation}. Until the editor exposes a type picker on
                // the decoration card, default to 'embroidery' (matches the
                // Path A inline-upload picker, PR #1413). Without this the
                // POST 500s with NotNullViolation.
                decoration_type:
                    pendingDeco.decoration_type || 'embroidery',
            },
        },
    }
}

/**
 * Build the JSON:API POST body for a new variant. Pebble 12.
 *
 * PR-N4: the legacy ``variant_key`` column has been dropped on both
 * sides. The client contributes the routing identity (``kind`` +
 * ``color_skus``); the backend persists it as-is. No transitional
 * autofill listener remains.
 *
 * The Variant model keys on ``placement_id`` (FK ``placements.id``,
 * NOT NULL) post P5.5 PR-1 / P6 PR-2 -- migration
 * ``route_fork_schema_082`` dropped the transient ``decoration_id``
 * passenger column (its FK was retired in ``route_fork_schema_071``).
 * A POST carrying ``decoration_id`` is a SAFRS unknown-attribute:
 * silently ignored on create, so ``placement_id`` arrives NULL and the
 * ``before_insert`` guard raises 400 "Cannot create Variant:
 * placement_id is required" -- the write never reaches the DB. Emit
 * the canonical ``placement_id``.
 *
 * Callers already hold the placement id: the staged ``variant.parent``
 * comes from ``getVariantParentId``, which resolves
 * ``relationships.placement.data.id`` / ``attributes.placement_id``
 * first, and the ``tmp-`` branch resolves through ``tempIdToServerId``
 * (populated by POST /api/placements/).
 *
 * @param {object} pendingVariant — entry from pendingVariants
 * @param {string|number} placementId — resolved server Placement id
 * @param {string|null} artworkFileId — uuid returned by /api/upload_image
 * @returns {object}
 */
export function buildVariantPostBody(
    pendingVariant,
    placementId,
    artworkFileId
) {
    // PR-L: carry kind + color_skus through to the backend on create.
    // Defaults match the Variant model server defaults: 'catch_all_light'
    // + empty color_skus. Callers that staged via addVariant get the
    // values they passed (or the model defaults via the addVariant
    // default-fill path).
    const kind = pendingVariant.kind || 'catch_all_light'
    const colorSkus = Array.isArray(pendingVariant.color_skus)
        ? pendingVariant.color_skus.slice()
        : []
    return {
        data: {
            type: 'Variant',
            attributes: {
                placement_id: placementId,
                // PR-N4: variant_key column dropped; routing identity
                // is carried entirely by kind + color_skus.
                kind,
                color_skus: colorSkus,
                artwork_file_id: artworkFileId || null,
                artwork_filename: pendingVariant.artwork_filename || null,
                ink_color_hex: pendingVariant.ink_color_hex || null,
                display_order: pendingVariant.display_order || 0,
            },
        },
    }
}

/**
 * Build the JSON:API POST body for a new decoration_skip_color. Pebble 12.
 *
 * The DecorationSkipColor model keys on ``placement_id`` (FK
 * ``placements.id``) post P5.5 PR-1 / P6 PR-2 -- migration
 * ``route_fork_schema_082`` dropped the transient ``decoration_id``
 * passenger column. A POST carrying ``decoration_id`` is a SAFRS
 * unknown-attribute: silently ignored on create, so the intended key
 * never lands and the write no-ops. Emit the canonical ``placement_id``.
 *
 * The editor's per-row key IS the Placement id post P5.5 PR-3.5 (the
 * view-model "decoration" row's id is the Placement row's id), so
 * callers already hold the placement id under whatever they named the
 * variable.
 *
 * @param {string|number} placementId — Placement row id (FK placements.id)
 * @param {string} colorSku
 * @returns {object}
 */
export function buildSkipColorPostBody(placementId, colorSku) {
    return {
        data: {
            type: 'DecorationSkipColor',
            attributes: {
                placement_id: placementId,
                color_sku: colorSku,
            },
        },
    }
}

/**
 * Build the JSON:API POST body for a new personalization_field. Pebble 8b PR 3.
 *
 * Carries the parent ``placement_id`` FK (resolved if the source was a
 * pending decoration tempId) and the user-supplied attributes.
 * `options` is JSONB (list of strings) for select fields, null
 * otherwise. `max_length` is meaningful for text + select only; left
 * nullable everywhere else.
 *
 * Same canonical-key contract as ``buildVariantPostBody`` above: the
 * PersonalizationField model keys on ``placement_id`` (FK
 * ``placements.id``, NOT NULL) and the ``decoration_id`` passenger
 * column is gone, so emitting ``decoration_id`` would be silently
 * dropped by SAFRS and trip the ``before_insert`` guard.
 *
 * @param {object} pendingField — entry from pendingPersonalizationFields
 * @param {string|number} placementId — resolved server Placement id
 * @returns {object}
 */
export function buildPersonalizationFieldPostBody(pendingField, placementId) {
    return {
        data: {
            type: 'PersonalizationField',
            attributes: {
                placement_id: placementId,
                label: pendingField.label ?? '',
                field_type: pendingField.field_type ?? 'text',
                required: !!pendingField.required,
                max_length: pendingField.max_length ?? null,
                options: pendingField.options ?? null,
                display_order: Number.isFinite(pendingField.display_order)
                    ? pendingField.display_order
                    : 0,
            },
        },
    }
}

/**
 * Build the JSON:API PATCH body for an existing personalization_field.
 * Pebble 8b PR 3. Attributes object carries only the patched fields
 * (caller's responsibility — store's edit Map already holds a partial
 * patch, not a full attribute object).
 *
 * @param {string|number} id — server-side personalization_field id
 * @param {object} patch — partial attribute patch
 * @returns {object}
 */
export function buildPersonalizationFieldPatchBody(id, patch) {
    return {
        data: {
            type: 'PersonalizationField',
            id: String(id),
            attributes: { ...patch },
        },
    }
}

/**
 * Module-private monotonic counter for staged-decoration temporary ids.
 * Monotonic (NOT crypto.randomUUID) so tests can assert exact values like
 * `'tmp-1'` without flakiness and to keep behavior stable across older
 * jsdom environments that may lack the WebCrypto surface.
 *
 * Note: counter is module-scoped, not store-scoped. With pinia
 * `setActivePinia(createPinia())` between tests, each test creates a fresh
 * store instance but the module counter persists for the lifetime of the
 * test worker. Tests that rely on the exact starting value should only
 * assert on the first call within their case (e.g., `tmp-1` immediately
 * after a fresh store) — subsequent counters drift across tests. This is
 * fine for production: tempIds only need to be unique within an editor
 * session, not globally meaningful.
 */
let _tempIdCounter = 0
function nextTempId() {
    _tempIdCounter += 1
    return `tmp-${_tempIdCounter}`
}

// SAFRS auto-derives the JSON:API `type` field from the model class
// name (PascalCase): "Decoration", "Variant", "DecorationSkipColor",
// "PersonalizationField", "CampaignProduct", etc. Earlier pebbles
// were authored against unit-test mocks that used lowercase plural
// snake_case ("decorations", "variants", etc.) — a contract drift that
// silently broke ALL server-decoration/variant/skip-color/personalization
// rendering against real API responses. This helper normalizes both
// forms so the store works against real SAFRS payloads AND existing
// test fixtures. Map keys are canonical lowercase plural; values are
// the set of strings (PascalCase model class name + lowercase plural)
// the store accepts as equivalent.
// P5.5 PR-3.5 (task #195): the editor's "decoration" view-model row
// is now sourced from JSON:API `Placement` / `placements` entries in
// the included payload (placement-rooted include path).  The legacy
// 'Decoration' / 'decorations' tokens stayed in the alias set as
// dual-read backstops; with P6 PR-2 the slim Decoration substrate
// occupies those type names but the editor still treats only the
// Placement rows as view-model "decoration" rows.  The store walker
// branches at line ~895 on `entry.type === 'Placement'` to filter
// Decoration-typed entries back out of the merge.
const _ENTITY_TYPE_ALIASES = {
    decorations: new Set([
        'decorations',
        'Decoration',
        'placements',
        'Placement',
    ]),
    variants: new Set(['variants', 'Variant']),
    decoration_skip_colors: new Set([
        'decoration_skip_colors',
        'DecorationSkipColor',
    ]),
    personalization_fields: new Set([
        'personalization_fields',
        'PersonalizationField',
    ]),
}

function isEntityType(entry, kind) {
    const aliases = _ENTITY_TYPE_ALIASES[kind]
    if (!aliases) return false
    return aliases.has(entry?.type)
}

export const useProductEditorStore = defineStore('productEditor', () => {
    // ---- state ----
    /** @type {import('vue').Ref<number|string|null>} */
    const campaignProductId = ref(null)
    /** @type {import('vue').Ref<object|null>} */
    const serverState = ref(null)
    /** @type {import('vue').Ref<Map<string, *>>} */
    const dirty = ref(new Map())
    const loading = ref(false)
    /** @type {import('vue').Ref<Error|null>} */
    const loadError = ref(null)
    const saveInFlight = ref(false)
    /** @type {import('vue').Ref<Error|null>} */
    const saveError = ref(null)

    // PR-4b (light up the Stage): DST/EMB artwork preview state, keyed by
    // the string ``artwork_file_id`` a Variant references (== str(Artwork.id)).
    // The store-editor Stage renders the four preview states off
    // ``activeVariant.preview``; that field is stamped onto each variant
    // view-model from this map (see the `decorations` computed). The SAFRS
    // ``?include=placements.variants`` payload cannot carry it (a read
    // serialization must not commit the lazy-render enqueue transition), so
    // ``_hydratePreviewStates`` fetches it in one explicit round-trip after
    // each load and populates this reactive map. Empty map -> every variant
    // preview is null -> the Stage falls through to its Artifi render (the
    // pre-PR-4b behavior, zero regression).
    /** @type {import('vue').Ref<Map<string, object>>} */
    const _previewByArtworkId = ref(new Map())
    // One preview per artwork VERSION (Jake, 2026-10-21): a product shows the
    // picture of the version its decoration PINS, so a newer file minted on
    // the artwork does not repaint products that already use the old one.
    // Keyed by String(artwork_version_id); filled by _hydratePreviewStates.
    const _previewByVersionId = ref(new Map())

    // Card #573 (Jake, 2026-10-28): the low-resolution verdict of each flagged
    // placement, keyed by String(placement id), for the placement list's icon.
    // The SAFRS include payload cannot carry a computed verdict without a lazy
    // load per placement, so ``_hydratePlacementResolution`` asks ONE batched
    // read after each load (and so after every size or artwork change, which
    // reload). Empty map -> no icons (fail open).
    /** @type {import('vue').Ref<Map<string, object>>} */
    const _resolutionByPlacementId = ref(new Map())

    // ---- field-only autosave (MVP, per Jake 2026-07-09) ----
    // Scalar field edits (the `dirty` Map) autosave on a debounce, mirroring
    // the store editor's dirty -> saving -> saved | error idiom. Structural
    // changes (decorations / variants / skip-colors / personalization /
    // reorders / deletes) stay behind the explicit Save button and are NEVER
    // fired by autosave. Full autosave (removing the button) is deferred
    // post-MVP: https://trello.com/c/3v7gvA8L. Design doc:
    // docs/planning/2026-07-10-product-editor-autosave-proposal.md.
    //
    // Single-source state machine (design-philosophy.md single-source-state):
    // ONE ref drives every autosave-state read; there is no second "dirty"
    // flag. The predicate for "settled but not flushed" is the `dirty` Map
    // being non-empty; the predicate for "structural work is pending" is the
    // `hasUnsavedStructuralChanges` computed below.
    //
    // States: 'idle' (nothing to save) -> 'dirty' (edit settled, debounce
    // armed) -> 'saving' (PATCH in flight) -> 'saved' (PATCH ok; reverts to
    // 'idle' after a short delay) | 'error' (PATCH failed; dirty Map kept for
    // the next debounce to retry).
    /** @type {import('vue').Ref<'idle'|'dirty'|'saving'|'saved'|'error'>} */
    const fieldAutoSaveState = ref('idle')

    // Bumped once per SUCCESSFUL save of the product -- a field autosave
    // flush or an explicit Save. The editor's per-color render trigger
    // (useStorefrontColorRenders) debounces on it: renders run the SA-set
    // delay (default 5s) after the LAST save, so a distributor can toggle colors freely before anything is
    // rendered (Jake, 2026-10-07). A counter, not a timestamp, so two saves in
    // the same millisecond still restart the debounce.
    /** @type {import('vue').Ref<number>} */
    const saveCount = ref(0)

    // Bumped when a placement is created OUTSIDE the product save: the
    // Add-placement wizard POSTs it itself, then the rail reloads the
    // editor (notePlacementSaved). saveCount never moves for it, so without
    // this the render triggers never heard about the new placement and the
    // Product Listing the wizard closes onto rendered nothing (Bug Backlog
    // #356). useStorefrontColorRenders treats it as a save.
    /** @type {import('vue').Ref<number>} */
    const placementSaveCount = ref(0)

    function notePlacementSaved() {
        placementSaveCount.value += 1
    }

    // Debounce window for coalescing rapid field edits into one PATCH.
    // ~700ms matches the store editor's feel (StoreAddEdit uses 350ms for a
    // heavier full-tree save; the scalar PATCH is cheap, so a slightly longer
    // window keeps rapid typing to a single round-trip).
    const FIELD_AUTOSAVE_DEBOUNCE_MS = 700
    // How long the 'saved' badge lingers before falling back to 'idle'.
    const FIELD_AUTOSAVE_SAVED_LINGER_MS = 1500

    // Debounce timer handle + re-entry guard + pending re-arm flag. Mirror
    // StoreAddEdit's _autoSaveTimer / _autoSaveInFlight / _autoSavePending.
    // These are module-lifecycle-scoped to the store instance (setup Pinia),
    // NOT reactive state — they are pure control-flow bookkeeping.
    let _fieldAutoSaveTimer = null
    let _fieldAutoSaveInFlight = false
    let _fieldAutoSavePending = false
    // Settles when the autosave currently in flight finishes (null when none
    // is). flushFieldAutoSave awaits it so a route leave cannot resolve while
    // an edit is still queued behind that flight.
    let _fieldAutoSaveFlightDone = null
    // The dirty snapshot the in-flight autosave is sending (null when none
    // is). Lets isFieldEditQueuedBehindFlight tell a newer edit from the one
    // already on the wire.
    let _fieldAutoSaveFlightSnapshot = null

    // Supplier meta — published by Section 1 (SupplierSkuPanel) after
    // useSanmarProductMeta resolves; consumed by Section 2 (storefront
    // listing) for default product_name and image lightbox sources. Owned
    // by the store so cross-section reads don't require prop drilling.
    // Cleared on openEditor / closeEditor to prevent cross-product leaks.
    /** @type {import('vue').Ref<object|null>} */
    const supplierMeta = ref(null)

    // Supplier-meta loading flag. supplierMeta is null both before a fetch
    // starts AND while one is in flight; readers that resolve colors/sizes
    // against it (ColorFilmstrip, LeftRail counts, ColorsPanel) must not
    // treat "not loaded yet" as "loaded and empty" -- doing so synthesizes
    // wrong-colored swatches and flashes a misleading "0 colors" for a
    // product that actually has many. This flag lets those readers show a
    // loading/placeholder state instead. Pushed from the section that owns
    // the useSanmarProductMeta fetch (SupplierSkuPanel) via
    // setSupplierMetaLoading. Cleared on openEditor / closeEditor.
    /** @type {import('vue').Ref<boolean>} */
    const supplierMetaLoading = ref(false)

    // The supplier-meta fetch FAILED (its composable's `error` is set).
    // supplierMeta cannot say so: the shell publishes a resolved-empty `{}`
    // before the fetch (readiness = supplierMeta != null, the #2003 design)
    // and a failed fetch leaves the composable's meta null, so the store
    // keeps that `{}`. Readers that must tell "failed" from "genuinely
    // empty" (the listing pool's empty-state copy) read this flag. Pushed
    // by the shell with setSupplierMetaFailed; the composable clears its
    // error when a retry starts, which clears this. Reset on closeEditor.
    /** @type {import('vue').Ref<boolean>} */
    const supplierMetaFailed = ref(false)

    // The clean-up a CONFIRMED SKU swap still owes the product once the NEW
    // style's catalog has loaded: `{ sku, oldNames }` (the new SKU, and the
    // names of the colors selected before the swap). Held HERE rather than in
    // the Supplier & SKU panel because that panel is only mounted while its
    // tab is active -- a clean-up that lived in the panel was lost whenever
    // the user left the tab before the catalog arrived (card #349). Consumed
    // once by useSkuSwapCleanup, which the always-mounted editor shell runs.
    // Cleared on openEditor / closeEditor with the other per-product state.
    /** @type {import('vue').Ref<{sku: string, oldNames: string[], previous?: object}|null>} */
    const pendingSwapCleanup = ref(null)

    // True after a supplier switch cleared the SKU of a product that had one:
    // the product still carries the OLD item's name, descriptions and sizes, so
    // the next SKU pick is a swap (its catalog values overwrite them), not a
    // first pick that only fills empty fields (card #349). Held here, not in
    // the Supplier & SKU panel, so leaving the tab between the switch and the
    // pick does not lose it. Cleared by the pick; reset with the product.
    const skuClearedForSwitch = ref(false)
    /**
     * What the product held BEFORE a supplier switch cleared its SKU, colors
     * and listing images (card #542). A failed lookup for the new supplier's
     * item restores it; a landed swap discards it. First snapshot wins.
     * @type {import('vue').Ref<object|null>}
     */
    const supplierSwitchSnapshot = ref(null)

    // The open product's supplier_key, as ProductEditor resolves it from the
    // vendor list (null while unresolved). Published here because the shell
    // is the only place that holds the vendor options, while the Stage,
    // filmstrip, panels and storefront renders all need to gate Artifi on the
    // KEY (``artifiPreviewEligible``). Not cleared on open/close: the shell's
    // publisher re-runs whenever the effective supplier changes, and a reset
    // here could land AFTER its last publish and strand the next product on
    // null.
    /** @type {import('vue').Ref<string|null>} */
    const supplierKey = ref(null)
    // False until the shell has resolved the supplier against the vendor
    // list. Distinguishes "no key YET" from "resolved, and there is no key"
    // (see artifiPreviewEligible).
    /** @type {import('vue').Ref<boolean>} */
    const supplierKeyResolved = ref(false)

    // The Pricing tab's per-size breakdown (Jake, 2026-10-06): every cost,
    // charge, price, margin and markup at every quantity break, as the
    // server quotes it (GET /campaign_product/<id>/price_breaks).
    //
    // Each response is STAMPED with `priceBreaksKey` -- the saved product's
    // pricing inputs at fetch time -- and `priceBreaks` reads it only while
    // the key still matches. After a save that changes a pricing input it
    // reads null until the refetch lands, so no surface shows numbers for a
    // product state that is gone. Null too before the first load and after
    // a failed one (`priceBreaksError` says which).
    const _priceBreaksData = ref(null)
    const _priceBreaksDataKey = ref(null)
    /** @type {import('vue').Ref<Error|null>} */
    const priceBreaksError = ref(null)
    let _priceBreaksToken = 0

    // Single-source predicate for "supplier meta is usable". Meta is ready
    // once a non-null payload has been published; while null (whether a
    // fetch is pending or has not started) it is NOT ready. Downstream
    // readers gate on this rather than each re-deriving the null check.
    const supplierMetaReady = computed(() => supplierMeta.value != null)

    // Single-source predicate for "no further supplier-catalog answer is
    // coming": the supplier key is resolved (so no fetch is waiting on the
    // vendor list) AND no fetch is in flight. Distinct from
    // `supplierMetaReady`, which is true whenever SOME payload exists -- the
    // shell publishes `{}` before the product's SKU loads, so ready reads
    // true while the real request is still running (measured on 8090).
    // Deliberately does NOT require a payload: a failed fetch leaves meta
    // null with loading cleared, and that is final too.
    //
    // For readers that must act ONCE on the final garment hexes -- the
    // variant cards' auto-default colorway, whose bind buys a paid Artifi
    // composite and is never revisited.
    //
    // `supplierKeyResolved` is deliberately NOT reset by openEditor /
    // closeEditor (see `supplierKey` above): the shell republishes it
    // synchronously during the next product's setup, BEFORE onMounted calls
    // openEditor, so a reset there would land after that publish and strand
    // an absent-supplier product with settled === false and no default
    // bound. Pinned by ProductEditor.colorway-settle-switch.spec.js.
    const supplierMetaSettled = computed(
        () => supplierKeyResolved.value && !supplierMetaLoading.value,
    )

    /**
     * The SAVED product's pricing inputs, as a string, or null before it has
     * loaded: the attributes `price_for` reads, plus the product's placements
     * and decorations (which set the decoration cost). A save that changes
     * none of them -- a name, a description, a fixed price -- leaves it as
     * is, so the breakdown is neither refetched nor hidden.
     */
    const priceBreaksKey = computed(() => {
        const state = serverState.value
        const attrs = state?.data?.attributes
        if (campaignProductId.value == null || !attrs) return null
        const decorationRows = (Array.isArray(state.included) ? state.included : [])
            .filter((e) => PRICE_BREAK_INCLUDED_TYPES.has(e?.type))
            .map((e) => `${e.type}:${e.id}`)
            .sort()
        return JSON.stringify([
            String(campaignProductId.value),
            PRICE_BREAK_ATTRIBUTES.map((name) => attrs[name] ?? null),
            decorationRows,
        ])
    })

    const priceBreaks = computed(() =>
        _priceBreaksDataKey.value !== null
        && _priceBreaksDataKey.value === priceBreaksKey.value
            ? _priceBreaksData.value
            : null,
    )

    // PEBBLE 12 ENTRY POINT:
    // Frontend-only staged decoration adds (pebble 7 Track A). Each entry is
    // a plain object `{ _tempId, placement_id, name, description }`. The
    // tempId distinguishes a staged add from a server-loaded decoration in
    // the merged `decorations` getter below; pebble 12 will translate this
    // list into the JSON:API relationship-create payload at save time and
    // then drop the tempIds in favor of returned server ids.
    /** @type {import('vue').Ref<Array<{_tempId: string, placement_id: string, name: string, description: string|null}>>} */
    const pendingDecorations = ref([])

    // PEBBLE 12 ENTRY POINT:
    // Frontend-only staged variant adds (pebble 8a Track A). Each entry is
    // `{ _tempId, parent, kind, color_skus, artwork_file, artwork_filename,
    //   artwork_object_url, ink_color_hex, display_order }`. `parent` is
    // either a server-side decoration id (string) OR a pending decoration
    // `_tempId`. Pebble 12 will:
    //   - POST pending variants under their decoration (resolving _tempId
    //     parents to the server ids returned by the decoration creates).
    //   - DELETE any server-side variants collected in
    //     `pendingVariantDeletes` (this 8a slice doesn't yet support
    //     deleting server-loaded variants — only pending ones — so the
    //     delete set isn't populated here. The collection point is flagged
    //     in `removeVariant` below as a TODO for 8b/12.)
    /** @type {import('vue').Ref<Array<object>>} */
    const pendingVariants = ref([])

    // Skip-color stage: ADDS keyed by decoration id-or-_tempId → Set<color_sku>.
    // Pebble 12 emits POSTs from this bucket. toggleSkipColor routes a "turn
    // off a server-loaded color" into pendingSkipColorDeletes (below) so we
    // don't conflate add and delete on the same key.
    /** @type {import('vue').Ref<Map<string, Set<string>>>} */
    const pendingSkipColors = ref(new Map())

    // Skip-color stage: DELETES keyed by decoration SERVER id → Set<color_sku>.
    // Only populated for server-loaded skip_colors that the user toggled off.
    // Pending decorations cannot have server-side skip_colors so this map
    // only ever uses real decoration ids as keys. Pebble 12 emits DELETEs
    // from this bucket by looking up the skip-color row's id in
    // serverState.included.
    /** @type {import('vue').Ref<Map<string, Set<string>>>} */
    const pendingSkipColorDeletes = ref(new Map())

    // PEBBLE 8b PR 1 — Reorder edits keyed by decoration server id → new
    // display_order. Pending (staged) decorations carry their display_order
    // in the pending entry itself; reorder of a pending row updates that
    // entry directly. This Map only ever holds SERVER ids — save() PATCHes
    // each row to its new display_order.
    /** @type {import('vue').Ref<Map<string, number>>} */
    const pendingDecorationOrderEdits = ref(new Map())

    // Variant reorder edits keyed by variant server id → new display_order.
    // Same shape and discipline as pendingDecorationOrderEdits.
    /** @type {import('vue').Ref<Map<string, number>>} */
    const pendingVariantOrderEdits = ref(new Map())

    // Pebble #162/#172 — Delete placement (server-loaded decoration delete).
    // A plain Set of server decoration ids staged for DELETE on next save().
    // Pending (staged) decorations are removed from `pendingDecorations`
    // directly by id (_tempId) — they never appear here.
    // Clearing discipline: clear on openEditor, closeEditor, revert,
    // and post-save (mirrors pendingPersonalizationFieldDeletes).
    /** @type {import('vue').Ref<Set<string>>} */
    const pendingDecorationDeletes = ref(new Set())

    // PEBBLE 8b PR 3 — Personalization field state machine. Three buckets
    // mirror the skip-color machine but with edits, because personalization
    // rows are mutable (skip-colors are just on/off SKU memberships).
    //
    //  - pendingPersonalizationFields: pending ADDS. Each entry is a plain
    //    object `{_tempId, parent, label, field_type, required, max_length,
    //    options, display_order}`. `parent` is either a SERVER decoration
    //    id string OR a pending decoration `_tempId`. Resolved at save time
    //    via `tempIdToServerId` (Group A → Group B.5 ordering).
    //
    //  - pendingPersonalizationFieldEdits: edits to SERVER fields. Keyed by
    //    server id → partial attribute patch object. The store keeps only
    //    the diff (not a full attribute set) so multiple edits merge cleanly.
    //
    //  - pendingPersonalizationFieldDeletes: deletes of SERVER fields. A
    //    plain Set of server ids. Save emits one DELETE per entry.
    //
    // Clearing discipline: all three buckets clear on openEditor, closeEditor,
    // revert, and post-save (regardless of partial failure, mirroring the
    // skip-color SIMPLIFICATION at save()).
    /** @type {import('vue').Ref<Array<object>>} */
    const pendingPersonalizationFields = ref([])
    /** @type {import('vue').Ref<Map<string, object>>} */
    const pendingPersonalizationFieldEdits = ref(new Map())
    /** @type {import('vue').Ref<Set<string>>} */
    const pendingPersonalizationFieldDeletes = ref(new Set())

    // ---- sticky authoring intent (Jake, 2026-09-12) ----
    // A ONE-WAY LATCH: set the first time the user takes any authoring action
    // on the currently-open product, and cleared ONLY when the editor moves to
    // a different product (openEditor with a new id) or tears down
    // (closeEditor). Nothing else clears it -- not save, not autosave, not
    // revert, not an edit undone back to its server value.
    //
    // WHY IT CANNOT BE `unsavedChangeCount`
    // -------------------------------------
    // Every bucket that counter sums is PENDING WORK, and pending work drains:
    // `saveDirtyFields` replaces `dirty` with a fresh empty Map, and `revert`
    // empties all ten buckets at once. A user who picks a SKU, waits ~700ms
    // for the autosave debounce, and then leaves would read as count 0 --
    // byte-identical to a product nobody ever opened. Keying the stub-discard
    // decision on those counters would delete configured products, which is
    // the one unrecoverable mistake this feature can make.
    //
    // So this is a SEPARATE ref with the opposite lifecycle: pending-work
    // counters answer "is there anything left to save?", this answers "was a
    // human ever here?". Those are different questions and they need different
    // state (design-philosophy.md: one mechanism per concept).
    //
    // WHERE IT IS SET
    // ---------------
    // Inside the store's own mutating actions, via `_markAuthoringIntent()`,
    // rather than at the ~30 component call sites. The store is the single
    // choke point every authoring surface already funnels through -- the
    // panels call `updateField`, the inspector calls the variant/decoration
    // actions, the filmstrip calls `assignColor`. Instrumenting callers would
    // leave the flag one new component away from being wrong.
    //
    // SCOPE (accepted by Jake): in-memory only. It does not survive a page
    // reload, so create -> configure -> reload -> leave KEEPS the row rather
    // than discarding it. That is the safe direction, and Jake called reload
    // "firmly in the 20% camp" of an 80/20 fix. Do not add persistence.
    /** @type {import('vue').Ref<boolean>} */
    const _hasAuthoringIntent = ref(false)

    /**
     * Latch the authoring-intent flag. Idempotent and deliberately unguarded:
     * every caller is already a user-initiated mutation, so there is no
     * condition under which setting it is wrong. The ONLY way it goes back to
     * false is openEditor(different id) / closeEditor.
     */
    function _markAuthoringIntent() {
        _hasAuthoringIntent.value = true
    }

    // Collected save errors (pebble 12). Populated by `save()` whenever any
    // group A/B request fails. `saveError` (singular, kept for back-compat)
    // mirrors the FIRST entry. UI surfaces `saveErrors` as a list.
    /** @type {import('vue').Ref<Array<{message: string, item?: object}>>} */
    const saveErrors = ref([])

    // In-flight load token — incremented on each `loadServerState`,
    // `openEditor`, and `closeEditor`. Each call captures the token and only
    // commits its result if the token still matches when the response lands.
    // Closes the rapid mount/unmount + retry race where a stale GET
    // resolution would otherwise clobber the current product's state.
    // See: project_inflight-promise-guard-pattern.md
    const _loadToken = ref(0)

    // In-flight load promise dedup. The token above is a STALE-WRITE guard:
    // it discards results from late-resolving responses so the wrong product
    // can't clobber state. It does NOT prevent the network request from
    // firing — each call to loadServerState unconditionally fires a GET.
    // That meant a remount loop or any caller-side fan-out produced N
    // identical network round-trips for the same campaign_product id.
    //
    // _loadInFlight memoizes the active promise per campaignProductId so
    // concurrent same-id callers attach to the existing request instead of
    // starting a new one. Cleared in the load's finally block. Cross-id
    // callers (openEditor switched mid-flight) still fire fresh requests —
    // the dedup is keyed by id, not unconditional. See:
    // project_inflight-promise-guard-pattern.md (Layer 1 of the duplicate-
    // GET perf fix; Layer 2 chases the remount-or-re-call root cause).
    /** @type {import('vue').Ref<{ id: any, promise: Promise<void> } | null>} */
    const _loadInFlight = ref(null)

    // Layer 1.v2 - serial-recall short-circuit.
    //
    // The in-flight dedup above only protects CONCURRENT same-id callers.
    // The Layer 2 root cause (an App.vue containerKey watcher that bumped
    // on every reactive tick; fix is in the same PR) drove the symptom by
    // remounting <v-main> -> <router-view> -> ProductEditor.vue, which
    // re-ran onMounted -> openEditor + loadServerState IN SEQUENCE.
    // openEditor clears _loadInFlight, so each iteration's loadServerState
    // saw no active promise and started a fresh GET.
    //
    // Defense-in-depth: track the timestamp of the last successful load
    // per campaignProductId. If the same id is reloaded within the
    // throttle window AND serverState is still populated, skip the GET.
    // This caps a remount-loop's network footprint at ~1 GET per 250ms
    // regardless of caller behavior - a future Layer-2 regression (or
    // any new caller-side fan-out) cannot reintroduce the duplicate-GET
    // pattern.
    //
    // Cleared on closeEditor. Cross-id openEditor preserves the new id's
    // entry (if any) and clears the rest, bounding map size across long
    // sessions that open many products.
    //
    // The 250ms window is chosen to be:
    //   - long enough to absorb any plausible mount/unmount/remount race
    //     (Vue scheduler tick + microtask drain is well under 50ms)
    //   - short enough that a real user-driven reload (Save button ->
    //     refresh) feels instant and is never blocked (PATCH + UI
    //     feedback take well over 250ms in practice)
    /** @type {import('vue').Ref<Map<any, number>>} */
    const _lastLoadedAt = ref(new Map())
    const _SERIAL_THROTTLE_MS = 250

    // ---- getters ----
    const isDirty = computed(() => dirty.value.size > 0)
    const dirtyFieldCount = computed(() => dirty.value.size)

    /**
     * Count of pending changes across every bucket: scalar dirty Map,
     * pendingDecorations, pendingVariants, pendingSkipColors (sum of Set
     * sizes), pendingSkipColorDeletes (sum of Set sizes). Drives the SaveBar
     * counter and the Save button's enabled/disabled state.
     */
    const unsavedChangeCount = computed(() => {
        let count = dirty.value.size
        count += pendingDecorations.value.length
        count += pendingVariants.value.length
        for (const set of pendingSkipColors.value.values()) count += set.size
        for (const set of pendingSkipColorDeletes.value.values()) {
            count += set.size
        }
        // Pebble 8b PR 1: reorder edits count as unsaved changes. Each
        // edited row contributes 1, regardless of how far it moved.
        count += pendingDecorationOrderEdits.value.size
        count += pendingVariantOrderEdits.value.size
        // Pebble 8b PR 3: personalization field add/edit/delete buckets.
        count += pendingPersonalizationFields.value.length
        count += pendingPersonalizationFieldEdits.value.size
        count += pendingPersonalizationFieldDeletes.value.size
        // Pebble #162/#172: decoration deletes (server-loaded rows staged for DELETE).
        count += pendingDecorationDeletes.value.size
        return count
    })

    const hasUnsavedChanges = computed(() => unsavedChangeCount.value > 0)

    /**
     * Has the user taken ANY authoring action on the open product?
     *
     * STICKY -- see `_hasAuthoringIntent` above for why this is not derived
     * from `unsavedChangeCount`. Read by the stub-discard route guard: a
     * product with intent is NEVER discarded, whatever its fields look like.
     */
    const hasAuthoringIntent = computed(() => _hasAuthoringIntent.value)

    // ---- sticky "has had a SKU" latch (card #542) ----
    // A ONE-WAY LATCH, set the first time the open product carries a SKU --
    // loaded from the server or picked -- and cleared only when a different
    // product is opened or the editor closes. The leave guard reads it so a
    // saved product whose SKU was blanked (supplier switch, failed lookup) is
    // never offered for deletion: "blank right now" cannot tell a draft nobody
    // finished from a configured product that lost its SKU. Watched (sync) on
    // the PERSISTED (server) SKU only: an unsaved pick that then fails must not
    // latch (a draft whose first pick fails is still a draft; see
    // clearEverHadSku, called by failSwapCleanup).
    const _hasEverHadSku = ref(false)
    const hasEverHadSku = computed(() => _hasEverHadSku.value)
    /** Drop the latch: a failed first SKU pick left the draft SKU-less. */
    function clearEverHadSku() {
        _hasEverHadSku.value = false
    }
    function _isNonBlankSku(value) {
        return value != null && String(value).trim() !== ''
    }
    watch(
        () => _isNonBlankSku(serverState.value?.data?.attributes?.product_item_number),
        (has) => {
            if (has) _hasEverHadSku.value = true
        },
        { flush: 'sync', immediate: true },
    )

    // Field-only autosave (MVP): the Save button lights up ONLY for unsaved
    // STRUCTURAL changes. Scalar edits (the `dirty` Map) autosave, so they no
    // longer keep the button lit. `unsavedChangeCount` sums every bucket
    // including the scalar dirty Map; subtracting `dirtyFieldCount` leaves the
    // count of pending STRUCTURAL work only (decorations / variants /
    // skip-colors / personalization / reorders / deletes). True iff any
    // structural bucket is non-empty.
    const hasUnsavedStructuralChanges = computed(
        () => unsavedChangeCount.value - dirtyFieldCount.value > 0
    )

    const canSave = computed(
        () =>
            hasUnsavedChanges.value
            && !saveInFlight.value
            && !loading.value
            && !loadError.value
    )

    /**
     * Merged decoration view (pebble 7 Track A, extended in pebble 8a).
     * Combines server-loaded decorations (from `serverState.included`
     * filtered by `type === 'decorations'`) with pending (staged)
     * decorations, and hydrates a `variants` array on each entry.
     *
     * Order: server entries first, sorted by id ascending (stable across
     * re-loads when the server returns them in arbitrary order), then
     * pending entries in insertion order.
     *
     * Unified shape:
     *   `{id?, _tempId?, placement_id, name, description, display_order,
     *     variants, _staged}`
     *
     * - `_staged === true` iff this entry came from `pendingDecorations`.
     * - `variants` is always an array. For server decorations: filtered
     *   from `serverState.included` where `type === 'variants'` AND
     *   `relationships.decoration.data.id` matches this decoration's id.
     *   For pending decorations: filtered from `pendingVariants` where
     *   `parent === decoration._tempId`. Variants are sorted by
     *   `display_order ASC` then by stable row identifier (server id,
     *   else pending tempId).
     */
    const decorations = computed(() => {
        const included = Array.isArray(serverState.value?.included)
            ? serverState.value.included
            : []

        // Pre-collect server skip_colors grouped by parent decoration id
        // (pebble 9). Walking once is friendlier on big payloads.
        const serverSkipSkusByDeco = new Map()
        for (const entry of included) {
            if (!isEntityType(entry, 'decoration_skip_colors')) continue
            const parentId = getVariantParentId(entry)
            if (parentId == null) continue
            const sku = entry?.attributes?.color_sku
            if (sku == null) continue
            if (!serverSkipSkusByDeco.has(parentId)) {
                serverSkipSkusByDeco.set(parentId, new Set())
            }
            serverSkipSkusByDeco.get(parentId).add(sku)
        }

        // Pebble 8b PR 3: pre-collect server personalization_fields grouped
        // by parent decoration id. Walking once mirrors the skip-colors +
        // variants pattern above.
        const serverPersonalizationFieldsByDeco = new Map()
        for (const entry of included) {
            if (!isEntityType(entry, 'personalization_fields')) continue
            const parentId = getVariantParentId(entry)
            if (parentId == null) continue
            const attrs = entry.attributes ?? {}
            const pf = {
                id: entry.id,
                label: attrs.label ?? '',
                field_type: attrs.field_type ?? 'text',
                required: !!attrs.required,
                max_length: attrs.max_length ?? null,
                options: attrs.options ?? null,
                display_order: Number.isFinite(attrs.display_order)
                    ? attrs.display_order
                    : 0,
                _staged: false,
            }
            if (!serverPersonalizationFieldsByDeco.has(parentId)) {
                serverPersonalizationFieldsByDeco.set(parentId, [])
            }
            serverPersonalizationFieldsByDeco.get(parentId).push(pf)
        }

        // Helper: build the merged personalization_fields array for a
        // decoration (server-id OR pending _tempId). For server decorations
        // we start with server fields, apply pending edits (merge patch),
        // filter out pending deletes (by server id), then concatenate
        // pending adds whose `parent` matches the decoration's id. For
        // pending decorations there are no server fields and no edits/
        // deletes; only pending adds whose `parent === _tempId` apply.
        const mergePersonalizationFields = (decoIdOrTempId) => {
            const serverFields
                = serverPersonalizationFieldsByDeco.get(decoIdOrTempId) ?? []
            const editsMap = pendingPersonalizationFieldEdits.value
            const deletesSet = pendingPersonalizationFieldDeletes.value
            const merged = []
            for (const pf of serverFields) {
                if (deletesSet.has(pf.id)) continue
                const patch = editsMap.get(pf.id)
                if (patch) {
                    merged.push({ ...pf, ...patch })
                } else {
                    merged.push(pf)
                }
            }
            // Pending adds for this decoration. `parent` matches the
            // server id (string) OR the pending decoration _tempId.
            for (const pf of pendingPersonalizationFields.value) {
                if (pf.parent !== decoIdOrTempId) continue
                merged.push({ ...pf })
            }
            // Sort by display_order ASC; ties fall through to insertion
            // order (stable sort, server first then pending).
            merged.sort((a, b) => {
                const ao = Number.isFinite(a.display_order)
                    ? a.display_order
                    : 0
                const bo = Number.isFinite(b.display_order)
                    ? b.display_order
                    : 0
                return ao - bo
            })
            return merged
        }

        // The artwork version each placement's decoration pins -- the version
        // whose preview its variants show (see _previewByVersionId).
        const versionByPlacementId = _versionByPlacementId(included)

        // Pre-collect server variants grouped by parent decoration id.
        // Walking once vs. once-per-decoration is friendlier on big payloads.
        const serverVariantsByDeco = new Map()
        for (const entry of included) {
            if (!isEntityType(entry, 'variants')) continue
            const parentId = getVariantParentId(entry)
            if (parentId == null) continue
            const attrs = entry.attributes ?? {}
            const variant = {
                id: entry.id,
                artwork_file_id: attrs.artwork_file_id ?? null,
                artwork_filename: attrs.artwork_filename ?? null,
                artwork_object_url: null,
                artwork_file: null,
                ink_color_hex: attrs.ink_color_hex ?? null,
                // Colorway binding (card https://trello.com/c/vc8U5340).
                // BOTH halves have to be projected, because the surfaces
                // split the work between them: VariantCard reads
                // `colorway_id` for the picker's check-mark and trigger
                // label and Stage folds it into the preview cache key,
                // while the swatch row renders `colorway_threads_json`.
                //
                // `colorway_id` is PROVENANCE ONLY -- ADR 0044 -- and must
                // never be traversed back to the live colorway row to render
                // threads. The snapshot below is what the variant actually
                // sews; the live row may have been edited since.
                colorway_id: attrs.colorway_id ?? null,
                colorway_threads_json: Array.isArray(attrs.colorway_threads_json)
                    ? attrs.colorway_threads_json
                    : null,
                display_order: Number.isFinite(attrs.display_order)
                    ? attrs.display_order
                    : 0,
                // PR-M: carry kind + color_skus through to consumers so
                // DecorationInspector's existingSpecificColorSkus computed
                // can see server-loaded specific_colors variants and
                // exclude their routed colors from the add-specific
                // picker. Defaults preserve back-compat for legacy rows
                // that pre-date PR-J's column add (treated as catch-all).
                kind: attrs.kind ?? null,
                color_skus: Array.isArray(attrs.color_skus)
                    ? attrs.color_skus.slice()
                    : null,
                // Slice 1 (editable variant cards): optional custom name.
                // Null for un-renamed rows -> VariantCard falls back to
                // the kind-derived header label (byte-identical to today).
                name: attrs.name ?? null,
                // PR-4b (light up the Stage): the DST/EMB preview sidecar
                // {state, preview_url, ...} for the artwork this variant
                // references, or null (raster/SVG artwork with no sidecar,
                // or preview not hydrated yet). Read reactively from the
                // hydration map so a later fetch re-stamps every variant.
                // The Stage maps this through previewDisplay() to the four
                // display states; null -> kind 'none' -> Artifi fallback.
                //
                // The PINNED version's preview when the decoration names one;
                // a pinned version with no preview (a raster file) is null --
                // it must never borrow the artwork's newer file's picture.
                preview: _previewForVariant(
                    attrs.artwork_file_id,
                    versionByPlacementId.get(String(parentId)),
                ),
                _staged: false,
            }
            if (!serverVariantsByDeco.has(parentId)) {
                serverVariantsByDeco.set(parentId, [])
            }
            serverVariantsByDeco.get(parentId).push(variant)
        }

        const sortVariants = (vs) =>
            vs.slice().sort((a, b) => {
                if (a.display_order !== b.display_order) {
                    return a.display_order - b.display_order
                }
                // PR-N4: variant_key column gone. Tie-break by stable
                // row identifier (server id, then pending tempId) so
                // the order remains deterministic.
                const ak = String(a.id ?? a._tempId ?? '')
                const bk = String(b.id ?? b._tempId ?? '')
                return ak.localeCompare(bk)
            })

        // P5.5 PR-3.5 fix (bar-raiser P0): Placement carries the
        // binding (campaign_product + placement_zone + decoration)
        // but NONE of the display-layer columns the view-model needs
        // (no `name`, no `decoration_type`, no `display_order`). Those
        // live on the chained includes: PlacementZone.display_name for
        // the row label, DecorationProcess.slug for the technique. Pre-
        // build lookup maps over the chained includes so the merge
        // below is O(decorations) instead of O(decorations * included).
        const decorationById = new Map()
        const decorationProcessById = new Map()
        const placementZoneById = new Map()
        for (const entry of included) {
            const type = entry?.type
            // Post-P6 PR-2 the slim Decoration substrate occupies the
            // ``Decoration`` / ``decorations`` JSON:API type names.
            if (type === 'Decoration' || type === 'decorations') {
                decorationById.set(String(entry.id), entry)
            } else if (
                type === 'DecorationProcess'
                || type === 'decoration_processes'
            ) {
                decorationProcessById.set(String(entry.id), entry)
            } else if (
                type === 'PlacementZone'
                || type === 'placement_zones'
            ) {
                placementZoneById.set(String(entry.id), entry)
            }
        }
        // Resolve a Placement entry to its merged display attributes by
        // walking placement.decoration -> .method (technique slug) and
        // placement.placement_zone -> .display_name (row label).
        // Tolerates missing chain links (returns sensible empty
        // defaults) so the row still renders if the backend omits a
        // relationship target during transitional states.
        const resolvePlacementDisplay = (placementEntry) => {
            const rels = placementEntry?.relationships ?? {}
            const attrs = placementEntry?.attributes ?? {}
            // decoration -> method.slug for decoration_type.
            const decorationRelId = rels.decoration?.data?.id
            const decoration = decorationRelId != null
                ? decorationById.get(String(decorationRelId))
                : null
            const methodId = decoration?.relationships?.method?.data?.id
            const method = methodId != null
                ? decorationProcessById.get(String(methodId))
                : null
            const decorationTypeSlug = method?.attributes?.slug ?? null
            // placement_zone -> display_name for name.
            const zoneRelId = rels.placement_zone?.data?.id
            // Fallback to attributes.placement_zone_id (SAFRS sometimes
            // emits the FK only).
            const zoneId = zoneRelId != null
                ? String(zoneRelId)
                : (attrs.placement_zone_id != null
                    ? String(attrs.placement_zone_id)
                    : null)
            const zone = zoneId != null
                ? placementZoneById.get(zoneId)
                : null
            const zoneName = zone?.attributes?.display_name ?? null
            // Canvas view-code fix: also surface the zone SLUG (e.g.
            // "left_chest"). productEditorUiState.selectedView resolves the
            // Artifi view from this slug -- it must NOT read the placement
            // PK (`placement_id`), which is a number after the P6 PR-A
            // rename and previously fell through to the wrong "Full Center".
            const zoneSlug = zone?.attributes?.slug ?? null
            // Imprint dimensions live on the bound decoration (decorations_v2
            // width_in/height_in), surfaced so the LeftRail subline can read
            // "Embroidery - 4in x 4in".
            const decAttrs = decoration?.attributes ?? {}
            // The NESTED zone object, kept alongside the flattened
            // slug/id. DecorationInspector's `zoneNameLabel` reads
            // `placement.placement_zone` and takes `.name` OR `.label`
            // off it (DecorationInspector.vue:1101-1106), so a bare slug
            // string cannot satisfy it.
            const zoneObject = zone
                ? {
                    id: zoneId,
                    name: zoneName,
                    label: zoneSlug,
                    slug: zoneSlug,
                }
                : null
            return {
                name: zoneName,
                decoration_type: decorationTypeSlug,
                placement_zone_id: zoneId,
                placement_zone_slug: zoneSlug,
                placement_zone: zoneObject,
                width_in: decAttrs.width_in ?? null,
                height_in: decAttrs.height_in ?? null,
                artwork_version_id: decAttrs.artwork_version_id ?? null,
            }
        }

        // P6 PR-2: the alias set still includes the legacy
        // `Decoration` / `decorations` tokens (now occupied by the
        // SLIM digitization-artifact substrate after the rename).
        // In a placement-rooted payload the Placement is the view-model
        // row -- the Decoration is the chained include used to resolve
        // display attrs above. The payload is placement-rooted when the
        // primary resource declares a `placements` relationship, or when
        // any Placement-typed entry is present. The relationship check is
        // load-bearing: deleting the LAST placement prunes every Placement
        // entry but leaves its chained Decoration behind, and without it
        // that Decoration resurfaced as a phantom row until a reload
        // (Trello saGDU067). Only legacy fixtures that emit bare
        // `Decoration` / `decorations` rows fall through to treating the
        // Decoration row as the view-model row for back-compat.
        const placementRooted
            = serverState.value?.data?.relationships?.placements != null
            || included.some(
                e => e?.type === 'Placement' || e?.type === 'placements'
            )

        const server = []
        for (const entry of included) {
            if (!isEntityType(entry, 'decorations')) continue
            if (
                placementRooted
                && (entry.type === 'Decoration' || entry.type === 'decorations')
            ) {
                continue
            }
            // Pebble #162/#172 — filter out decorations staged for delete.
            if (pendingDecorationDeletes.value.has(entry.id)) continue
            const attrs = entry.attributes ?? {}
            // Merged display attrs from chained includes
            // (placements.decoration.method, placements.placement_zone).
            // Falls back to attrs.name / attrs.decoration_type for
            // legacy / placement-less fixtures.
            const displayAttrs = (
                entry.type === 'Placement' || entry.type === 'placements'
            )
                ? resolvePlacementDisplay(entry)
                : {
                    name: null,
                    decoration_type: null,
                    placement_zone_id: null,
                    placement_zone_slug: attrs.placement_zone_slug ?? null,
                }
            const variants = sortVariants(
                serverVariantsByDeco.get(entry.id) ?? []
            )
            const serverSkipSet = serverSkipSkusByDeco.get(entry.id)
            const pendingSkipSet
                = pendingSkipColors.value.get(entry.id) ?? new Set()
            const pendingDeleteSet
                = pendingSkipColorDeletes.value.get(entry.id) ?? new Set()
            const allSkipSkus = new Set([
                ...(serverSkipSet ?? []),
                ...pendingSkipSet,
            ])
            // Pebble 12: pending deletes remove from the effective set.
            for (const del of pendingDeleteSet) allSkipSkus.delete(del)
            // Pebble 8b PR 1: apply pending display_order edits when the
            // user has reordered this row but not yet saved.
            //
            // P5.5 PR-3.5 fix (bar-raiser P0): Placement has no
            // `display_order` column (legacy Decoration did). The
            // canonical server-side ordering is `Placement.id ASC`
            // (see campaign_product.py:253 `order_by="Placement.id"`).
            // Project the numeric id onto display_order so the sort
            // below is deterministic AND user reorders via
            // pendingDecorationOrderEdits override correctly. Legacy
            // Decoration rows (transitional) still carry `display_order`
            // in attrs and take precedence; coerce to Number for the
            // id-based fallback.
            const attrOrder = Number.isFinite(attrs.display_order)
                ? attrs.display_order
                : null
            const idAsOrder = Number(entry.id)
            const baseOrder = attrOrder != null
                ? attrOrder
                : (Number.isFinite(idAsOrder) ? idAsOrder : 0)
            const editedOrder = pendingDecorationOrderEdits.value.get(
                entry.id
            )
            server.push({
                id: entry.id,
                // P5.5 PR-3.5 fix (bar-raiser P0): Placement IS the
                // placement -- its id IS the placement_id the view
                // model references for routing, child-walking, and
                // selection. The legacy Decoration row carried a
                // separate STRING `placement_id` (zone slug); the new
                // substrate makes Placement.id the canonical pointer.
                // Transitional Decoration rows still expose their
                // `attrs.placement_id` slug for back-compat in mocks.
                placement_id: attrs.placement_id ?? entry.id,
                // Zone SLUG for the Stage's selectedView resolution (bug:
                // selectedView must map the zone slug -> Artifi view code,
                // NOT the placement row id). resolvePlacementDisplay walks
                // placement.placement_zone.slug; legacy fixtures may carry
                // it on attrs directly.
                placement_zone_slug:
                    displayAttrs.placement_zone_slug
                    ?? attrs.placement_zone_slug
                    ?? null,
                name: displayAttrs.name ?? attrs.name ?? '',
                description: attrs.description ?? null,
                // Polish Day 1 round-3 fix: include decoration_type so
                // surfaces like the LeftRail subline ("Left chest .
                // Embroidery") can read the technique. The store
                // previously dropped this field silently — every other
                // place that builds a decoration row (addDecoration,
                // applyDecorationEdit, materializePendingDecoration)
                // carries decoration_type; the server-row branch was an
                // oversight.
                // P5.5 PR-3.5 fix (bar-raiser P0): for Placement rows,
                // the slug comes from the chained
                // decoration.method.slug walk; only fall back to
                // 'embroidery' if BOTH the chained walk and the legacy
                // attr are absent.
                decoration_type:
                    displayAttrs.decoration_type
                    ?? attrs.decoration_type
                    ?? 'embroidery',
                // Canvas view-code fix: carry the zone id alongside the zone
                // slug above so productEditorUiState.selectedView resolves
                // the Artifi view from the real zone (e.g. "left_chest")
                // instead of misreading the numeric placement PK.
                placement_zone_id:
                    displayAttrs.placement_zone_id
                    ?? attrs.placement_zone_id
                    ?? null,
                // Imprint dimensions for the LeftRail subline
                // ("Embroidery - 4in x 4in"). From the bound decoration.
                width_in:
                    displayAttrs.width_in
                    ?? attrs.width_in
                    ?? null,
                height_in:
                    displayAttrs.height_in
                    ?? attrs.height_in
                    ?? null,
                // Garment-fit warning (#2445 Track A). Derived, read-only
                // attribute served on the Placement itself -- does this
                // placement's requested size exceed the garment's
                // printable area for its zone? Computed server-side
                // (api/src/services/placement_fit.py over the existing
                // placement_compatibility verdict engine) and rendered by
                // DecorationInspector. `null` = fits, or the server had
                // insufficient data to judge; normalized here so the
                // consumer never sees `undefined`.
                //
                // Purely advisory per Jake's 2026-08-14 decision: it
                // gates nothing and the placement always saves.
                fit_warning: attrs.fit_warning ?? null,
                // Per-color approval override (GH #3833), derived and
                // served on the Placement. PerColorApprovalBadge reads it
                // verbatim; without this copy every placement read
                // "Inherits approval", including ones WITH an override.
                per_color_approval: _perColorApproval(attrs.per_color_approval),
                // ---- Fields DecorationInspector's placement-details row
                // reads VERBATIM. Kept alongside (not instead of) the
                // names above, which the LeftRail subline and the Artifi
                // view-code resolution already depend on.
                //
                // 2026-09-12: these four were missing, so
                // `hasPlacementDetails` (DecorationInspector.vue:1111) was
                // false for every placement and the row never rendered on
                // the real surface -- while its component spec passed by
                // hand-building rows in the serializer's vocabulary. The
                // API does serve `decoration_id` on the Placement
                // attributes (verified live: int, not a string), it was
                // simply never copied onto the view-model row.
                //
                // `boundDecorationId` requires `typeof id === "number"`,
                // so coerce rather than pass a JSON:API string through.
                decoration_id: _asFiniteNumber(
                    attrs.decoration_id
                    ?? _relationshipId(entry, 'decoration'),
                ),
                imprint_width_in: _asFiniteNumber(
                    displayAttrs.width_in ?? attrs.width_in,
                ),
                imprint_height_in: _asFiniteNumber(
                    displayAttrs.height_in ?? attrs.height_in,
                ),
                placement_zone: displayAttrs.placement_zone ?? null,
                // The bound Decoration's artwork version. DecorationInspector
                // keys the colorway fetch (and the per-color approval
                // request) on `placement.artwork_version_id`. The API serves
                // it on the included Decoration, never on the Placement, so
                // without this copy the id was always null and the colorway
                // control could not render on ANY placement (2026-09-28).
                artwork_version_id: _asFiniteNumber(
                    displayAttrs.artwork_version_id ?? attrs.artwork_version_id,
                ),
                // Size (inches) the preview drain measured from the pinned
                // stitch file, or null. The rail labels a placement size
                // "(file size)" only when it matches this.
                file_size_in: _fileSizeFromPreview(
                    _previewByVersionId.value.get(
                        String(
                            displayAttrs.artwork_version_id
                                ?? attrs.artwork_version_id,
                        ),
                    ),
                ),
                // Card #573: the server's low-resolution verdict for this
                // placement's saved size (amber / red icon in the rail), or
                // null. Filled from ONE batched read per load.
                dpi_resolution:
                    _resolutionByPlacementId.value.get(String(entry.id)) ?? null,
                display_order:
                    editedOrder !== undefined ? editedOrder : baseOrder,
                variants,
                skip_color_skus: Array.from(allSkipSkus),
                // Pebble 8b PR 3: merged personalization_fields surface.
                personalization_fields: mergePersonalizationFields(entry.id),
                _staged: false,
            })
        }
        // Bar-raiser fix P0 #1: sort by display_order ASC so the getter
        // reflects reorderDecoration edits (pendingDecorationOrderEdits
        // are folded into entry.display_order above). Fall back to numeric
        // id ASC for stable ordering on ties / nulls. Mirrors the variant
        // sort key at sortVariants() above.
        server.sort((a, b) => {
            const ao = Number.isFinite(a.display_order) ? a.display_order : 0
            const bo = Number.isFinite(b.display_order) ? b.display_order : 0
            if (ao !== bo) return ao - bo
            const an = Number(a.id)
            const bn = Number(b.id)
            const aIsNum = Number.isFinite(an)
            const bIsNum = Number.isFinite(bn)
            if (aIsNum && bIsNum) return an - bn
            return String(a.id).localeCompare(String(b.id))
        })

        const pending = pendingDecorations.value.map((entry, idx) => {
            const variants = sortVariants(
                pendingVariants.value
                    .filter(v => v.parent === entry._tempId)
                    .map(v => ({
                        _tempId: v._tempId,
                        // PR-N4: variant_key dropped. Pending variants
                        // have no server id yet; consumers iterate by
                        // _tempId until the save round-trip resolves.
                        kind: v.kind ?? null,
                        color_skus: Array.isArray(v.color_skus)
                            ? v.color_skus.slice()
                            : [],
                        artwork_file: v.artwork_file ?? null,
                        artwork_filename: v.artwork_filename ?? null,
                        artwork_object_url: v.artwork_object_url ?? null,
                        ink_color_hex: v.ink_color_hex ?? null,
                        display_order: v.display_order ?? 0,
                        _staged: true,
                    }))
            )
            const pendingSkipSet
                = pendingSkipColors.value.get(entry._tempId) ?? new Set()
            return {
                _tempId: entry._tempId,
                placement_id: entry.placement_id,
                // Shape parity with server rows so selectedView resolves a
                // pending (client-added) placement's Stage view from the
                // zone SLUG, not the row id. NOTE the legacy asymmetry: on
                // the PENDING path `entry.placement_id` carries a zone slug
                // (addDecoration's @param {{placement_id: string}} is a
                // slug), whereas on the SERVER path placement_id is the
                // numeric Placement row id. Surfacing placement_zone_slug on
                // both keeps selectedView's resolution chain uniform and
                // avoids the "Full Center" default for freshly-added
                // placements once addDecoration is re-wired to a live caller.
                placement_zone_slug:
                    entry.placement_zone_slug ?? entry.placement_id ?? null,
                name: entry.name,
                description: entry.description,
                // Pending decorations don't yet have a server display_order;
                // fall back to insertion index so consumers see a stable,
                // monotonic value. Pebble 8b's reorder action will overwrite.
                display_order: idx,
                variants,
                skip_color_skus: Array.from(pendingSkipSet),
                // Pebble 8b PR 3: merged personalization_fields for a
                // pending decoration are pending-add-only (no server rows,
                // no edits/deletes); parent matches _tempId.
                personalization_fields: mergePersonalizationFields(
                    entry._tempId
                ),
                _staged: true,
            }
        })
        return [...server, ...pending]
    })

    // ---- actions ----

    /**
     * Revoke every object URL currently held by a pending variant. Called
     * from openEditor (defensive clear) and closeEditor to prevent leaks of
     * blob URLs created via `URL.createObjectURL` for artwork previews.
     * Silent no-op on entries without a URL or on environments missing the
     * surface (older test runners).
     */
    function _revokeAllPendingVariantUrls() {
        const revoke = globalThis.URL?.revokeObjectURL
        if (typeof revoke !== 'function') return
        for (const v of pendingVariants.value) {
            if (v?.artwork_object_url) {
                revoke(v.artwork_object_url)
            }
        }
    }

    /**
     * Open the editor for a specific campaign_product. Clears any prior
     * draft state. Does NOT trigger a network load — callers (the shell
     * component) call `loadServerState` explicitly so the loading state
     * machine is visible in the calling site.
     */
    function openEditor(id) {
        // Layer 1.v2: same-id no-op. When openEditor is called with the
        // SAME id that is already open (the remount-loop fan-out shape),
        // there is no draft state to wipe and no token swap needed. If
        // we reset state here, the immediately-following loadServerState
        // would refetch (or short-circuit against a now-null serverState
        // and never repopulate it -- worse). Short-circuit cleanly.
        //
        // This is the structural defense: the only legitimate reason to
        // re-call openEditor with the same id is a benign remount, and
        // benign remounts must not wipe state. State-mutating openEditor
        // calls only happen on cross-id switches (Vue reactivity routes
        // a different campaignProductId in via setActivePinia or the
        // route change, both of which take a different id path).
        if (
            campaignProductId.value === id
            && id != null
            && id !== ''
        ) {
            return
        }

        // Bump the load token so any in-flight loads from a previously-open
        // product are discarded when they resolve.
        _loadToken.value += 1
        // Drop the in-flight slot too: the load it tracks is now stale (its
        // result will be discarded by the token check), and a fresh
        // loadServerState for the new id must NOT collide with a leftover
        // entry keyed by the previous id.
        _loadInFlight.value = null
        // Layer 1.v2: switching products -- preserve the NEW id's
        // throttle entry (if any; rare but valid when bouncing between
        // two products quickly) and clear the rest to bound map size.
        const keepEntry = _lastLoadedAt.value.get(id)
        _lastLoadedAt.value = new Map()
        if (keepEntry != null) _lastLoadedAt.value.set(id, keepEntry)
        // Defensive: revoke any in-flight artwork object URLs before we
        // drop the pending list on the floor. Without this, switching
        // products would leak one blob URL per staged variant artwork.
        _revokeAllPendingVariantUrls()
        // Sticky authoring intent belongs to ONE product. We are past the
        // same-id short-circuit, so this is a genuine switch and the new
        // product starts untouched. (A same-id re-open is a benign remount and
        // must NOT clear the flag -- that would re-arm the stub discard on a
        // product the user has already configured.)
        _hasAuthoringIntent.value = false
        _hasEverHadSku.value = false
        // Field-only autosave: cancel any armed debounce from the prior
        // product and reset the state machine so the new product opens idle.
        _cancelFieldAutoSaveTimer()
        _fieldAutoSavePending = false
        fieldAutoSaveState.value = 'idle'
        campaignProductId.value = id
        serverState.value = null
        // PR-4b: drop the prior product's preview map so a stale sidecar
        // never briefly stamps onto the new product's variants before its
        // own hydration resolves.
        _previewByArtworkId.value = new Map()
        _previewByVersionId.value = new Map()
        _resolutionByPlacementId.value = new Map()
        dirty.value = new Map()
        loadError.value = null
        saveError.value = null
        saveErrors.value = []
        supplierMeta.value = null
        supplierMetaLoading.value = false
        pendingSwapCleanup.value = null
        skuClearedForSwitch.value = false
        supplierSwitchSnapshot.value = null
        _priceBreaksData.value = null
        _priceBreaksDataKey.value = null
        priceBreaksError.value = null
        _priceBreaksToken += 1
        pendingDecorations.value = []
        pendingVariants.value = []
        pendingSkipColors.value = new Map()
        pendingSkipColorDeletes.value = new Map()
        pendingDecorationOrderEdits.value = new Map()
        pendingVariantOrderEdits.value = new Map()
        pendingPersonalizationFields.value = []
        pendingPersonalizationFieldEdits.value = new Map()
        pendingPersonalizationFieldDeletes.value = new Set()
        pendingDecorationDeletes.value = new Set()
    }

    /**
     * Reset the entire store back to defaults. Used when the editor sheet
     * closes or the route unmounts.
     */
    function closeEditor() {
        // Bump the load token so any in-flight loads are discarded when they
        // resolve after the editor is closed.
        _loadToken.value += 1
        // Drop the in-flight slot too — the editor is closed, no caller
        // should attach to a load whose result will be discarded.
        _loadInFlight.value = null
        // Layer 1.v2: editor fully torn down -- drop all throttle entries
        // so a fresh open of any product (including the just-closed one)
        // does not short-circuit against possibly-stale state.
        _lastLoadedAt.value = new Map()
        _revokeAllPendingVariantUrls()
        // Editor torn down -- the next open starts from a clean slate.
        _hasAuthoringIntent.value = false
        _hasEverHadSku.value = false
        // Field-only autosave: cancel any armed debounce so a stale timer
        // can't fire a PATCH after the editor is gone, and reset the machine.
        _cancelFieldAutoSaveTimer()
        _fieldAutoSavePending = false
        fieldAutoSaveState.value = 'idle'
        campaignProductId.value = null
        serverState.value = null
        _previewByArtworkId.value = new Map()
        _previewByVersionId.value = new Map()
        _resolutionByPlacementId.value = new Map()
        dirty.value = new Map()
        loading.value = false
        loadError.value = null
        saveInFlight.value = false
        saveError.value = null
        saveErrors.value = []
        supplierMeta.value = null
        supplierMetaLoading.value = false
        // Reset ONLY here, not in openEditor: a leaked failure from the
        // product being left is cleared on close, and an openEditor reset
        // could clobber a failure the shell published before it ran.
        supplierMetaFailed.value = false
        pendingSwapCleanup.value = null
        skuClearedForSwitch.value = false
        supplierSwitchSnapshot.value = null
        _priceBreaksData.value = null
        _priceBreaksDataKey.value = null
        priceBreaksError.value = null
        _priceBreaksToken += 1
        pendingDecorations.value = []
        pendingVariants.value = []
        pendingSkipColors.value = new Map()
        pendingSkipColorDeletes.value = new Map()
        pendingDecorationOrderEdits.value = new Map()
        pendingVariantOrderEdits.value = new Map()
        pendingPersonalizationFields.value = []
        pendingPersonalizationFieldEdits.value = new Map()
        pendingPersonalizationFieldDeletes.value = new Set()
        pendingDecorationDeletes.value = new Set()
    }

    /**
     * PR-4b (light up the Stage): fetch the DST/EMB preview state for the
     * artworks this product's variants reference and populate
     * ``_previewByArtworkId``.
     *
     * A ``Variant`` references an artwork via ``artwork_file_id`` (a string
     * equal to ``str(Artwork.id)``); the store-editor Stage renders that
     * artwork's preview sidecar (spinner / cached raster / re-upload tile).
     * The SAFRS ``?include=placements.variants`` payload can't carry the
     * sidecar (a read serialization must not commit the lazy-render
     * enqueue), so we ask ``POST /api/artworks/preview_states`` for the
     * distinct ids in one round-trip. The endpoint reuses the SAME rollup
     * as the Decoration Library grid, so loading the editor kicks the
     * render exactly as a library-grid load does.
     *
     * Guarantees:
     *  - Collects the DISTINCT non-null ``artwork_file_id`` strings from the
     *    just-loaded ``serverState.included`` variants. No ids -> no POST.
     *  - Stale-token guard: a response is discarded if a newer load
     *    superseded this one (same discipline as ``loadServerState``).
     *  - Failure-safe: a rejected fetch is swallowed (the Stage falls
     *    through to Artifi). It must NEVER fail the load.
     *
     * @param {number} myToken -- the ``loadServerState`` token this
     *   hydration belongs to; a mismatch at resolve time means a newer
     *   load owns the state and this response is stale.
     */
    /**
     * ``{placementId: artwork_version_id}`` from the included Decoration
     * rows -- the version each placement's decoration pins.
     */
    function _versionByPlacementId(included) {
        const versionByDecoration = new Map()
        for (const entry of included) {
            if (entry?.type !== 'Decoration' && entry?.type !== 'decorations') {
                continue
            }
            const version = _asFiniteNumber(entry?.attributes?.artwork_version_id)
            if (version != null) versionByDecoration.set(String(entry.id), version)
        }
        const out = new Map()
        for (const entry of included) {
            if (entry?.type !== 'placements' && entry?.type !== 'Placement') {
                continue
            }
            const decorationId = entry?.relationships?.decoration?.data?.id
            if (decorationId == null) continue
            const version = versionByDecoration.get(String(decorationId))
            if (version != null) out.set(String(entry.id), version)
        }
        return out
    }

    /** ``{w, h}`` inches from a READY preview with a real size, else null. */
    function _fileSizeFromPreview(preview) {
        if (!preview || preview.state !== 'ready') return null
        const w = _asFiniteNumber(preview.bbox_w_in)
        const h = _asFiniteNumber(preview.bbox_h_in)
        return w != null && h != null && w > 0 && h > 0 ? { w, h } : null
    }

    /** The preview a variant shows: its pinned version's, else its artwork's. */
    function _previewForVariant(artworkFileId, pinnedVersionId) {
        if (artworkFileId == null || artworkFileId === '') return null
        if (pinnedVersionId != null) {
            return _previewByVersionId.value.get(String(pinnedVersionId)) ?? null
        }
        return _previewByArtworkId.value.get(String(artworkFileId)) ?? null
    }

    /**
     * Card #573: fetch the flagged placements' low-resolution verdicts for the
     * open product in ONE request and populate ``_resolutionByPlacementId``.
     * Same discipline as ``_hydratePreviewStates``: a stale response is
     * dropped, and a failure leaves the icons as they were (it never fails
     * the load).
     */
    async function _hydratePlacementResolution(myToken, id) {
        try {
            const resp = await axiosInstance.get(
                `/api/campaign_products/${id}/placement_resolution/`,
            )
            if (myToken !== _loadToken.value) return
            const next = new Map()
            for (const [k, v] of Object.entries(resp?.data?.placements ?? {})) {
                next.set(String(k), v)
            }
            _resolutionByPlacementId.value = next
        } catch {
            // Best-effort: no icons rather than a failed load.
            if (myToken === _loadToken.value) {
                _resolutionByPlacementId.value = new Map()
            }
        }
    }

    async function _hydratePreviewStates(myToken) {
        const included = Array.isArray(serverState.value?.included)
            ? serverState.value.included
            : []
        const ids = new Set()
        for (const entry of included) {
            if (!isEntityType(entry, 'variants')) continue
            const fileId = entry?.attributes?.artwork_file_id
            if (fileId == null || fileId === '') continue
            ids.add(String(fileId))
        }
        if (ids.size === 0) return
        const versionIds = new Set(
            Array.from(_versionByPlacementId(included).values()).map(String),
        )
        const body = { artwork_ids: Array.from(ids) }
        if (versionIds.size) body.artwork_version_ids = Array.from(versionIds)
        try {
            const resp = await axiosInstance.post(
                '/api/artworks/preview_states',
                body,
            )
            if (myToken !== _loadToken.value) {
                // A newer load superseded this one -- discard the stale
                // preview map rather than stamp it onto the wrong product.
                return
            }
            const previews = resp?.data?.previews ?? {}
            const next = new Map()
            for (const [k, v] of Object.entries(previews)) {
                next.set(String(k), v)
            }
            const byVersion = new Map()
            for (const [k, v] of Object.entries(
                resp?.data?.version_previews ?? {},
            )) {
                byVersion.set(String(k), v)
            }
            _previewByVersionId.value = byVersion
            _previewByArtworkId.value = next
        } catch {
            // Preview hydration is best-effort. A failure leaves the map as
            // it was (variants keep preview=null) and the Stage falls
            // through to its Artifi render. Never surface as a load error.
        }
    }

    /**
     * Fetch the server state for the currently-open campaign_product and any
     * included resources the editor needs (decorations + their nested
     * variants / personalization_fields / skip_colors).
     *
     * No-arg: the id is read from `campaignProductId.value`. Callers MUST
     * call `openEditor(id)` first; calling `loadServerState()` with no id
     * set in the store is a programming error (sets `loadError` and skips
     * the network call rather than firing a request against an invalid URL
     * like `/api/campaign_products/null`).
     *
     * In-flight token discipline: each call increments `_loadToken` and
     * captures the new value locally. Only the call whose token still
     * matches at response time commits its result; older (stale) responses
     * are discarded silently. Same applies if `openEditor` / `closeEditor`
     * fires mid-flight. See `project_inflight-promise-guard-pattern.md`.
     *
     * On success (current token): populates `serverState`, clears
     * `loadError`. On failure (current token): sets `loadError`, clears
     * `serverState`. Never throws.
     */
    async function loadServerState({ force = false, background = false } = {}) {
        const id = campaignProductId.value
        if (id == null || id === '') {
            loadError.value = new Error(
                'loadServerState called with no campaignProductId — '
                + 'call openEditor(id) first'
            )
            serverState.value = null
            return
        }

        // In-flight dedup: if a load for the SAME id is already running,
        // attach to its promise instead of firing a duplicate GET. This is
        // the request-dedup layer; the token below is the stale-write guard.
        // Cross-id callers (openEditor switched mid-flight) fall through and
        // start a fresh request, matching the existing token semantics.
        // A FORCED load never joins: the in-flight request may have started
        // before the write the caller is refreshing after, and would show
        // the old state. The token bump below supersedes it instead.
        if (!force && _loadInFlight.value && _loadInFlight.value.id === id) {
            return _loadInFlight.value.promise
        }

        // Serial-recall short-circuit (Layer 1.v2). If a successful load
        // for the same id resolved within the throttle window AND we
        // still have its serverState in memory, skip the network round-
        // trip. openEditor's same-id path is now a no-op (so the
        // remount-loop fan-out never reaches here), but defense-in-depth
        // catches any future caller-side fan-out we haven't anticipated.
        //
        // Callers that mutated server state and need the canonical
        // refresh (save() is the only one today) pass { force: true } to
        // bypass the throttle. A real user-driven Reload button can also
        // pass force to ensure freshness.
        if (!force) {
            const lastAt = _lastLoadedAt.value.get(id)
            if (
                lastAt != null
                && Date.now() - lastAt < _SERIAL_THROTTLE_MS
                && serverState.value != null
            ) {
                return
            }
        }

        _loadToken.value += 1
        const myToken = _loadToken.value
        // ``background``: refresh WITHOUT the loading state. ProductEditor
        // renders the editor only while !loading, so a foreground reload
        // unmounts it -- an action that refreshes after a write (Replace)
        // would lose its own confirmation and flash the page.
        if (!background) loading.value = true

        const fetchPromise = (async () => {
            try {
                // Phase F PR-4 note on artworks: this include path
                // intentionally does NOT request artworks/repositories. The
                // current schema has no FK from Variant -> Artwork (the
                // formal relationship is a later Phase F PR), so SAFRS
                // can't resolve a `variants.artwork` include token. Until
                // that FK lands, the Pinia `artworks` store hydrates the
                // tenant's artwork + repository collections in parallel via
                // /api/artworks/ + /api/artwork_repositories/mine. See
                // ProductEditor.vue's onMounted hook and stores/artworks.js.
                // When the FK arrives, add `decorations.variants.artwork,
                // decorations.variants.artwork.repository` here and the
                // included-payload walk will pick them up alongside variants.
                // P5.5 PR-3.5 (task #195): placement-rooted include
                // path. Replaces the legacy
                // `?include=decorations,decorations.variants,...` walk
                // that traversed `CampaignProduct.decorations` (legacy
                // ORM relationship on the soon-dropped `decorations`
                // table). The new walk goes through `placements`
                // (canonical post-P6) so reads survive the legacy
                // table drop in P6 PR-2.
                //
                // Children (Variant / PersonalizationField /
                // DecorationSkipColor) carry both
                // `attributes.placement_id` AND
                // `relationships.placement.data.id`; `getVariantParentId`
                // resolves from whichever SAFRS populated.
                //
                // Card: Boulders #163 (https://trello.com/c/S5kN5poU).
                // P5.5 PR-3.5 fix (bar-raiser P0): chain the
                // placement-rooted include through `decoration.method`
                // (for the technique slug used by LeftRail's
                // `formatTechnique` and DecorationInspector's badge) and
                // `placement_zone` (for the human-readable zone display
                // name shown as the placement label). The legacy
                // `Decoration` row carried `name` / `decoration_type` /
                // `display_order` as columns; the placement-rooted
                // substrate splits those across PlacementZone (label)
                // and DecorationProcess (technique slug). The getter
                // below merges the chained attrs into the view-model
                // row, otherwise post-cutover loads render with blank
                // names and a hard-coded 'embroidery' fallback for
                // every row. See decoration.py / placement.py /
                // decoration_process.py module docstrings.
                const url
                    = '/api/campaign_products/'
                    + id
                    + '?include=placements,'
                    + 'placements.variants,'
                    + 'placements.personalization_fields,'
                    + 'placements.skip_colors,'
                    + 'placements.decoration,'
                    + 'placements.decoration.method,'
                    + 'placements.placement_zone'
                const response = await axiosInstance.get(url)
                if (myToken !== _loadToken.value) {
                    // Stale response — a newer load (or openEditor/closeEditor)
                    // superseded this one. Discard silently; the newer call owns
                    // the `loading` flag and the final state.
                    return
                }
                // axios returns {data: <body>, status, headers, ...}; we want the body.
                // The body is the JSON:API response: {data: {type, id, attributes, relationships}, included: [...]}
                // Consumers read serverState.value?.data?.attributes — so serverState must
                // BE the body, not the axios envelope. See PR #1102 /gate finding P0-1
                // for the latent-bug history (this was wrong from pebble 3 onward; tests
                // were inadvertently mocking the corrected shape so unit tests passed
                // but production rendered with undefined attributes).
                serverState.value = response.data
                loadError.value = null
                // Layer 1.v2: record success timestamp for the serial-
                // recall short-circuit. Only on success, so a failed
                // load never blocks a retry.
                _lastLoadedAt.value.set(id, Date.now())
                // PR-4b (light up the Stage): hydrate the DST/EMB preview
                // states for the artworks this product's variants reference.
                // Fire-and-forget -- a preview-fetch failure must NEVER fail
                // the load (the Stage just falls through to Artifi). Not
                // awaited so the editor renders immediately; the reactive
                // map re-stamps the variants when it resolves.
                _hydratePreviewStates(myToken)
                // Card #573: the placement list's low-resolution icons.
                // Same fire-and-forget, fail-open shape.
                _hydratePlacementResolution(myToken, id)
            } catch (err) {
                if (myToken !== _loadToken.value) {
                    // Stale failure — discard.
                    return
                }
                if (background) {
                    // Keep what is on screen; say the refresh failed.
                    saveErrors.value.push({
                        message:
                            'Saved, but could not reload the product: '
                            + `${err?.message ?? err}. Reload the page to see it.`,
                    })
                    return
                }
                loadError.value = err
                serverState.value = null
            } finally {
                if (myToken === _loadToken.value) {
                    loading.value = false
                }
                // Release the in-flight slot iff we still own it. If a newer
                // call (different id, or after a token bump) installed its
                // own entry, leave it alone — that newer load owns the slot.
                if (
                    _loadInFlight.value
                    && _loadInFlight.value.promise === fetchPromise
                ) {
                    _loadInFlight.value = null
                }
            }
        })()

        _loadInFlight.value = { id, promise: fetchPromise }
        return fetchPromise
    }

    /**
     * Record (or clear) a per-field edit. Keyed by an attribute name —
     * for pebble 3 ONLY top-level JSON:API attribute names are accepted
     * (`'name'`, `'sku'`, etc.). Dotted/nested paths are rejected with a
     * thrown Error so misuse fails fast at the call site rather than
     * silently shipping a malformed PATCH body.
     *
     * Why fail fast on dotted paths: `buildPatchBody` is a thin placeholder
     * that emits `{ data: { attributes: <flat-dirty-map> } }`. A key like
     * `'decorations.0.name'` would either trip a SAFRS 400 with a
     * non-obvious cause OR (worse) be silently swallowed as a misnamed
     * top-level attribute. Pebble 12 lands the nested-path → JSON:API
     * relationship mapping; until then, callers must keep paths top-level.
     *
     * If the new value deep-equals the corresponding server value, the
     * entry is REMOVED from the dirty map (a no-op edit is not dirty).
     * Otherwise it's overwritten in place.
     *
     * @throws {Error} if `path` contains a `.` (nested path; pebble 12 territory)
     */
    function updateField(path, value) {
        if (typeof path === 'string' && path.includes('.')) {
            throw new Error(
                `updateField: nested/dotted paths land in pebble 12; `
                + `use a top-level attribute key (got: "${path}")`
            )
        }

        // A hidden unsellable color (see getEffectiveAttribute) is not in
        // the list a panel writes back, because the panel never saw it. Put
        // it back, so it leaves only through the save-time drop that
        // announces it -- never silently because nothing rendered it.
        value = _withHiddenUnsellable(path, value)

        // Sticky authoring intent. Set here rather than only when the dirty
        // Map actually grows, because an edit that returns a field to its
        // server value (below: `next.delete(path)`) is still a user action --
        // the row must not become discardable again just because they undid
        // their typing. Placed AFTER the dotted-path throw: that is a
        // programming error, not a user act.
        _markAuthoringIntent()

        // Compare against the canonical server-side value at the same path,
        // relative to the JSON:API attributes block. Surface spec keeps the
        // path vocabulary minimal for pebble 3.
        const serverValue = getByPath(
            serverState.value?.data?.attributes,
            path
        )
        const next = new Map(dirty.value)
        if (deepEqual(value, serverValue)) {
            next.delete(path)
        } else {
            next.set(path, value)
        }
        dirty.value = next

        // Field-only autosave (MVP): a scalar edit arms the debounced flush.
        // If the edit emptied the dirty Map (a no-op / revert-to-server edit)
        // there is nothing to autosave -- return to idle and cancel any armed
        // timer. Otherwise mark 'dirty' and (re)arm the debounce.
        if (next.size === 0) {
            _cancelFieldAutoSaveTimer()
            // Don't stomp a 'saving' / 'error' state mid-flight; only settle
            // back to idle when we're not actively saving.
            if (!_fieldAutoSaveInFlight) fieldAutoSaveState.value = 'idle'
            return
        }
        fieldAutoSaveState.value = 'dirty'
        _kickFieldAutoSave()
    }

    /**
     * Put fields back to earlier values (a cancelled SKU swap, card #349) so
     * that the server ends on them too.
     *
     * A plain updateField is not enough while an autosave is ON THE WIRE:
     * serverState still holds the pre-flight values, so writing those back
     * compares equal and DELETES the dirty key, nothing is queued, and the
     * in-flight PATCH then lands the very values being rolled back. For a key
     * the in-flight flush is carrying, the restored value is therefore kept
     * dirty regardless; the flush's success path leaves a key whose dirty
     * value changed pending, and the debounce armed here sends it. With no
     * flush carrying the key this is exactly updateField (restoring an
     * unsaved edit queues no request at all).
     *
     * @param {Record<string, any>} values attribute name -> value to restore
     */
    function restoreFields(values) {
        let forced = false
        for (const [path, value] of Object.entries(values ?? {})) {
            updateField(path, value)
            const carried = _fieldAutoSaveInFlight
                && _fieldAutoSaveFlightSnapshot?.has(path)
            if (carried && !dirty.value.has(path)) {
                const next = new Map(dirty.value)
                next.set(path, value)
                dirty.value = next
                forced = true
            }
        }
        if (forced) {
            fieldAutoSaveState.value = 'dirty'
            _kickFieldAutoSave()
        }
    }

    /**
     * Clear the armed autosave debounce timer, if any. Idempotent.
     */
    function _cancelFieldAutoSaveTimer() {
        if (_fieldAutoSaveTimer) {
            clearTimeout(_fieldAutoSaveTimer)
            _fieldAutoSaveTimer = null
        }
    }

    /**
     * (Re)arm the debounced autosave. Rapid successive edits collapse into a
     * single trailing-edge flush FIELD_AUTOSAVE_DEBOUNCE_MS after the last
     * edit settles. Mirrors StoreAddEdit._kickAutoSave.
     */
    function _kickFieldAutoSave() {
        _cancelFieldAutoSaveTimer()
        _fieldAutoSaveTimer = setTimeout(() => {
            _fieldAutoSaveTimer = null
            saveDirtyFields()
        }, FIELD_AUTOSAVE_DEBOUNCE_MS)
    }

    // Non-standard (extended) sizes carry per-size pricing (upcharge +
    // margin) that must be pruned when the size is deselected. NON_STANDARD_SIZES
    // is imported from the shared sizeConstants module (single source of truth).

    /**
     * Deselect a size (Sizes & Colors merge / Pricing split, 2026-07-14).
     *
     * Removes `size` from `selected_sizes`, and drops that size's entry from
     * `fixed_size_prices` (any size). For NON-STANDARD sizes ALSO prunes the
     * legacy per-size pricing data so a stale upcharge/margin does not linger
     * after the size is turned off:
     *   - `non_standard_upcharges[size]`, and
     *   - `added_cost_and_margin[primary][size]` for EVERY primary
     *     (each selected color SKU, or the `_default` sentinel).
     *
     * This is the size->pricing cleanup rule the SizesPricingPanel used to
     * inline in its `toggleSize()` deselect branch. Promoting it to the
     * store makes the coupling explicit and single-sourced (design-
     * philosophy: authority-in-data / one write model): the sizes host now
     * delegates here instead of reaching into pricing-domain attributes, and
     * the Pricing panel need not be mounted for the cleanup to run. All
     * cleanup steps are ordinary `updateField` writes -- no pricing-local
     * draft refs are involved, so the behavior is portable across the tab
     * split.
     *
     * @param {string} size
     */
    function deselectSize(size) {
        // Sticky authoring intent: a user reached this action. Latched
        // BEFORE any early return -- an attempted authoring act still means
        // a human was here, and keeping the row is the safe direction.
        _markAuthoringIntent()

        const current = new Set(
            getEffectiveAttribute('selected_sizes') ?? [],
        )
        if (!current.has(size)) {
            // Not selected -> nothing to remove and no pricing to prune.
            // Still write the (unchanged) array so the caller sees a
            // deterministic post-state; updateField no-ops if it matches
            // the server value.
            updateField('selected_sizes', Array.from(current))
            return
        }
        current.delete(size)
        updateField('selected_sizes', Array.from(current))

        // A fixed price never outlives the size it priced (any size, not
        // only extended ones -- Fixed price is per size, Jake 2026-09-29).
        const fixedPrices = getEffectiveAttribute('fixed_size_prices')
        if (fixedPrices && typeof fixedPrices === 'object' && size in fixedPrices) {
            const nextFixed = { ...fixedPrices }
            delete nextFixed[size]
            updateField('fixed_size_prices', nextFixed)
        }

        // Standard sizes have no legacy upcharge/margin data to prune.
        if (!NON_STANDARD_SIZES.includes(size)) return

        // Clear the legacy non_standard_upcharges key for this size.
        const upcharges = {
            ...(getEffectiveAttribute('non_standard_upcharges') ?? {}),
        }
        if (size in upcharges) {
            delete upcharges[size]
            updateField('non_standard_upcharges', upcharges)
        }

        // Clear added_cost_and_margin[primary][size] under every primary
        // (each selected color SKU, or the _default sentinel).
        const colorSkus = getEffectiveAttribute('selected_color_skus') ?? []
        const primaries = colorSkus.length > 0 ? [...colorSkus] : ['_default']
        const acm = { ...(getEffectiveAttribute('added_cost_and_margin') ?? {}) }
        let mutated = false
        for (const primary of primaries) {
            if (acm[primary] && size in acm[primary]) {
                const next = { ...acm[primary] }
                delete next[size]
                acm[primary] = next
                mutated = true
            }
        }
        if (mutated) {
            updateField('added_cost_and_margin', acm)
        }
    }

    /**
     * Return the EFFECTIVE value of a top-level attribute — the dirty
     * (unsaved) value if the user has edited it, otherwise the canonical
     * server value. Lets UI components show the user's in-flight edits
     * without forcing them to round-trip through save.
     *
     * Critical for downstream computeds like Sale Price (size × upcharge):
     * without this, the panel would render the user's typed upcharge
     * AS-TYPED in the input (because the input keeps its own DOM state)
     * but the Sale Price column would still show base-price-plus-zero
     * (because Sale Price reads from `serverState`).
     *
     * @param {string} path  top-level attribute key (e.g. 'non_standard_upcharges')
     * @returns the dirty value if present, else the server value, else undefined
     */
    function _rawEffectiveAttribute(path) {
        if (dirty.value.has(path)) return dirty.value.get(path)
        return getByPath(serverState.value?.data?.attributes, path)
    }

    // Colors the supplier no longer sells (Jake, 2026-10-07): the editor
    // never shows them, including ones the product already holds, and its
    // next save drops them (see _stageUnsellableColorDrop). This is the ONE
    // read seam every panel goes through, so they all agree. Null -- hide
    // nothing -- when sellability is unknown or when no color would remain
    // (@/utils/unsellableColors).
    const _unsellableColorKeys = computed(() => notSellableColorKeys(
        supplierMeta.value,
        _rawEffectiveAttribute('product_item_number'),
    ))
    const _hiddenUnsellableKeys = computed(() => unsellableKeysToHide(
        _unsellableColorKeys.value,
        _rawEffectiveAttribute('selected_color_skus'),
    ))

    function getEffectiveAttribute(path) {
        const value = _rawEffectiveAttribute(path)
        if (path === 'selected_color_skus' || path === 'selected_colors') {
            return withoutUnsellable(path, value, _hiddenUnsellableKeys.value)
        }
        return value
    }

    function _withHiddenUnsellable(path, value) {
        if (path !== 'selected_color_skus' && path !== 'selected_colors') return value
        const keys = _hiddenUnsellableKeys.value
        if (!keys || !Array.isArray(value)) return value
        const current = _rawEffectiveAttribute(path)
        if (!Array.isArray(current)) return value
        const visible = withoutUnsellable(path, current, keys)
        const hidden = current.filter((entry) => !visible.includes(entry))
        const present = new Set(value.map((v) => (path === 'selected_colors' ? v?.color_name : v)))
        const restore = hidden.filter((entry) => !present.has(
            path === 'selected_colors' ? entry?.color_name : entry,
        ))
        return restore.length ? [...value, ...restore] : value
    }

    // The snackbar for a drop (EditorPagehead shows it): { id, message }.
    // A fresh object per notice so a repeat of the same text still fires.
    const unsellableColorNotice = ref(null)
    let _unsellableNoticeSeq = 0
    // The product whose all-unsellable refusal was already announced, so
    // autosave does not repeat it on every keystroke.
    let _unsellableRefusalAnnouncedFor = null

    function _announceUnsellable(colors, refused = false) {
        _unsellableNoticeSeq += 1
        unsellableColorNotice.value = {
            id: _unsellableNoticeSeq,
            message: unsellableColorMessage(
                colors,
                _rawEffectiveAttribute('supplier_name'),
                refused,
            ),
        }
    }

    /**
     * Stage the drop of the colors the supplier no longer sells into the
     * dirty Map, so the save being made sends it. Returns the dropped color
     * names (announce them only once the PATCH succeeds), or [].
     *
     * Writes the dirty Map directly rather than through updateField: this
     * is part of a save already under way, not a user edit, so it must not
     * re-arm the autosave debounce or latch authoring intent.
     */
    function _stageUnsellableColorDrop() {
        const plan = planUnsellableColorDrop(
            _rawEffectiveAttribute,
            _unsellableColorKeys.value,
        )
        if (!plan) return []
        if (plan.refused) {
            if (_unsellableRefusalAnnouncedFor !== campaignProductId.value) {
                _unsellableRefusalAnnouncedFor = campaignProductId.value
                _announceUnsellable(plan.colors, true)
            }
            return []
        }
        const next = new Map(dirty.value)
        for (const [path, value] of Object.entries(plan.changes)) {
            next.set(path, value)
        }
        dirty.value = next
        return plan.dropped
    }

    /**
     * Land a new `hide_from_storefront` value on the canonical
     * serverState attribute view. Unlike `updateField`, this does NOT
     * mark the field dirty -- the visibility toggle is an immediate
     * server-side action (POST /campaign_product/toggleHideFromStorefront
     * round-trips before this is called) rather than a buffered edit
     * the user could revert. Mirrors the gallery eye-toggle's
     * persistence model: the change is already committed server-side
     * by the time we touch the client state.
     *
     * No-op when serverState hasn't loaded yet (the caller in
     * EditorPagehead already gates on campaignProductId, so this is
     * defensive).
     *
     * @param {boolean} value
     */
    function setHideFromStorefront(value) {
        // Sticky authoring intent: a user reached this action. Latched
        // BEFORE any early return -- an attempted authoring act still means
        // a human was here, and keeping the row is the safe direction.
        _markAuthoringIntent()

        const attrs = serverState.value?.data?.attributes
        if (!attrs) return
        attrs.hide_from_storefront = Boolean(value)
    }

    /**
     * Land a new `store_category_id` value on the canonical serverState
     * attribute view (D1-frontend, card https://trello.com/c/a7j1VV6D).
     * Mirrors `setHideFromStorefront`: the category picker PATCHes the D1
     * membership-write endpoint (`/campaign_products/<id>/category`)
     * BEFORE calling this — the write is already committed server-side by
     * the time the client state is touched, so this is not a dirty-Map
     * edit and does not go through `updateField`.
     *
     * No-op when serverState hasn't loaded yet (defensive, mirrors
     * `setHideFromStorefront`).
     *
     * @param {number|null} value
     */
    function setStoreCategoryId(value) {
        // Sticky authoring intent: a user reached this action. Latched
        // BEFORE any early return -- an attempted authoring act still means
        // a human was here, and keeping the row is the safe direction.
        _markAuthoringIntent()

        const attrs = serverState.value?.data?.attributes
        if (!attrs) return
        attrs.store_category_id = value ?? null
    }

    /**
     * Discard all in-flight edits (pebble 12 — extended). Leaves
     * `serverState` intact so the editor re-binds to canonical values
     * without a network round-trip. Clears:
     *   - dirty Map (scalar edits)
     *   - pendingDecorations (staged adds)
     *   - pendingVariants (staged adds — revokes their object URLs first)
     *   - pendingSkipColors (staged skip-color adds)
     *   - pendingSkipColorDeletes (staged skip-color deletes)
     *   - saveError / saveErrors
     */
    function revert() {
        _revokeAllPendingVariantUrls()
        // Field-only autosave: discarding edits cancels the armed debounce
        // and resets the state machine.
        _cancelFieldAutoSaveTimer()
        _fieldAutoSavePending = false
        fieldAutoSaveState.value = 'idle'
        dirty.value = new Map()
        pendingDecorations.value = []
        pendingVariants.value = []
        pendingSkipColors.value = new Map()
        pendingSkipColorDeletes.value = new Map()
        pendingDecorationOrderEdits.value = new Map()
        pendingVariantOrderEdits.value = new Map()
        pendingPersonalizationFields.value = []
        pendingPersonalizationFieldEdits.value = new Map()
        pendingPersonalizationFieldDeletes.value = new Set()
        pendingDecorationDeletes.value = new Set()
        saveError.value = null
        saveErrors.value = []
    }

    /**
     * Internal: upload an artwork file via multipart POST to /api/upload_image
     * and return the resulting uuid (used as artwork_file_id on the variant
     * POST that follows). Caller is responsible for wrapping in try/catch —
     * propagates the original error on failure.
     */
    async function _uploadArtwork(file, filename) {
        const formData = new FormData()
        formData.append('file', file, filename)
        const response = await axiosInstance.post('/api/upload_image', formData, {
            // Let axios derive multipart/form-data + boundary from FormData.
            // The default axiosInstance config sets 'Content-Type: application/vnd.api+json',
            // so we explicitly unset it here. Setting it manually without the boundary
            // would break the multipart parse server-side. See PR #1102 /gate finding P1-3.
        })
        return response?.data?.uuid
    }

    /**
     * Autosave the scalar dirty fields ONLY (field-only autosave, MVP).
     *
     * This is the Group A scalar half of `save()` extracted so it can fire on
     * a debounce independently of the structural Group B/C ops. It PATCHes
     * `/api/campaign_products/<id>/` with the `dirty` Map attributes and fans
     * out the best-effort F2 `color_routing` side-writes (both key off the
     * `dirty` Map). It does NOT POST pending decorations/variants, DELETE
     * skip-colors/placements, PATCH reorders, or reload canonical state --
     * those stay behind the explicit Save button.
     *
     * State machine: 'saving' while the PATCH is in flight, then 'saved'
     * (lingering briefly before 'idle') on success, or 'error' on failure.
     * On success the SCALAR dirty Map is cleared; structural buckets are left
     * untouched. On failure the dirty Map is KEPT so the user's edit is not
     * lost and the next debounce (or an explicit save) retries -- mirrors the
     * store editor's non-blocking failure model.
     *
     * Re-entrancy: guarded by `_fieldAutoSaveInFlight` AND `saveInFlight`. An
     * edit that lands while a flush is in flight sets `_fieldAutoSavePending`
     * so a fresh debounce re-arms after the in-flight flush settles (no
     * overlapping PATCH). A no-op when the dirty Map is empty or when no
     * campaign_product is open. Respects the `saveInFlight` structural-save
     * lock so autosave never races an explicit save().
     */
    async function saveDirtyFields() {
        // Re-entry guard: never overlap an autosave with itself OR with an
        // explicit structural save(). A change that arrives mid-flight
        // re-arms via _fieldAutoSavePending so nothing is dropped.
        if (_fieldAutoSaveInFlight || saveInFlight.value) {
            _fieldAutoSavePending = true
            return
        }
        const cpId = campaignProductId.value
        if (cpId == null || cpId === '') return
        if (dirty.value.size === 0) {
            // Nothing to flush; settle to idle.
            if (fieldAutoSaveState.value === 'dirty') {
                fieldAutoSaveState.value = 'idle'
            }
            return
        }

        _fieldAutoSaveInFlight = true
        let endFlight
        _fieldAutoSaveFlightDone = new Promise((resolve) => {
            endFlight = resolve
        })
        fieldAutoSaveState.value = 'saving'
        // Snapshot the keys being flushed so a concurrent edit that lands
        // during the await only clears what we actually PATCHed (defense
        // against clobbering a newer edit's dirty entry on success).
        const droppedUnsellable = _stageUnsellableColorDrop()
        const flushedSnapshot = new Map(dirty.value)
        _fieldAutoSaveFlightSnapshot = flushedSnapshot

        try {
            // The PATCH response, or null when it failed. Its attributes
            // carry the server-derived fields adopted below.
            const patchResponse = await _sendFieldPatches(flushedSnapshot, cpId)

            // The editor moved to ANOTHER product while this PATCH was in
            // flight (#4146 gate P2-3). The save belongs to the old product:
            // it must not merge the old product's fields into the new one's
            // serverState, clear the new one's dirty keys, report its state,
            // or tick saveCount (which would arm the new product's
            // render although it was never saved).
            if (campaignProductId.value !== cpId) return

            if (!patchResponse) {
                // Keep the dirty Map so the edit is not lost; next debounce
                // (or explicit save) retries.
                fieldAutoSaveState.value = 'error'
                return
            }
            if (droppedUnsellable.length) _announceUnsellable(droppedUnsellable)

            // Success: clear only the keys we flushed AND advance serverState
            // to the just-persisted value for each cleared key.
            //
            // Why advance serverState (P1 regression fix, #2017): once a key
            // is removed from the dirty Map, getEffectiveAttribute falls
            // through to serverState. If serverState still holds the PRE-edit
            // value, the effective read returns the STALE value the instant
            // autosave clears the key -- so a just-selected storefront image
            // (or any autosaved scalar) vanishes from the UI until a manual
            // refetch. The PATCH succeeded with exactly `flushedSnapshot`
            // values, so serverState is now authoritatively those values.
            //
            // Concurrency: only advance + clear a key whose dirty value STILL
            // deep-equals the flushed value. A key re-edited during the await
            // (dirty != flushed) is left pending -- neither cleared nor
            // overwritten -- so the newer edit keeps winning in
            // getEffectiveAttribute and flushes on the next debounce.
            //
            // Edge: if serverState has no attributes object (meta not loaded),
            // there is nowhere to land the saved value. Clearing the dirty key
            // then would lose it entirely, so we KEEP the key pending in that
            // case (getEffectiveAttribute still returns it from dirty).
            const attrs = serverState.value?.data?.attributes
            const remaining = new Map(dirty.value)
            if (attrs) {
                // Build a fresh attributes object so Vue tracks the change and
                // re-renders effective-attribute consumers. Mutating in place
                // would land the value but not always trigger reactivity on a
                // ref-wrapped nested object.
                const nextAttrs = { ...attrs }
                for (const key of flushedSnapshot.keys()) {
                    if (
                        deepEqual(remaining.get(key), flushedSnapshot.get(key))
                    ) {
                        nextAttrs[key] = flushedSnapshot.get(key)
                        remaining.delete(key)
                    }
                }
                // Server-derived, read-only attributes (never sent, never
                // dirty) are recomputed by the backend from what was just
                // saved -- e.g. `price_preview` after a pricing-mode or size
                // change. Adopt them from the PATCH response so the editor
                // shows the server's number rather than a stale one (and
                // never has to compute a price itself).
                const responseAttrs = patchResponse?.data?.data?.attributes
                if (responseAttrs && typeof responseAttrs === 'object') {
                    for (const key of SERVER_DERIVED_ATTRIBUTES) {
                        if (key in responseAttrs) {
                            nextAttrs[key] = responseAttrs[key]
                        }
                    }
                }
                serverState.value = {
                    ...serverState.value,
                    data: {
                        ...serverState.value.data,
                        attributes: nextAttrs,
                    },
                }
            }
            // When attrs is missing we leave every flushed key in `remaining`
            // (nothing deleted), so the value stays readable via the dirty Map.
            dirty.value = remaining
            saveError.value = null
            // 'saved' means NOTHING is left unsaved. A key re-edited during
            // the await is still in `remaining`, with its own debounce armed
            // by updateField; reporting 'saved' here would show "Saved" while
            // that newer edit is unsent (e.g. Remove all right after a
            // deselect), and anyone trusting the badge would lose it.
            fieldAutoSaveState.value = remaining.size > 0 ? 'dirty' : 'saved'
            saveCount.value += 1
            // Fall back to idle after the badge lingers, unless a fresh edit
            // has already moved us to 'dirty'/'saving'.
            setTimeout(() => {
                if (fieldAutoSaveState.value === 'saved') {
                    fieldAutoSaveState.value
                        = dirty.value.size > 0 ? 'dirty' : 'idle'
                }
            }, FIELD_AUTOSAVE_SAVED_LINGER_MS)
        } finally {
            _fieldAutoSaveInFlight = false
            // A change arrived mid-flight -> re-arm a fresh debounce so the
            // coalesced edit flushes (no overlapping PATCH).
            if (_fieldAutoSavePending) {
                _fieldAutoSavePending = false
                if (dirty.value.size > 0) {
                    fieldAutoSaveState.value = 'dirty'
                    _kickFieldAutoSave()
                }
            }
            _fieldAutoSaveFlightDone = null
            _fieldAutoSaveFlightSnapshot = null
            endFlight()
        }
    }

    /**
     * Send the scalar autosave requests for `snapshot`: the authoritative
     * PATCH `/api/campaign_products/<id>/` plus the best-effort F2
     * `color_routing` side-writes. Shared by the debounced autosave and the
     * unload flush so both put the same requests on the wire.
     *
     * `requestConfig` is passed through to axios only when given (the unload
     * flush uses it for keepalive). Resolves the authoritative PATCH's
     * response when it succeeded (the autosave adopts server-derived
     * attributes such as `price_preview` from it), or null when it failed;
     * never rejects.
     */
    /**
     * Card #545: the server refuses turning Visible on while a placement's
     * location isn't valid for the SKU (400). A refusal is deterministic, so
     * the flip must not stay pending: every later autosave would carry it and
     * be refused too, and no other edit on the product could save. Drop it
     * (the toggle snaps back to the saved state; saveError says why). Any
     * other key in the refused PATCH stays pending, as before.
     */
    function _dropRefusedUnhide(snapshot, err) {
        if (err?.response?.status !== 400) return
        // Only THIS refusal: any other 400 keeps the make-visible pending
        // (gate P2-5, PR #4918).
        if (err?.response?.data?.errors?.[0]?.detail !== UNHIDE_REFUSED_DETAIL) return
        if (snapshot.get('visible') !== true) return
        if (serverState.value?.data?.attributes?.visible !== false) return
        if (dirty.value.get('visible') !== true) return
        const next = new Map(dirty.value)
        next.delete('visible')
        dirty.value = next
    }

    function _sendFieldPatches(snapshot, cpId, requestConfig) {
        const send = (url, body) => (requestConfig
            ? axiosInstance.patch(url, body, requestConfig)
            : axiosInstance.patch(url, body))

        const patch = send(
            `/api/campaign_products/${cpId}/`,
            buildCpPatchBody(snapshot, cpId)
        ).then(
            (response) => response ?? {},
            (err) => {
                // Prefer the server's reason (SAFRS errors[].detail) over
                // axios' bare "status code 400".
                const reason = err?.response?.data?.errors?.[0]?.detail
                    ?? err?.response?.data?.error
                    ?? err?.message
                saveError.value = new Error(
                    `Failed to autosave fields: ${reason}`
                )
                _dropRefusedUnhide(snapshot, err)
                return null
            }
        )
        const promises = [patch]

        // F2 triple-write (mirrors save() Group A). Best-effort; a failure
        // here does NOT mark the autosave failed -- the legacy
        // color_overrides PATCH above is authoritative for the resolver.
        if (snapshot.has('color_overrides')) {
            const prevOverrides
                = serverState.value?.data?.attributes?.color_overrides
                ?? {}
            const nextOverrides = snapshot.get('color_overrides') ?? {}
            const changes = diffColorOverridesToF2(prevOverrides, nextOverrides)
            for (const change of changes) {
                promises.push(
                    send(
                        `/api/campaign_products/${cpId}/color_routing`,
                        change,
                    ).catch((err) => {
                        console.warn(
                            '[productEditor] F2 color_routing '
                            + `write failed for sku=${change.color_sku} `
                            + `variant_key=${change.variant_key}:`,
                            err?.message ?? err,
                        )
                        return null
                    })
                )
            }
        }

        return Promise.allSettled(promises).then(() => patch)
    }

    /**
     * Flush any pending scalar autosave immediately (cancels the debounce).
     * Called on editor close / route-leave so a settled-but-not-yet-flushed
     * edit persists before navigation rather than being dropped by
     * closeEditor's state reset. No-op when the dirty Map is empty.
     *
     * If an autosave is already in flight, waits for it first: an edit made
     * during that flight is only queued behind it (`_fieldAutoSavePending`
     * re-arms a debounce), and closeEditor cancels that debounce, so without
     * the wait the route leave would resolve with the newer edit unsent.
     * With no flight in progress the PATCH is dispatched synchronously,
     * which the unmount path relies on.
     *
     * Returns a promise that settles once the pending edit has been sent, so
     * callers (the route-leave guard) can await it before navigating.
     */
    async function flushFieldAutoSave() {
        _cancelFieldAutoSaveTimer()
        for (
            let flight = _fieldAutoSaveFlightDone;
            flight;
            flight = _fieldAutoSaveFlightDone
        ) {
            await flight
        }
        // The finished flight may have re-armed a debounce for the queued
        // edit; send it now instead.
        _cancelFieldAutoSaveTimer()
        _fieldAutoSavePending = false
        if (dirty.value.size === 0) return
        await saveDirtyFields()
    }

    // Request options for the unload flush. `keepalive` lets the request
    // outlive the page, and it needs the fetch adapter (XHR has no
    // equivalent). navigator.sendBeacon is NOT usable here: the API
    // authenticates with an `Authorization: Bearer` header, which a beacon
    // cannot carry, while this goes through the normal interceptor. Browsers
    // cap keepalive bodies at 64KB in flight; a scalar PATCH is far below it.
    const UNLOAD_FLUSH_REQUEST_CONFIG = Object.freeze({
        adapter: 'fetch',
        fetchOptions: Object.freeze({ keepalive: true }),
    })

    /**
     * True while an autosave is in flight AND a newer scalar edit is waiting
     * behind it -- the one state in which leaving the page can lose an edit
     * despite the unload flush. The keepalive request for the newer edit
     * reaches the API concurrently with the in-flight one, and whichever
     * commits last wins; measured on 8085 (2026-09-30) the older value won.
     * The editor asks the browser to confirm leaving in this state only.
     *
     * A plain function, not a computed: the flight bookkeeping is not
     * reactive, and the only reader is a beforeunload handler.
     */
    function isFieldEditQueuedBehindFlight() {
        const onTheWire = _fieldAutoSaveFlightSnapshot
        if (!_fieldAutoSaveInFlight || !onTheWire) return false
        for (const [key, value] of dirty.value) {
            if (!onTheWire.has(key) || !deepEqual(onTheWire.get(key), value)) {
                return true
            }
        }
        return false
    }

    /**
     * Send any pending scalar autosave as keepalive requests. Called from
     * `pagehide` (tab close, reload, external navigation), where an awaited
     * flush would be torn down with the page. Synchronous and fire-and-forget:
     * the requests are dispatched before this returns and nothing waits on
     * them.
     *
     * Sends even while an earlier autosave is in flight: the newer edit would
     * otherwise never leave the browser. That send RACES the in-flight PATCH
     * on the server, though, so it is only best effort -- see
     * isFieldEditQueuedBehindFlight, which the editor uses to ask the user to
     * stay in that case. Local state is left as-is: the page is going away, and if
     * it is instead restored from the back/forward cache the edit is still
     * dirty and flushes again on the next edit or leave (the PATCH is
     * idempotent).
     */
    function flushFieldAutoSaveOnUnload() {
        const cpId = campaignProductId.value
        if (cpId == null || cpId === '') return
        if (dirty.value.size === 0) return
        _cancelFieldAutoSaveTimer()
        _sendFieldPatches(
            new Map(dirty.value),
            cpId,
            UNLOAD_FLUSH_REQUEST_CONFIG
        )
    }

    /**
     * Save orchestrator (pebble 12). Drives every pending bucket out to the
     * server in two groups, then re-fetches canonical state. Group A runs in
     * parallel: PATCH `/api/campaign_products/<id>` for the scalar dirty Map
     * AND POST every entry in `pendingDecorations`. Group B runs AFTER A so
     * it can resolve pending-decoration `_tempId` parents to server ids:
     *   - POST variants (each with an optional artwork upload first)
     *   - POST skip-color adds
     *   - DELETE server-loaded skip-color rows
     *
     * After Group B settles, `loadServerState()` re-fetches the canonical
     * server state.
     *
     * Error handling (SIMPLIFICATION): individual request failures are
     * collected into `saveErrors[]`. After the reload, ALL pending buckets
     * are cleared regardless of partial failure. The user sees the error
     * list and can redo any failed item manually. Rationale: per-item
     * surgical retry adds complexity that's not worth the protection given
     * how rare partial failures are in practice (mostly network blips that
     * fail all-or-nothing).
     *
     * Re-entrancy: gated on `saveInFlight`. A second call while a save is
     * in flight is an immediate no-op (no second PATCH).
     *
     * Pre-conditions: `campaignProductId.value` must be set (`openEditor`
     * has been called). Without it, `saveError` is populated and the call
     * is a no-op.
     */
    async function save() {
        if (saveInFlight.value) return // re-entrancy guard
        const cpId = campaignProductId.value
        if (cpId == null || cpId === '') {
            saveError.value = new Error(
                'save called with no campaignProductId — '
                + 'call openEditor(id) first'
            )
            return
        }

        saveInFlight.value = true
        saveError.value = null
        saveErrors.value = []
        // Field-only autosave: an explicit structural save also flushes the
        // scalar dirty Map (Group A PATCH below). Cancel any armed autosave
        // debounce so it doesn't fire a duplicate scalar PATCH after this
        // save clears the dirty Map.
        _cancelFieldAutoSaveTimer()
        _fieldAutoSavePending = false
        // Snapshot the pending buckets at the start of the save. The
        // SIMPLIFICATION clears them after reload regardless of outcome.
        const tempIdToServerId = new Map()

        try {
            // ============================================================
            // GROUP A — PATCH campaign_products + POST new decorations
            // (parallel)
            // ============================================================
            const groupAPromises = []
            const droppedUnsellable = _stageUnsellableColorDrop()

            if (dirty.value.size > 0) {
                groupAPromises.push(
                    axiosInstance
                        .patch(
                            `/api/campaign_products/${cpId}/`,
                            buildCpPatchBody(dirty.value, cpId)
                        )
                        .then((response) => {
                            if (droppedUnsellable.length) {
                                _announceUnsellable(droppedUnsellable)
                            }
                            return response
                        })
                        .catch((err) => {
                            saveErrors.value.push({
                                message: `Failed to save attributes: ${err.message}`,
                            })
                            return null
                        })
                )
            }

            // ----------------------------------------------------------
            // F2 triple-write (Wave 2 PR-1, F2.d)
            // ----------------------------------------------------------
            // The legacy ``color_overrides`` PATCH above is authoritative
            // for now. This block additionally fans out one PATCH per
            // changed color_sku to the canonical F2 writer endpoint so
            // ``product_color_variant_routing`` catches up immediately.
            //
            // Best-effort: an F2 endpoint failure is logged + collected
            // as a non-fatal saveError but does NOT block the save.
            // PR-2 of Wave 2 collapses the resolver onto F2; until then,
            // the legacy maps are still consulted at read time.
            if (dirty.value.has('color_overrides')) {
                const prevOverrides
                    = serverState.value?.data?.attributes?.color_overrides
                    ?? {}
                const nextOverrides = dirty.value.get('color_overrides') ?? {}
                const changes
                    = diffColorOverridesToF2(prevOverrides, nextOverrides)
                for (const change of changes) {
                    groupAPromises.push(
                        axiosInstance
                            .patch(
                                `/api/campaign_products/${cpId}/color_routing`,
                                change,
                            )
                            .catch((err) => {
                                // Non-fatal: surface as a warning-shaped
                                // saveError so the user sees something
                                // went wrong, but the legacy write
                                // already covered the user-visible
                                // resolver path. PR-2 will tighten this.
                                console.warn(
                                    '[productEditor] F2 color_routing '
                                    + `write failed for sku=${change.color_sku} `
                                    + `variant_key=${change.variant_key}:`,
                                    err?.message ?? err,
                                )
                                return null
                            })
                    )
                }
            }

            // PR-Z4 (2026-06-26): the legacy pending-decorations POST
            // loop was removed here. ``pendingDecorations`` had only one
            // producer (the deleted AddPlacementModal.vue) and zero live
            // callers after PR-4 and PR-5; AddPlacementWizard ships the
            // composite placement+decoration shape directly via
            // POST /api/placements/. The ``tempIdToServerId`` map that
            // Group B used to resolve tmp- prefixed parent ids therefore
            // never receives entries; the variant resolution block in
            // Group B is structurally dead for tmp- parents but
            // harmless. A wider cleanup of ``pendingDecorations``,
            // ``addDecoration``, and the tmp- branches in Group B is
            // a separate followup.

            await Promise.allSettled(groupAPromises)

            // ============================================================
            // GROUP B — variants (with artwork upload), skip-color
            // adds/deletes (parallel; depends on tempIdToServerId)
            // ============================================================
            const groupBPromises = []

            // B.1: Variants — each variant may need an artwork upload first,
            // then the variant POST. Each variant is its own async chain.
            //
            // Pre-flight collision detection on routing identity
            // (kind, color_sku). addVariant blocks these at stage time;
            // this is the last line of defense for sessions that staged
            // before the dedup landed OR any caller that bypasses
            // addVariant (e.g. tests, future restore-from-localStorage).
            //
            // Two collision maps:
            //   serverCatchAllKindsByParent: Map<parentId, Set<kind>> --
            //     each catch-all kind may appear at most once.
            //   serverSpecificColorsByParent: Map<parentId, Set<color_sku>>
            //     -- any color_sku may be routed by at most one
            //     specific_colors variant.
            const serverCatchAllKindsByParent = new Map()
            const serverSpecificColorsByParent = new Map()
            for (const e of serverState.value?.included ?? []) {
                if (!isEntityType(e, 'variants')) continue
                const parentId = getVariantParentId(e)
                if (parentId == null) continue
                const kind = e.attributes?.kind
                if (kind === 'specific_colors') {
                    if (!serverSpecificColorsByParent.has(parentId)) {
                        serverSpecificColorsByParent.set(parentId, new Set())
                    }
                    const skus = Array.isArray(e.attributes?.color_skus)
                        ? e.attributes.color_skus
                        : []
                    const bucket = serverSpecificColorsByParent.get(parentId)
                    for (const sku of skus) bucket.add(sku)
                } else if (typeof kind === 'string') {
                    if (!serverCatchAllKindsByParent.has(parentId)) {
                        serverCatchAllKindsByParent.set(parentId, new Set())
                    }
                    serverCatchAllKindsByParent.get(parentId).add(kind)
                }
            }

            for (const variant of pendingVariants.value) {
                groupBPromises.push(
                    (async () => {
                        try {
                            // Resolve parent decoration id.
                            let parentDecoId
                            if (
                                typeof variant.parent === 'string'
                                && variant.parent.startsWith('tmp-')
                            ) {
                                parentDecoId = tempIdToServerId.get(
                                    variant.parent
                                )
                                if (!parentDecoId) {
                                    saveErrors.value.push({
                                        message:
                                            `Skipped variant `
                                            + `(${variant.kind ?? 'unknown'}): `
                                            + `parent decoration failed to save`,
                                        item: variant,
                                    })
                                    return
                                }
                            } else {
                                parentDecoId = variant.parent
                            }

                            // Pre-flight collision check on routing
                            // identity (kind, color_skus). Skip the POST
                            // and surface a clean error rather than
                            // letting the backend 409 on the partial
                            // UNIQUE ``ix_variants_placement_kind_catchall``
                            // (on (placement_id, kind); recreated in
                            // route_fork_schema_086 after 082 dropped the
                            // decoration-keyed original).
                            const parentKey = String(parentDecoId)
                            const variantKind = variant.kind ?? 'catch_all_light'
                            if (variantKind === 'specific_colors') {
                                const existing
                                    = serverSpecificColorsByParent.get(parentKey)
                                if (existing) {
                                    const variantSkus = Array.isArray(
                                        variant.color_skus,
                                    )
                                        ? variant.color_skus
                                        : []
                                    const collidingSku = variantSkus.find(
                                        sku => existing.has(sku),
                                    )
                                    if (collidingSku != null) {
                                        saveErrors.value.push({
                                            message:
                                                `Variant already exists for `
                                                + `color ${collidingSku} on `
                                                + `decoration ${parentDecoId}.`,
                                            item: variant,
                                        })
                                        return
                                    }
                                }
                            } else {
                                const existingKinds
                                    = serverCatchAllKindsByParent.get(parentKey)
                                if (
                                    existingKinds
                                    && existingKinds.has(variantKind)
                                ) {
                                    saveErrors.value.push({
                                        message:
                                            `Variant of kind ${variantKind} `
                                            + `already exists on decoration `
                                            + `${parentDecoId}. Edit the `
                                            + `existing variant instead of `
                                            + `staging a duplicate.`,
                                        item: variant,
                                    })
                                    return
                                }
                            }

                            // Skip empty variants — pebble 8a/12 still
                            // expects every variant to carry artwork on save.
                            // PR #1126 loosened canAddVariant so the user can
                            // stage A+B up front, but the server-side variant
                            // row must not be empty. Surface as a saveError,
                            // not a silent skip (bug fix #161, bar-raiser P2
                            // on #1126).
                            //
                            // bindVariantArtwork branch: when the user picked
                            // an existing Artwork (via the editor-artwork
                            // picker) we set ``artwork_file_id`` directly and
                            // null out ``artwork_file``. In that case the
                            // upload step is skipped -- the artwork already
                            // lives in S3 -- and we POST the variant straight
                            // with the bound id. ``artwork_file`` blob still
                            // wins when both are set (legacy callsites).
                            const preBoundId = variant.artwork_file_id
                            if (!variant.artwork_file && !preBoundId) {
                                saveErrors.value.push({
                                    message:
                                        `Skipped variant `
                                        + `(${variant.kind ?? 'unknown'}): `
                                        + `no artwork attached. Upload `
                                        + `artwork before saving.`,
                                    item: variant,
                                })
                                return
                            }

                            // Upload artwork before the variant POST -- only
                            // when the caller staged a blob. The pre-bound
                            // path (artwork_file_id already set) skips this.
                            let uuid = preBoundId || null
                            if (variant.artwork_file) {
                                try {
                                    uuid = await _uploadArtwork(
                                        variant.artwork_file,
                                        variant.artwork_filename
                                    )
                                } catch (err) {
                                    saveErrors.value.push({
                                        message:
                                            `Artwork upload failed `
                                            + `(${variant.artwork_filename}):`
                                            + ` ${err.message}`,
                                        item: variant,
                                    })
                                    return // skip the variant POST
                                }
                            }

                            await axiosInstance.post(
                                '/api/variants/',
                                buildVariantPostBody(variant, parentDecoId, uuid)
                            )
                        } catch (err) {
                            saveErrors.value.push({
                                message:
                                    `Failed to create variant `
                                    + `(${variant.kind ?? 'unknown'}): `
                                    + `${err.message}`,
                                item: variant,
                            })
                        }
                    })()
                )
            }

            // B.2: Skip-color adds.
            // The staging key is the Placement id (the view-model row's id
            // IS the Placement id post P5.5 PR-3.5). DecorationSkipColor
            // keys on placement_id, so this is exactly the FK the POST
            // needs. The tmp- branch is structurally dead (no producer of
            // pendingDecorations remains) but kept for shape parity.
            for (const [
                placementIdOrTempId,
                colorSet,
            ] of pendingSkipColors.value.entries()) {
                let realPlacementId = placementIdOrTempId
                if (
                    typeof placementIdOrTempId === 'string'
                    && placementIdOrTempId.startsWith('tmp-')
                ) {
                    realPlacementId = tempIdToServerId.get(placementIdOrTempId)
                    if (!realPlacementId) continue // parent failed; error already recorded
                }
                for (const sku of colorSet) {
                    groupBPromises.push(
                        axiosInstance
                            .post(
                                '/api/decoration_skip_colors/',
                                buildSkipColorPostBody(realPlacementId, sku)
                            )
                            .catch((err) => {
                                saveErrors.value.push({
                                    message:
                                        `Failed to add skip color `
                                        + `${sku}: ${err.message}`,
                                })
                            })
                    )
                }
            }

            // B.3: Skip-color deletes. Look up the server-side
            // decoration_skip_colors row by (placement_id, color_sku) in
            // the current serverState.included payload -- getVariantParentId
            // resolves the parent placement id -- then DELETE by row id.
            const skipColorIncluded = (
                serverState.value?.included ?? []
            ).filter(e => isEntityType(e, 'decoration_skip_colors'))
            for (const [
                decoId,
                colorSet,
            ] of pendingSkipColorDeletes.value.entries()) {
                for (const sku of colorSet) {
                    const skipRow = skipColorIncluded.find(
                        e =>
                            getVariantParentId(e) === String(decoId)
                            && e?.attributes?.color_sku === sku
                    )
                    if (!skipRow) continue // already gone or never existed
                    groupBPromises.push(
                        axiosInstance
                            .delete(
                                `/api/decoration_skip_colors/${skipRow.id}/`
                            )
                            .catch((err) => {
                                saveErrors.value.push({
                                    message:
                                        `Failed to delete skip color `
                                        + `${sku}: ${err.message}`,
                                })
                            })
                    )
                }
            }

            // B.4: Pebble 8b PR 1 — reorder edits. PATCH display_order on
            // each affected server decoration and variant. Pending rows
            // carry their new display_order in the POST body emitted in
            // Group A / earlier in Group B, so they need no work here.
            for (const [
                decoId,
                newOrder,
            ] of pendingDecorationOrderEdits.value.entries()) {
                groupBPromises.push(
                    axiosInstance
                        // P5.5 PR-3a + P6 PR-A: URL is
                        // ``/api/placements/<placement_id>/`` --
                        // ``decoId`` here is the REAL placement_id
                        // sourced from the PR-3.5 SAFRS-include
                        // (CampaignProduct.placements). Body shape
                        // unchanged for back-compat with the
                        // server's response wrapper; display_order is
                        // accepted and dropped by the backend
                        // (Placement has no display_order column
                        // today, canonical order is Placement.id ASC).
                        .patch(`/api/placements/${decoId}/`, {
                            data: {
                                type: 'Decoration',
                                id: String(decoId),
                                attributes: { display_order: newOrder },
                            },
                        })
                        .catch((err) => {
                            saveErrors.value.push({
                                message:
                                    `Failed to reorder decoration `
                                    + `${decoId}: ${err.message}`,
                            })
                        })
                )
            }
            for (const [
                varId,
                newOrder,
            ] of pendingVariantOrderEdits.value.entries()) {
                groupBPromises.push(
                    axiosInstance
                        .patch(`/api/variants/${varId}/`, {
                            data: {
                                type: 'Variant',
                                id: String(varId),
                                attributes: { display_order: newOrder },
                            },
                        })
                        .catch((err) => {
                            saveErrors.value.push({
                                message:
                                    `Failed to reorder variant `
                                    + `${varId}: ${err.message}`,
                            })
                        })
                )
            }

            // B.5: Pebble 8b PR 3 — personalization fields. POST pending
            // adds (resolving _tempId parents via tempIdToServerId from
            // Group A), PATCH each entry in pendingPersonalizationFieldEdits,
            // DELETE each id in pendingPersonalizationFieldDeletes. Errors
            // collected into saveErrors; buckets cleared after Group C.
            for (const pf of pendingPersonalizationFields.value) {
                let parentDecoId = pf.parent
                if (
                    typeof parentDecoId === 'string'
                    && parentDecoId.startsWith('tmp-')
                ) {
                    const resolved = tempIdToServerId.get(parentDecoId)
                    if (!resolved) {
                        // Parent decoration POST failed — error already
                        // recorded in Group A. Skip this field.
                        saveErrors.value.push({
                            message:
                                `Skipped personalization field `
                                + `"${pf.label}": parent decoration `
                                + `failed to save`,
                            item: pf,
                        })
                        continue
                    }
                    parentDecoId = resolved
                }
                groupBPromises.push(
                    axiosInstance
                        .post(
                            '/api/personalization_fields/',
                            buildPersonalizationFieldPostBody(
                                pf,
                                parentDecoId
                            )
                        )
                        .catch((err) => {
                            saveErrors.value.push({
                                message:
                                    `Failed to create personalization `
                                    + `field "${pf.label}": ${err.message}`,
                                item: pf,
                            })
                        })
                )
            }
            for (const [
                pfId,
                patch,
            ] of pendingPersonalizationFieldEdits.value.entries()) {
                groupBPromises.push(
                    axiosInstance
                        .patch(
                            `/api/personalization_fields/${pfId}/`,
                            buildPersonalizationFieldPatchBody(pfId, patch)
                        )
                        .catch((err) => {
                            saveErrors.value.push({
                                message:
                                    `Failed to update personalization `
                                    + `field ${pfId}: ${err.message}`,
                            })
                        })
                )
            }
            for (const pfId of pendingPersonalizationFieldDeletes.value) {
                groupBPromises.push(
                    axiosInstance
                        .delete(`/api/personalization_fields/${pfId}/`)
                        .catch((err) => {
                            saveErrors.value.push({
                                message:
                                    `Failed to delete personalization `
                                    + `field ${pfId}: ${err.message}`,
                            })
                        })
                )
            }

            // B.6: Pebble #162/#172 pebble 12 -- placement deletes.
            // ``pendingDecorationDeletes`` holds Placement row ids (the
            // "Decoration" naming is legacy; the delete-placement UI in
            // DecorationInspector / LeftRail stages placementId). Fire one
            // soft-deleting DELETE per staged id against the placement-rooted
            // endpoint (api/src/endpoints/placements.py delete_placement).
            // Skip tmp- ids: those are client-only placements that never
            // reached the server, so there is nothing to delete.
            for (const placementId of pendingDecorationDeletes.value) {
                if (
                    typeof placementId === 'string'
                    && placementId.startsWith('tmp-')
                ) {
                    continue
                }
                groupBPromises.push(
                    axiosInstance
                        .delete(`/api/placements/${placementId}/`)
                        .catch((err) => {
                            saveErrors.value.push({
                                message:
                                    `Failed to delete placement `
                                    + `${placementId}: ${err.message}`,
                            })
                        })
                )
            }

            await Promise.allSettled(groupBPromises)

            // ============================================================
            // GROUP C — reload canonical server state, clear pending
            // buckets (SIMPLIFICATION: regardless of partial failure).
            // ============================================================
            // force: true bypasses the Layer 1.v2 serial-recall throttle.
            // Server state just changed; we MUST refetch to surface the
            // canonical payload regardless of how recently the previous
            // load resolved.
            await loadServerState({ force: true })

            _revokeAllPendingVariantUrls()
            dirty.value = new Map()
            // Field-only autosave: the explicit save flushed the scalar dirty
            // Map too, so the autosave machine settles back to idle.
            fieldAutoSaveState.value = 'idle'
            pendingDecorations.value = []
            pendingVariants.value = []
            pendingSkipColors.value = new Map()
            pendingSkipColorDeletes.value = new Map()
            pendingDecorationOrderEdits.value = new Map()
            pendingVariantOrderEdits.value = new Map()
            pendingPersonalizationFields.value = []
            pendingPersonalizationFieldEdits.value = new Map()
            pendingPersonalizationFieldDeletes.value = new Set()
            // Pebble #162/#172 pebble 12: clear staged placement deletes so
            // the unsavedChangeCount drops to 0 after save. The DELETE HTTP
            // calls fired above in Group B.6; clearing here keeps the Save
            // button and change counter honest after the save round-trip.
            pendingDecorationDeletes.value = new Set()

            // Back-compat: surface the FIRST collected error as `saveError`.
            if (saveErrors.value.length > 0) {
                saveError.value = new Error(saveErrors.value[0].message)
            }
            if (saveErrors.value.length === 0) saveCount.value += 1
        } finally {
            saveInFlight.value = false
        }
    }

    /**
     * Publish supplier-side product meta (from useSanmarProductMeta) so any
     * editor section can read it without prop drilling. No validation; the
     * payload is whatever the composable resolved with (or null when cleared).
     *
     * @param {object|null} meta
     */
    function setSupplierMeta(meta) {
        supplierMeta.value = meta
    }

    /**
     * Publish the open product's supplier_key (see ``supplierKey``).
     *
     * @param {string|null} key
     * @param {boolean} resolved - false while the vendor list is pending
     */
    function setSupplierKey(key, resolved) {
        supplierKey.value = key || null
        supplierKeyResolved.value = !!resolved
    }

    /** Publish a price_breaks body the server just returned for the open product. */
    function _publishPriceBreaks(body) {
        _priceBreaksToken += 1
        _priceBreaksData.value = body ?? null
        _priceBreaksDataKey.value = priceBreaksKey.value
        priceBreaksError.value = null
    }

    /**
     * Remember the distributor's blank cost for a size the supplier did not
     * price (`manual_blank_costs`, shared by every product of the
     * distributor using this supplier style). Publishes the fresh breakdown
     * the server returns. Throws on failure so the caller can show why.
     *
     * @param {string} size
     * @param {number} costCents integer cents > 0
     */
    async function saveManualBlankCost(size, costCents) {
        const id = campaignProductId.value
        if (id == null) return
        const response = await axiosInstance.put(
            `/api/campaign_product/${id}/manual_blank_cost`,
            { size, cost_cents: costCents },
        )
        if (campaignProductId.value !== id) return
        _publishPriceBreaks(response?.data)
    }

    /**
     * Dismiss "the supplier's cost replaced yours" for `sizes`. The manual
     * costs are kept server-side. Throws on failure.
     *
     * @param {string[]} sizes
     */
    async function acknowledgeSupplierCosts(sizes) {
        const id = campaignProductId.value
        if (id == null) return
        const response = await axiosInstance.post(
            `/api/campaign_product/${id}/manual_blank_cost/acknowledge`,
            { sizes },
        )
        if (campaignProductId.value !== id) return
        _publishPriceBreaks(response?.data)
    }

    /**
     * Fetch the open product's per-size price breakdown into `priceBreaks`.
     *
     * A response that lands after the editor moved to another product (or
     * after a newer call) is dropped. A failed fetch clears the breakdown
     * rather than leaving a previous product state's numbers on screen.
     */
    async function loadPriceBreaks() {
        const id = campaignProductId.value
        const key = priceBreaksKey.value
        // Nothing saved to quote yet (the product is still loading).
        if (id == null || key == null) return
        const token = ++_priceBreaksToken
        try {
            const response = await axiosInstance.get(
                `/api/campaign_product/${id}/price_breaks`,
            )
            if (token !== _priceBreaksToken) return
            _priceBreaksData.value = response?.data ?? null
            _priceBreaksDataKey.value = key
            priceBreaksError.value = null
        } catch (err) {
            if (token !== _priceBreaksToken) return
            _priceBreaksData.value = null
            _priceBreaksDataKey.value = null
            priceBreaksError.value = err
        }
    }

    /**
     * Publish the supplier-meta fetch's loading state so readers can tell
     * "not loaded yet" apart from "loaded and empty". Pushed from the
     * section that owns the useSanmarProductMeta fetch (SupplierSkuPanel).
     *
     * @param {boolean} value
     */
    function setSupplierMetaLoading(value) {
        supplierMetaLoading.value = !!value
    }

    /**
     * Publish whether the supplier-meta fetch failed. See
     * `supplierMetaFailed`.
     *
     * @param {boolean} value
     */
    function setSupplierMetaFailed(value) {
        supplierMetaFailed.value = !!value
    }

    /** See `supplierSwitchSnapshot`. */
    function setSupplierSwitchSnapshot(snapshot) {
        supplierSwitchSnapshot.value = snapshot ?? null
    }

    /** See `skuClearedForSwitch`. */
    function setSkuClearedForSwitch(value) {
        skuClearedForSwitch.value = value === true
    }

    /**
     * Arm (or, with null, disarm) the clean-up owed to a confirmed SKU swap.
     * See `pendingSwapCleanup`.
     *
     * @param {{sku: string, oldNames: string[], previous?: object}|null} pending
     */
    function setPendingSwapCleanup(pending) {
        pendingSwapCleanup.value = pending
    }

    // PEBBLE 12 ENTRY POINT:
    /**
     * Stage a new decoration for the currently-open product. Pebble 7
     * surfaces this from the hotspot panel; pebble 12 will translate
     * `pendingDecorations` into JSON:API relationship-creates at save time.
     *
     * @param {{placement_id: string}} entry
     */
    function addDecoration({ placement_id, decoration_type, name }) {
        // Sticky authoring intent: a user reached this action. Latched
        // BEFORE any early return -- an attempted authoring act still means
        // a human was here, and keeping the row is the safe direction.
        _markAuthoringIntent()

        const newEntry = {
            _tempId: nextTempId(),
            placement_id,
            name: name || '',
            description: null,
            // See buildDecorationPostBody — backend column is NOT NULL.
            // Caller can override; otherwise default to 'embroidery'.
            decoration_type: decoration_type || 'embroidery',
        }
        // Clone-on-write per the Map idiom used elsewhere in this store —
        // keeps Vue 3 reactivity honest when an array reference vs. a
        // mutated array would otherwise confuse downstream computeds.
        pendingDecorations.value = [...pendingDecorations.value, newEntry]
    }

    // PEBBLE 12 ENTRY POINT:
    /**
     * Remove a decoration by its id or `_tempId`.
     *
     * Two cases:
     *   - Pending decoration (has a `_tempId` starting with 'tmp-'):
     *     Remove from `pendingDecorations` array directly.
     *   - Server-loaded placement (real id, no 'tmp-' prefix):
     *     Stage a DELETE in `pendingDecorationDeletes` (the id is a
     *     Placement row id; the "Decoration" naming is legacy). The
     *     `decorations` computed filters it out immediately so the UI
     *     updates without waiting for save. On save(), Group B.6 fires a
     *     soft-deleting `DELETE /api/placements/<id>/` per staged id (the
     *     legacy `/api/decorations/<id>/` SAFRS DELETE was retired in
     *     P5.5 PR-2c; the placement-rooted handler lives in
     *     api/src/endpoints/placements.py delete_placement).
     *
     * @param {string|number} idOrTempId
     */
    function removeDecoration(idOrTempId) {
        // Sticky authoring intent: a user reached this action. Latched
        // BEFORE any early return -- an attempted authoring act still means
        // a human was here, and keeping the row is the safe direction.
        _markAuthoringIntent()

        const asStr = String(idOrTempId)
        if (asStr.startsWith('tmp-')) {
            // Pending decoration — drop from the staging array.
            pendingDecorations.value = pendingDecorations.value.filter(
                entry => entry._tempId !== idOrTempId
            )
        } else {
            // Server-loaded decoration — stage a DELETE.
            const next = new Set(pendingDecorationDeletes.value)
            next.add(asStr)
            pendingDecorationDeletes.value = next
        }
    }

    // A removed placement takes the listing's chosen decorated renders with
    // it: the server drops them in the same transaction (GH #4286 / #4287,
    // api/src/services/editor_render_pointers.py drop_listing_renders).
    // Nothing refetches after a delete, so mirror that here -- in the loaded
    // value AND in a pending listing edit, or the next autosave of the
    // listing would PATCH the stale render back onto the product.
    function _dropListingRenders() {
        const attrs = serverState.value?.data?.attributes
        if (attrs && 'storefront_images' in attrs) {
            attrs.storefront_images = withoutRenderImages(attrs.storefront_images)
        }
        if (dirty.value.has('storefront_images')) {
            const pending = dirty.value.get('storefront_images')
            const next = withoutRenderImages(pending)
            if (next !== pending) updateField('storefront_images', next)
        }
    }

    // ---------------------------------------------------------------------
    // The distributor's OWN preview image for one color (card #243).
    //
    // The server stores the image and writes it FIRST in the color's
    // storefront_images list in ONE request
    // (api/src/endpoints/campaign_product_color_previews.py), then returns
    // the whole map. The editor lands that map as server truth so the Stage
    // and Product Listing show the upload at once. A pending listing edit is
    // flushed BEFORE the request (so the server map already contains it); if
    // that flush could not land, the same edit is applied to the pending
    // value (the storefrontImages mirrors) so the next autosave cannot PATCH
    // the upload away -- the same reconciliation _dropListingRenders does.
    // ---------------------------------------------------------------------
    const _colorPreviewStatus = ref({})
    const _IDLE_PREVIEW_STATUS = Object.freeze({ state: 'idle', message: null })

    function colorPreviewStatus(color) {
        return _colorPreviewStatus.value[color] ?? _IDLE_PREVIEW_STATUS
    }

    function _setColorPreviewStatus(color, status) {
        const next = { ..._colorPreviewStatus.value }
        if (status) next[color] = status
        else delete next[color]
        _colorPreviewStatus.value = next
    }

    function _colorPreviewErrorMessage(err, fallback) {
        const body = err?.response?.data
        const message = typeof body?.error === 'string'
            ? body.error
            : body?.error?.message
        return typeof message === 'string' && message ? message : fallback
    }

    function _landStorefrontImages(serverMap, applyToPending) {
        const attrs = serverState.value?.data?.attributes
        if (attrs && serverMap && typeof serverMap === 'object') {
            attrs.storefront_images = serverMap
        }
        if (dirty.value.has('storefront_images')) {
            // Re-run updateField so a pending value that now equals the
            // server's drops out of the dirty map.
            updateField(
                'storefront_images',
                applyToPending(dirty.value.get('storefront_images')),
            )
        }
    }

    /**
     * Upload ``file`` as ``color``'s preview image.
     *
     * @param {string} color - a selected color (the storefront_images key).
     * @param {File|Blob} file
     * @param {string} [filename] - for a Blob with no name (the open canvas).
     * @returns {Promise<object|null>} the server response, or null on failure.
     */
    async function uploadColorPreview(color, file, filename) {
        const cpId = campaignProductId.value
        if (cpId == null || cpId === '' || !color || !file) return null
        _setColorPreviewStatus(color, { state: 'uploading', message: null })
        await flushFieldAutoSave()
        const form = new FormData()
        form.append('color', color)
        form.append('file', file, filename || file.name || 'preview.png')
        try {
            // No Content-Type: axios derives multipart + boundary from
            // FormData (same as _uploadArtwork).
            const response = await axiosInstance.post(
                `/api/campaign_products/${cpId}/color_previews`,
                form,
            )
            if (campaignProductId.value !== cpId) return null
            const data = response?.data ?? {}
            const entry = {
                source: 'upload',
                url: data.url,
                key: data.key,
                color,
            }
            _landStorefrontImages(data.storefront_images, (pending) =>
                withColorUpload(pending, color, entry),
            )
            _setColorPreviewStatus(color, null)
            return data
        } catch (err) {
            if (campaignProductId.value !== cpId) return null
            _setColorPreviewStatus(color, {
                state: 'error',
                message: _colorPreviewErrorMessage(
                    err,
                    'The image could not be uploaded. Try again.',
                ),
            })
            return null
        }
    }

    /**
     * Remove ``color``'s uploaded preview image (its other images stay).
     *
     * @param {string} color
     * @returns {Promise<boolean>}
     */
    async function removeColorPreview(color) {
        const cpId = campaignProductId.value
        if (cpId == null || cpId === '' || !color) return false
        _setColorPreviewStatus(color, { state: 'uploading', message: null })
        await flushFieldAutoSave()
        try {
            const response = await axiosInstance.delete(
                `/api/campaign_products/${cpId}/color_previews`,
                { params: { color } },
            )
            if (campaignProductId.value !== cpId) return false
            _landStorefrontImages(response?.data?.storefront_images, (pending) =>
                withoutColorUploads(pending, color),
            )
            _setColorPreviewStatus(color, null)
            return true
        } catch (err) {
            if (campaignProductId.value !== cpId) return false
            _setColorPreviewStatus(color, {
                state: 'error',
                message: _colorPreviewErrorMessage(
                    err,
                    'The image could not be removed. Try again.',
                ),
            })
            return false
        }
    }

    /**
     * Immediately persist a placement delete (bug fix:
     * fix/placement-delete-persists-immediately).
     *
     * Prior behavior: ``removeDecoration`` only STAGED a server
     * placement delete in ``pendingDecorationDeletes``; the actual
     * ``DELETE /api/placements/<id>/`` fired only inside ``save()``
     * (Group B.6). If the user deleted a placement but never clicked
     * Save, the DELETE never fired, so ``loadServerState`` re-fetched the
     * row on refresh and the "deleted" placement came back.
     *
     * This action mirrors the immediate-persist shape of ``deleteVariant``
     * (and its siblings renameVariant / bindVariantArtwork): the
     * delete is committed to the server right
     * away, awaited, with an optimistic local prune + rollback on
     * failure. Placement-delete is no longer the odd one out.
     *
     * Wire contract (matches api/src/endpoints/placements.py
     * delete_placement):
     *   DELETE /api/placements/<placementId>/   (trailing slash, SAFRS)
     *     -> 204 No Content on success (soft-delete)
     *     -> 404 on missing / already-deleted / cross-tenant (closed
     *        oracle). A 404 is treated as SUCCESS here: the row is gone,
     *        which is exactly the desired end state (idempotent).
     *
     * Two branches, mirroring deleteVariant:
     *   1. Pending placement (``tmp-*`` id) -- a client-only row that
     *      never reached the server. Drop it from ``pendingDecorations``
     *      in place; no HTTP call.
     *   2. Server placement -- optimistically prune the Placement entry
     *      from ``serverState.included`` (so the ``decorations`` computed
     *      re-renders without the row), then fire the DELETE. On a real
     *      4xx/5xx (other than 404) restore the entry and surface the
     *      error via ``saveErrors`` so the existing banner displays it --
     *      the UI must reflect server truth, never silently no-persist.
     *
     * @param {string|number} placementId
     * @returns {Promise<boolean>}  true on success, false on failure
     */
    async function deletePlacement(placementId) {
        // Sticky authoring intent: a user reached this action. Latched
        // BEFORE any early return -- an attempted authoring act still means
        // a human was here, and keeping the row is the safe direction.
        _markAuthoringIntent()

        if (placementId == null || placementId === '') {
            // eslint-disable-next-line no-console
            console.warn('deletePlacement: placementId is required')
            return false
        }

        const idStr = String(placementId)

        // ---- Pending-placement branch -------------------------------
        // tmp-* denotes a client-only row; drop it, no HTTP call.
        if (idStr.startsWith('tmp-')) {
            pendingDecorations.value = pendingDecorations.value.filter(
                entry => entry._tempId !== placementId,
            )
            // Belt-and-suspenders: if the id was somehow also staged for
            // a server delete, clear it too.
            if (pendingDecorationDeletes.value.has(idStr)) {
                const next = new Set(pendingDecorationDeletes.value)
                next.delete(idStr)
                pendingDecorationDeletes.value = next
            }
            return true
        }

        // ---- Server-placement branch --------------------------------
        // Optimistically prune the Placement entry from
        // serverState.included so the decorations computed re-renders
        // without the deleted row. Cache the pruned entry + its index so
        // we can restore on failure (rollback) and keep ordering stable.
        const included = Array.isArray(serverState.value?.included)
            ? serverState.value.included
            : []
        let prunedEntry = null
        let prunedIndex = -1
        for (let i = 0; i < included.length; i += 1) {
            const e = included[i]
            const isPlacementType
                = e?.type === 'Placement' || e?.type === 'placements'
            if (!isPlacementType) continue
            if (String(e.id) !== idStr) continue
            prunedEntry = e
            prunedIndex = i
            break
        }
        if (prunedEntry !== null && serverState.value) {
            const next = included.slice()
            next.splice(prunedIndex, 1)
            serverState.value.included = next
        }

        try {
            await axiosInstance.delete(`/api/placements/${placementId}/`)
            _dropListingRenders()
            return true
        } catch (err) {
            // 404 == already gone (deleted concurrently, or a stale row).
            // The desired end state -- the row absent -- already holds, so
            // treat it as success and leave the optimistic prune in place.
            const status = err?.response?.status
            if (status === 404) {
                _dropListingRenders()
                return true
            }
            // Real failure: roll back the optimistic prune so the user
            // sees the row is still there (server truth), and surface the
            // error via saveErrors for the existing banner. Never silently
            // no-persist.
            if (prunedEntry !== null && serverState.value) {
                const restore = Array.isArray(serverState.value.included)
                    ? serverState.value.included.slice()
                    : []
                const insertAt = Math.min(prunedIndex, restore.length)
                restore.splice(insertAt, 0, prunedEntry)
                serverState.value.included = restore
            }
            saveErrors.value.push({
                message:
                    `Failed to delete placement ${placementId}: `
                    + `${err?.message ?? err}`,
            })
            return false
        }
    }

    // PEBBLE 12 ENTRY POINT:
    /**
     * Stage a new variant under a decoration (pebble 8a Track A). The
     * decoration may be either a server-loaded decoration (parent = server
     * id string) or a pending decoration (parent = `_tempId`). Pebble 12
     * translates these into JSON:API variant-creates at save time and
     * drops the tempIds in favor of server ids returned in the response.
     *
     * `display_order` is computed as `max(existing for this parent) + 1`,
     * counting BOTH server-loaded variants (from `serverState.included`)
     * AND existing pending variants, so the first staged variant under a
     * server decoration with two existing variants gets display_order 3.
     *
     * Deduplication semantics (PR-N4 final shape):
     *
     * Identity is the variant ``kind``. The legacy ``variant_key``
     * column is gone -- the partial UNIQUE on (decoration_id, kind)
     * enforces the catch-all singleton rule at the DB level;
     * application-level dedup mirrors it for staged rows: at most one
     * variant of each kind per decoration. A second add with the same
     * kind is refused -- the existing one is the routing target.
     *
     * The ``specific_colors`` color_skus-overlap rule that used to live
     * here was removed 2026-08-24 with the per-SKU authoring UI (MVP
     * colorization is light/dark only). Nothing stages a
     * specific_colors variant through this action any more; rows that
     * already exist are read-only from the front end and still resolve
     * via the backend resolver tier.
     *
     * Callers pass ``kind`` + ``color_skus`` (catch-alls pass ``[]``).
     * The signature drops the legacy ``variant_key`` parameter; older
     * call sites have been migrated. Pass-throughs from tests that
     * still set ``variant_key`` are tolerated via the standard
     * "unknown destructure key is ignored" JS behavior.
     *
     * Returns the new entry's ``_tempId`` on success. Returns the
     * existing ``_tempId`` if a same-routing pending entry already
     * exists. Returns ``null`` if the routing identity collides with a
     * server-loaded variant.
     *
     * @param {Object} args
     * @param {string|number} args.decorationId
     * @param {string} [args.kind] - one of catch_all_light,
     *   catch_all_dark, single_catch_all. Defaults to catch_all_light.
     * @param {string[]} [args.color_skus] - color SKU list; empty for
     *   catch-alls, which is every kind this action now stages.
     *   Defaults to [].
     * @returns {string|null} the new variant's ``_tempId``, the existing
     *   pending ``_tempId`` on benign re-add, or ``null`` on server
     *   collision.
     */
    function addVariant({
        decorationId,
        kind = null,
        color_skus = null,
    }) {
        // Sticky authoring intent: a user reached this action. Latched
        // BEFORE any early return -- an attempted authoring act still means
        // a human was here, and keeping the row is the safe direction.
        _markAuthoringIntent()

        // Coerce decorationId to string at the boundary so callers passing
        // a numeric primary key still match the string ids returned by
        // getVariantParentId / used in pendingVariants. P2 from /gate
        // (PR #1125) -- prevents a silent-dedupe-bypass footgun.
        const decoId = String(decorationId)
        const effectiveKind = kind ?? 'catch_all_light'
        const effectiveColorSkus = Array.isArray(color_skus)
            ? color_skus.slice()
            : []
        const included = serverState.value?.included

        // Helper -- collect server variants under this decoration.
        const serverVariantsForParent = () => {
            const out = []
            if (Array.isArray(included)) {
                for (const entry of included) {
                    if (!isEntityType(entry, 'variants')) continue
                    const parentId = getVariantParentId(entry)
                    if (parentId !== decoId) continue
                    out.push(entry)
                }
            }
            return out
        }

        // 1. Pending same-kind on same parent -> return existing tempId.
        const existingPending = pendingVariants.value.find(
            v => String(v.parent) === decoId
                && (v.kind ?? 'catch_all_light') === effectiveKind
        )
        if (existingPending) {
            // eslint-disable-next-line no-console
            console.warn(
                `addVariant: kind "${effectiveKind}" already pending `
                + `for decoration ${decorationId}; returning existing tempId`
            )
            return existingPending._tempId
        }
        // 2. Server same-kind on same parent -> refuse. Legacy rows
        // without ``kind`` are treated as catch_all_light (matches the
        // backend column server_default + the consumer read paths).
        for (const entry of serverVariantsForParent()) {
            const serverKind = entry.attributes?.kind ?? 'catch_all_light'
            if (serverKind === effectiveKind) {
                // eslint-disable-next-line no-console
                console.warn(
                    `addVariant: kind "${effectiveKind}" already exists `
                    + `on server for decoration ${decoId}; refusing to add`
                )
                return null
            }
        }

        // Find max display_order across server + pending for this parent.
        let max = 0
        if (Array.isArray(included)) {
            for (const entry of included) {
                if (!isEntityType(entry, 'variants')) continue
                const parentId = getVariantParentId(entry)
                if (parentId !== decoId) continue
                const order = entry.attributes?.display_order
                if (Number.isFinite(order) && order > max) max = order
            }
        }
        for (const v of pendingVariants.value) {
            if (String(v.parent) === decoId && v.display_order > max) {
                max = v.display_order
            }
        }
        // PR-N4: variant_key column is gone. Pending entries carry
        // only routing identity (kind + color_skus) + the artwork
        // binding; refresh-after-save replaces them with server rows
        // keyed by id.
        const newEntry = {
            _tempId: nextTempId(),
            parent: decorationId,
            kind: effectiveKind,
            color_skus: effectiveColorSkus,
            artwork_file: null,
            artwork_filename: null,
            artwork_object_url: null,
            ink_color_hex: null,
            display_order: max + 1,
        }
        pendingVariants.value = [...pendingVariants.value, newEntry]
        return newEntry._tempId
    }

    // PEBBLE 12 ENTRY POINT:
    /**
     * Remove a variant by its `_tempId` (pending) — also revokes its
     * artwork object URL if one was created. Calling with a server-side
     * variant id is currently a no-op (TODO pebble 8b/12: collect server
     * ids in a `pendingVariantDeletes` Set and emit JSON:API
     * relationship-deletes at save time).
     *
     * @param {string|number} idOrTempId
     */
    function removeVariant(idOrTempId) {
        // Sticky authoring intent: a user reached this action. Latched
        // BEFORE any early return -- an attempted authoring act still means
        // a human was here, and keeping the row is the safe direction.
        _markAuthoringIntent()

        const target = pendingVariants.value.find(
            v => v._tempId === idOrTempId
        )
        if (target?.artwork_object_url) {
            const revoke = globalThis.URL?.revokeObjectURL
            if (typeof revoke === 'function') {
                revoke(target.artwork_object_url)
            }
        }
        pendingVariants.value = pendingVariants.value.filter(
            v => v._tempId !== idOrTempId
        )
    }

    // PEBBLE 12 ENTRY POINT:
    /**
     * Attach an artwork file to a pending variant (pebble 8a Track A).
     * Creates a blob preview URL via `URL.createObjectURL` and revokes any
     * prior URL before swapping. Server-side variant artwork edits are
     * out of scope for 8a — calls targeting a server variant id are a
     * no-op for now (TODO 8b: introduce `pendingVariantEdits` map mirror
     * of `pendingDecorationEdits`).
     *
     * @param {string|number} idOrTempId
     * @param {{file: Blob, filename: string}} args
     */
    function updateVariantArtwork(idOrTempId, { file, filename }) {
        // Sticky authoring intent: a user reached this action. Latched
        // BEFORE any early return -- an attempted authoring act still means
        // a human was here, and keeping the row is the safe direction.
        _markAuthoringIntent()

        // Pre-flight validation (pebble 8a /gate — security Low). Reject
        // hostile/oversized files BEFORE mutating store state or creating
        // a blob URL. Bail loudly via console.warn so misuse during
        // development is discoverable; production UI surfaces the same
        // error message via a toast (pebble 12 wiring).
        const err = validateArtworkFile(file)
        if (err) {
            // eslint-disable-next-line no-console
            console.warn(`updateVariantArtwork rejected: ${err}`)
            return
        }
        const idx = pendingVariants.value.findIndex(
            v => v._tempId === idOrTempId
        )
        if (idx === -1) return
        const prior = pendingVariants.value[idx]
        if (prior.artwork_object_url) {
            const revoke = globalThis.URL?.revokeObjectURL
            if (typeof revoke === 'function') {
                revoke(prior.artwork_object_url)
            }
        }
        const create = globalThis.URL?.createObjectURL
        const nextUrl = typeof create === 'function' ? create(file) : null
        const updated = {
            ...prior,
            artwork_file: file,
            artwork_filename: filename,
            artwork_object_url: nextUrl,
        }
        const next = pendingVariants.value.slice()
        next[idx] = updated
        pendingVariants.value = next
    }

    /**
     * Set the ink color hex on a pending variant (pebble 8a Track A).
     * Server-side variant ink edits are out of scope for 8a (TODO 8b/12).
     *
     * @param {string|number} idOrTempId
     * @param {string} hex
     */
    function updateVariantInk(idOrTempId, hex) {
        // Sticky authoring intent: a user reached this action. Latched
        // BEFORE any early return -- an attempted authoring act still means
        // a human was here, and keeping the row is the safe direction.
        _markAuthoringIntent()

        const idx = pendingVariants.value.findIndex(
            v => v._tempId === idOrTempId
        )
        if (idx === -1) return
        const next = pendingVariants.value.slice()
        next[idx] = { ...next[idx], ink_color_hex: hex }
        pendingVariants.value = next
    }

    /**
     * Bind an already-created Artwork (or upload uuid) to a variant.
     *
     * Two callsites today:
     *   1. VariantCard "Replace" button -> UploadArtworkModal in
     *      DecorationInspector. User picks/uploads an Artwork via the
     *      artworks Pinia store; the resulting Artwork.id (or its embedded
     *      S3 uuid) is the ``artworkFileId`` we bind here.
     *   2. AddPlacementModal "Artwork (optional)" field. After the new
     *      decoration is staged and a default variant is added, this is
     *      called with the picked/uploaded Artwork so the new placement
     *      ships with art.
     *
     * Mirrors ``updateVariantArtwork`` (blob upload at save) but for the
     * "Artwork row already exists" path -- no blob, no S3 round trip at
     * save time. The pending-variant entry gets a synthetic ``artwork_file
     * = null`` so the save flow knows to skip the upload step (see
     * Group A.3 in ``save``); the ``artwork_file_id`` carries the
     * already-uploaded asset.
     *
     * Pending variants: mutate ``pendingVariants`` in place.
     * Server variants: optimistic local patch of ``serverState.included``
     * AND fire-and-forget PATCH /api/variants/:id/. The PATCH is committed
     * immediately (not buffered into ``dirty``) because the user has
     * already chosen the artwork in a modal and dismissed it -- there is
     * no "revert" affordance for this action. Mirrors
     * ``setHideFromStorefront`` which uses the same immediate-action
     * pattern.
     *
     * Network errors on the server-variant PATCH are recorded into
     * ``saveErrors`` so the user sees them via the existing error banner;
     * the local optimistic update is left in place because the next
     * full save will re-PATCH if dirty edits exist downstream.
     *
     * @param {string|number} idOrTempId  pending ``_tempId`` or server variant id
     * @param {{artworkFileId: string|number, artworkFilename?: string|null,
     *          inkColorHex?: string|null}} payload
     * @returns {Promise<void>}
     */
    async function bindVariantArtwork(
        idOrTempId,
        { artworkFileId, artworkFilename = null, inkColorHex = null } = {},
    ) {
        // Sticky authoring intent: a user reached this action. Latched
        // BEFORE any early return -- an attempted authoring act still means
        // a human was here, and keeping the row is the safe direction.
        _markAuthoringIntent()

        if (idOrTempId == null) return
        if (artworkFileId == null || artworkFileId === '') {
            // eslint-disable-next-line no-console
            console.warn('bindVariantArtwork: artworkFileId is required')
            return
        }

        // ---- Pending-variant branch ---------------------------------
        const pendingIdx = pendingVariants.value.findIndex(
            v => v._tempId === idOrTempId,
        )
        if (pendingIdx !== -1) {
            const prior = pendingVariants.value[pendingIdx]
            // Revoke any prior blob URL -- the bound Artwork wins.
            if (prior.artwork_object_url) {
                const revoke = globalThis.URL?.revokeObjectURL
                if (typeof revoke === 'function') {
                    revoke(prior.artwork_object_url)
                }
            }
            const updated = {
                ...prior,
                artwork_file: null,
                artwork_file_id: String(artworkFileId),
                artwork_filename:
                    artworkFilename || prior.artwork_filename || null,
                artwork_object_url: null,
            }
            if (inkColorHex) updated.ink_color_hex = inkColorHex
            const next = pendingVariants.value.slice()
            next[pendingIdx] = updated
            pendingVariants.value = next
            return
        }

        // ---- Server-variant branch ----------------------------------
        // Find the variant in serverState.included so we can patch it
        // optimistically. If we can't find it, treat as a no-op (caller
        // passed a stale id).
        const included = serverState.value?.included
        if (!Array.isArray(included)) return
        const target = String(idOrTempId)
        const idx = included.findIndex(
            e => isEntityType(e, 'variants') && String(e.id) === target,
        )
        if (idx === -1) return

        // Optimistic local update so the inspector re-renders immediately.
        const entry = included[idx]
        const nextAttrs = {
            ...(entry.attributes ?? {}),
            artwork_file_id: String(artworkFileId),
        }
        if (artworkFilename) nextAttrs.artwork_filename = artworkFilename
        if (inkColorHex) nextAttrs.ink_color_hex = inkColorHex
        const nextEntry = { ...entry, attributes: nextAttrs }
        // Mutate the array in place via a slice + splice clone so Vue's
        // reactivity picks up the change without us replacing the whole
        // serverState ref (which would unmount/remount the editor tree).
        const nextIncluded = included.slice()
        nextIncluded[idx] = nextEntry
        serverState.value.included = nextIncluded

        // Fire the PATCH. Errors go to saveErrors so the editor surfaces
        // them via the existing error banner. We do NOT await before
        // returning -- the optimistic local state is enough for the UI to
        // re-render; the PATCH races the next user action.
        const attributes = { artwork_file_id: String(artworkFileId) }
        if (artworkFilename) attributes.artwork_filename = artworkFilename
        if (inkColorHex) attributes.ink_color_hex = inkColorHex
        try {
            await axiosInstance.patch(`/api/variants/${target}/`, {
                data: {
                    type: 'Variant',
                    id: target,
                    attributes,
                },
            })
        } catch (err) {
            // Roll the optimistic patch back: a refused bind (e.g. the
            // method/format rule, WA-7c) must not leave the card showing
            // artwork the server never stored. Only while the row still
            // holds THIS optimistic value -- a later bind wins over this
            // rollback. (Matched by id + value, not identity: the stored
            // entries are reactive proxies, never === the raw nextEntry.)
            const current = serverState.value?.included
            if (Array.isArray(current)) {
                const at = current.findIndex(
                    e => isEntityType(e, 'variants')
                        && String(e.id) === target
                        && e.attributes?.artwork_file_id === String(artworkFileId),
                )
                if (at !== -1) {
                    const restored = current.slice()
                    restored[at] = entry
                    serverState.value.included = restored
                }
            }
            // Prefer the server's reason (SAFRS JSON:API errors[].detail)
            // over axios' bare "status code 400".
            const reason = err?.response?.data?.errors?.[0]?.detail
                ?? err?.response?.data?.error
                ?? err?.message
                ?? err
            saveErrors.value.push({
                message:
                    `Failed to bind artwork to variant ${target}: `
                    + `${reason}`,
            })
        }
    }

    /**
     * What a placement-card Replace would touch and may cost, for the scope
     * dialog: GET /api/artworks/<old>/rebind_scope. With ``newArtworkId``
     * the server adds the cost preview for "every product"; with
     * ``placementId`` too, for "this product only". Throws on failure -- the
     * dialog renders its own error state.
     *
     * @param {number|string} artworkId  the artwork being replaced
     * With ``variantId`` (one card of a split pair) the cost leaves out a
     * decoration that replace would not create.
     *
     * @param {{newArtworkId?: number|string, placementId?: number|string,
     *          variantId?: number|string}} [opts]
     * @returns {Promise<object>}
     */
    async function fetchArtworkRebindScope(
        artworkId,
        { newArtworkId, placementId, variantId } = {},
    ) {
        const query = new URLSearchParams()
        if (newArtworkId != null) query.set('new_artwork_id', String(newArtworkId))
        if (placementId != null) query.set('placement_id', String(placementId))
        if (variantId != null) query.set('variant_id', String(variantId))
        const qs = query.toString()
        const resp = await axiosInstance.get(
            `/api/artworks/${artworkId}/rebind_scope${qs ? `?${qs}` : ''}`,
        )
        return resp?.data ?? null
    }

    /**
     * Placement-card Replace, either scope: ONE server write
     * (POST /api/artworks/<old>/rebind) that re-points each affected
     * placement's decoration and moves its variants, in one transaction.
     * The variant-only PATCH is not used: it left the decoration -- what
     * orders, the decorator and previews read -- on the old art (#4082).
     *
     * On success the editor RELOADS the server state rather than guessing
     * a local mirror, so the cards and the stage show exactly what was
     * stored (decoration ids included).
     *
     * ``variantId`` names the card being replaced when it is ONE card of a
     * split light/dark pair still sharing the old art: the server then moves
     * only that card, so the pair stays split (card #307). Omitted for the
     * combined card, which moves the whole pair.
     *
     * @param {{oldArtworkId, newArtworkId, scope: 'placement'|'all', placementId?, variantId?}} args
     * @returns {Promise<object|false>}  the server's counts, or false
     */
    async function replaceArtwork({
        oldArtworkId,
        newArtworkId,
        scope,
        placementId = null,
        variantId = null,
    } = {}) {
        _markAuthoringIntent()
        if (oldArtworkId == null || newArtworkId == null) return false
        const body = { new_artwork_id: Number(newArtworkId), scope }
        if (scope === 'placement') body.placement_id = Number(placementId)
        if (variantId != null) body.variant_id = Number(variantId)

        let result
        try {
            const resp = await axiosInstance.post(
                `/api/artworks/${oldArtworkId}/rebind`,
                body,
            )
            result = resp?.data ?? {}
        } catch (err) {
            saveErrors.value.push({
                message:
                    'Could not replace the decoration: '
                    + `${err?.response?.data?.error ?? err.message ?? err}`,
            })
            return false
        }
        await loadServerState({ force: true, background: true })
        return result
    }

    /**
     * Record a per-color approval override the server has already minted
     * (GH #3833). Called after `POST /placements/<id>/request_per_color_approval`
     * succeeds, so there is nothing to save: this only mirrors the new state
     * into the Placement entry the `decorations` projection reads.
     *
     * Writes to serverState rather than to the projected row, because the
     * projection is rebuilt from serverState on every change and a mutation
     * on the row is lost at the next rebuild. Not an authoring act, so it
     * does not mark the editor dirty.
     *
     * @param {number|string} placementId
     * @param {{id: number, status: string}} approval
     */
    function setPerColorApproval(placementId, approval) {
        const included = serverState.value?.included
        if (!Array.isArray(included)) return
        const override = _perColorApproval(approval)
        if (override == null) return
        const target = String(placementId)
        const idx = included.findIndex(
            e => (e?.type === 'Placement' || e?.type === 'placements')
                && String(e.id) === target,
        )
        if (idx === -1) return
        const entry = included[idx]
        const nextIncluded = included.slice()
        nextIncluded[idx] = {
            ...entry,
            attributes: {
                ...(entry.attributes ?? {}),
                per_color_approval: override,
                per_color_approval_record_id: override.id,
            },
        }
        serverState.value.included = nextIncluded
    }

    /**
     * Hard-delete a Variant (PR feat/delete-variant-on-decoration-inspector).
     *
     * Wire contract (matches DELETE handler in
     * api/src/endpoints/decoration_variants.py, URL renamed by P5.5
     * PR-3a):
     *   DELETE /api/placements/<decorationId>/variants/<variantId>
     *   -> 204 No Content on success
     *   -> 404 on missing/foreign placement or variant
     *
     * Two branches mirror bindVariantArtwork:
     *
     *   1. Pending variant (tmp-* id) -- no server row yet. Drop from
     *      pendingVariants in place. No HTTP call.
     *   2. Server variant -- optimistic prune from serverState.included
     *      then DELETE. On 4xx/5xx restore the entry and surface the
     *      error via saveErrors so the existing banner displays it.
     *
     * The order_items.resolved_variant_id ON DELETE SET NULL FK (PR-N0
     * #1532) preserves the historical audit trail server-side; the
     * frontend does not need to mirror that constraint.
     *
     * @param {Object} args
     * @param {string|number} args.decorationId
     * @param {string|number} args.variantId
     * @returns {Promise<boolean>}  true on success, false on failure
     */
    async function deleteVariant({ decorationId, variantId } = {}) {
        // Sticky authoring intent: a user reached this action. Latched
        // BEFORE any early return -- an attempted authoring act still means
        // a human was here, and keeping the row is the safe direction.
        _markAuthoringIntent()

        if (decorationId == null || decorationId === '') {
            // eslint-disable-next-line no-console
            console.warn('deleteVariant: decorationId is required')
            return false
        }
        if (variantId == null || variantId === '') {
            // eslint-disable-next-line no-console
            console.warn('deleteVariant: variantId is required')
            return false
        }

        // ---- Pending-variant branch ---------------------------------
        // tmp-* prefix denotes a row that exists only locally; drop it.
        const idStr = String(variantId)
        if (idStr.startsWith('tmp-')) {
            const target = pendingVariants.value.find(
                v => v._tempId === variantId,
            )
            if (target?.artwork_object_url) {
                const revoke = globalThis.URL?.revokeObjectURL
                if (typeof revoke === 'function') {
                    revoke(target.artwork_object_url)
                }
            }
            pendingVariants.value = pendingVariants.value.filter(
                v => v._tempId !== variantId,
            )
            return true
        }

        // ---- Server-variant branch ----------------------------------
        // Optimistically prune the entry from serverState.included so
        // DecorationInspector re-renders without the deleted card. Cache
        // the pruned entry + its original index so we can restore on
        // failure (rollback) and keep the ordering stable.
        const included = Array.isArray(serverState.value?.included)
            ? serverState.value.included
            : []
        let prunedEntry = null
        let prunedIndex = -1
        for (let i = 0; i < included.length; i += 1) {
            const e = included[i]
            const isVariantType = e?.type === 'Variant' || e?.type === 'variants'
            if (!isVariantType) continue
            if (String(e.id) !== idStr) continue
            prunedEntry = e
            prunedIndex = i
            break
        }
        if (prunedEntry !== null && serverState.value) {
            const next = included.slice()
            next.splice(prunedIndex, 1)
            serverState.value.included = next
        }

        // P5.5 PR-3a: URL renamed from
        // ``/api/decorations/<id>/variants/<vid>`` to
        // ``/api/placements/<id>/variants/<vid>``. Same legacy-
        // decoration-id transitional semantics as the POST above.
        const url = `/api/placements/${decorationId}/variants/${variantId}`
        try {
            await axiosInstance.delete(url)
            return true
        } catch (err) {
            // Rollback the optimistic prune so the user sees the row
            // is still there (matching server truth).
            if (prunedEntry !== null && serverState.value) {
                const restore = Array.isArray(serverState.value.included)
                    ? serverState.value.included.slice()
                    : []
                const insertAt = Math.min(prunedIndex, restore.length)
                restore.splice(insertAt, 0, prunedEntry)
                serverState.value.included = restore
            }
            saveErrors.value.push({
                message:
                    `Failed to delete variant ${variantId} on `
                    + `decoration ${decorationId}: `
                    + `${err?.message ?? err}`,
            })
            return false
        }
    }

    /**
     * Slice 1 (editable variant cards) -- rename a server-loaded Variant.
     *
     * Wire contract (matches the PATCH handler in
     * api/src/endpoints/decoration_variants.py):
     *
     *   PATCH /api/placements/<placementId>/variants/<variantId>
     *     body: { name: string | null }   # FLAT, not JSON:API
     *     -> 200 { id, kind, color_skus, name, display_order, artwork }
     *     -> 400 on a name > 80 chars OR a body carrying kind/color_skus
     *     -> 404 on missing/foreign placement or variant
     *
     * ``name`` is the ONLY field this endpoint accepts. An empty / null
     * name clears the custom name (the card reverts to its kind label);
     * the backend validator trims whitespace and coerces "" -> NULL.
     *
     * Mirrors the immediate-action pattern of ``bindVariantArtwork`` /
     * ``deleteVariant``: the user committed the edit inline (there is no
     * separate Save affordance for it), so we commit immediately with an
     * optimistic local patch of ``serverState.included`` and roll back on
     * failure. Pending variants (``tmp-*``) have no server row to PATCH;
     * those are renamed locally in place.
     *
     * @param {Object} args
     * @param {string|number} args.decorationId - placement id in the URL
     *        (the load path still surfaces it as the decoration id during
     *        the transition window; the backend resolves it to a
     *        placement id -- same as deleteVariant)
     * @param {string|number} args.variantId
     * @param {string|null} args.name - new name, or null/"" to clear
     * @returns {Promise<boolean>}  true on success, false on failure
     */
    async function renameVariant({ decorationId, variantId, name } = {}) {
        // Sticky authoring intent: a user reached this action. Latched
        // BEFORE any early return -- an attempted authoring act still means
        // a human was here, and keeping the row is the safe direction.
        _markAuthoringIntent()

        if (decorationId == null || decorationId === '') {
            // eslint-disable-next-line no-console
            console.warn('renameVariant: decorationId is required')
            return false
        }
        if (variantId == null || variantId === '') {
            // eslint-disable-next-line no-console
            console.warn('renameVariant: variantId is required')
            return false
        }

        // Normalize: trim + coerce blank -> null so the local optimistic
        // state matches what the backend stores.
        const trimmed = typeof name === 'string' ? name.trim() : null
        const nextName = trimmed || null

        // ---- Pending-variant branch ---------------------------------
        const idStr = String(variantId)
        if (idStr.startsWith('tmp-')) {
            const idx = pendingVariants.value.findIndex(
                v => v._tempId === variantId,
            )
            if (idx === -1) return false
            const next = pendingVariants.value.slice()
            next[idx] = { ...next[idx], name: nextName }
            pendingVariants.value = next
            return true
        }

        // ---- Server-variant branch ----------------------------------
        // Optimistically patch the entry's ``name`` attribute so the card
        // re-renders immediately. Cache the prior value for rollback.
        const included = Array.isArray(serverState.value?.included)
            ? serverState.value.included
            : []
        const target = idStr
        const idx = included.findIndex(
            e =>
                (e?.type === 'Variant' || e?.type === 'variants')
                && String(e.id) === target,
        )
        let priorName
        let hadEntry = false
        if (idx !== -1 && serverState.value) {
            hadEntry = true
            const entry = included[idx]
            priorName = entry.attributes?.name
            const nextEntry = {
                ...entry,
                attributes: { ...(entry.attributes ?? {}), name: nextName },
            }
            const nextIncluded = included.slice()
            nextIncluded[idx] = nextEntry
            serverState.value.included = nextIncluded
        }

        // FLAT body -- the PATCH endpoint reads ``name`` directly off the
        // JSON body (NOT a JSON:API envelope), matching POST create.
        const url =
            `/api/placements/${decorationId}/variants/${variantId}`
        try {
            await axiosInstance.patch(url, { name: nextName })
            return true
        } catch (err) {
            // Roll back the optimistic name so the card matches server
            // truth, and surface the error via the existing banner.
            if (hadEntry && serverState.value) {
                const cur = Array.isArray(serverState.value.included)
                    ? serverState.value.included
                    : []
                const j = cur.findIndex(
                    e =>
                        (e?.type === 'Variant' || e?.type === 'variants')
                        && String(e.id) === target,
                )
                if (j !== -1) {
                    const restore = cur.slice()
                    restore[j] = {
                        ...restore[j],
                        attributes: {
                            ...(restore[j].attributes ?? {}),
                            name: priorName,
                        },
                    }
                    serverState.value.included = restore
                }
            }
            saveErrors.value.push({
                message:
                    `Failed to rename variant ${variantId} on `
                    + `decoration ${decorationId}: `
                    + `${err?.message ?? err}`,
            })
            return false
        }
    }

    /**
     * Slice 2 (editable variant cards) -- mutate a variant's routing KIND.
     *
     * Wire contract (matches the set_kind action in
     * api/src/endpoints/decoration_variants.py):
     *
     *   POST /api/placements/<placementId>/variants/<variantId>/set_kind
     *     body: { kind: "catch_all_light"|"catch_all_dark"|"specific_colors",
     *             color_skus: [str,...] }   # color_skus ONLY for specific
     *     -> 200 { variants: [ {id, kind, color_skus, name, ...}, ... ] }
     *     -> 400 bad kind / empty|stray color_skus for a specific conversion
     *     -> 409 single-owner conflict (another card holds that catch-all)
     *            OR a supplied color owned by another custom card (Slice 3)
     *     -> 404 missing/foreign placement or variant
     *
     * ``kind`` and ``color_skus`` are immutable via the generic PATCH path;
     * this dedicated action is the ONE controlled mutation surface. Mirrors
     * ``renameVariant``: optimistic in-memory patch of the affected
     * variants + rollback on failure + error banner. Pending variants
     * (``tmp-*``) have no server row and are mutated locally in place.
     *
     * @param {Object} args
     * @param {string|number} args.decorationId - placement id in the URL
     *        (surfaced as decoration id during the transition window; the
     *        backend resolves it to a placement id -- same as renameVariant)
     * @param {string|number} args.variantId
     * @param {string} args.kind - target kind
     * @param {string[]} [args.colorSkus] - seed colors for a specific
     *        conversion (required by the backend when kind is specific)
     * @returns {Promise<boolean>}  true on success, false on failure
     */
    async function setVariantKind(
        { decorationId, variantId, kind, colorSkus } = {},
    ) {
        // Sticky authoring intent: a user reached this action. Latched
        // BEFORE any early return -- an attempted authoring act still means
        // a human was here, and keeping the row is the safe direction.
        _markAuthoringIntent()

        if (decorationId == null || decorationId === '') {
            // eslint-disable-next-line no-console
            console.warn('setVariantKind: decorationId is required')
            return false
        }
        if (variantId == null || variantId === '') {
            // eslint-disable-next-line no-console
            console.warn('setVariantKind: variantId is required')
            return false
        }
        if (!kind) {
            // eslint-disable-next-line no-console
            console.warn('setVariantKind: kind is required')
            return false
        }

        const isSpecific = kind === 'specific_colors'
        // Catch-all target clears color_skus; specific carries the seed.
        const nextColorSkus = isSpecific
            ? (Array.isArray(colorSkus) ? colorSkus.slice() : [])
            : []

        // ---- Pending-variant branch ---------------------------------
        const idStr = String(variantId)
        if (idStr.startsWith('tmp-')) {
            const idx = pendingVariants.value.findIndex(
                v => v._tempId === variantId,
            )
            if (idx === -1) return false
            const next = pendingVariants.value.slice()
            next[idx] = {
                ...next[idx],
                kind,
                color_skus: nextColorSkus,
            }
            pendingVariants.value = next
            return true
        }

        // ---- Server-variant branch ----------------------------------
        // Optimistically patch kind + color_skus so the card re-renders
        // immediately; cache the prior values for rollback.
        const included = Array.isArray(serverState.value?.included)
            ? serverState.value.included
            : []
        const target = idStr
        const idx = included.findIndex(
            e =>
                (e?.type === 'Variant' || e?.type === 'variants')
                && String(e.id) === target,
        )
        let priorKind
        let priorColorSkus
        let hadEntry = false
        if (idx !== -1 && serverState.value) {
            hadEntry = true
            const entry = included[idx]
            priorKind = entry.attributes?.kind
            priorColorSkus = entry.attributes?.color_skus
            const nextEntry = {
                ...entry,
                attributes: {
                    ...(entry.attributes ?? {}),
                    kind,
                    color_skus: nextColorSkus,
                },
            }
            const nextIncluded = included.slice()
            nextIncluded[idx] = nextEntry
            serverState.value.included = nextIncluded
        }

        const body = { kind }
        if (isSpecific) body.color_skus = nextColorSkus
        const url =
            `/api/placements/${decorationId}/variants/${variantId}/set_kind`
        try {
            await axiosInstance.post(url, body)
            return true
        } catch (err) {
            // Roll back the optimistic mutation so the card matches server
            // truth, and surface the error via the existing banner.
            if (hadEntry && serverState.value) {
                const cur = Array.isArray(serverState.value.included)
                    ? serverState.value.included
                    : []
                const j = cur.findIndex(
                    e =>
                        (e?.type === 'Variant' || e?.type === 'variants')
                        && String(e.id) === target,
                )
                if (j !== -1) {
                    const restore = cur.slice()
                    restore[j] = {
                        ...restore[j],
                        attributes: {
                            ...(restore[j].attributes ?? {}),
                            kind: priorKind,
                            color_skus: priorColorSkus,
                        },
                    }
                    serverState.value.included = restore
                }
            }
            saveErrors.value.push({
                message:
                    `Failed to change kind for variant ${variantId} on `
                    + `decoration ${decorationId}: `
                    + `${err?.message ?? err}`,
            })
            return false
        }
    }

    /**
     * Bind a saved COLORWAY to a variant, and adopt the snapshot the server
     * writes back.
     *
     * Card: Embroidery colorway editing (MUST-SHIP)
     * https://trello.com/c/2hcgDM7J
     *
     * Wire contract (matches ``post_set_colorway`` in
     * api/src/endpoints/decoration_variants.py):
     *
     *   POST /api/placements/<placementId>/variants/<variantId>/set_colorway
     *     body: { colorway_id: int, stop_count: int, garment_hex: str|null }
     *     -> 200 { selected_colorway_id, threads }   # the SNAPSHOT
     *     -> 400 malformed body, or a slot-count mismatch
     *     -> 404 missing/foreign placement, variant, or colorway (closed
     *            oracle -- a 404 here does NOT mean the row is absent)
     *
     * NO OPTIMISTIC PATCH, deliberately -- and this is the one place this
     * module departs from its sibling actions (``renameVariant``,
     * ``setVariantKind``), so the reason matters.
     *
     * Those two send a value the client already knows and can therefore
     * predict. This one cannot: the threads a variant sews are RESOLVED
     * server-side against the garment colour, so a `match_garment` or
     * `contrast_auto` slot only becomes a concrete, orderable thread inside
     * ``colorway_binding.resolve_slots``. Guessing locally would render
     * threads the variant does not actually sew, and would then be silently
     * overwritten -- the worst of both. So we wait for the response and adopt
     * exactly what was persisted.
     *
     * That also means there is nothing to roll back on failure: the endpoint
     * runs every check before its first assignment, so a refusal leaves the
     * variant exactly as it was.
     *
     * COSTS REAL MONEY. A successful bind makes the backend buy an Artifi
     * on-garment composite, which counts against a hard annual cap. Callers
     * must not re-bind the already-bound colorway (VariantCard's
     * ``onPickColorway`` short-circuits a re-pick for this reason).
     *
     * @param {Object} args
     * @param {string|number} args.decorationId - placement id in the URL
     * @param {string|number} args.variantId
     * @param {number} args.colorwayId
     * @param {number} args.stopCount - slots in the colorway being bound
     * @param {string|null} [args.garmentHex] - the garment colour non-fixed
     *        slots resolve against; null when unknown, which the backend
     *        treats as unanswerable rather than guessing
     * @returns {Promise<boolean>} true on success
     */
    async function setVariantColorway(
        { decorationId, variantId, colorwayId, stopCount, garmentHex } = {},
    ) {
        // Sticky authoring intent: a user reached this action. Latched
        // BEFORE any early return -- an attempted authoring act still means
        // a human was here, and keeping the row is the safe direction.
        _markAuthoringIntent()

        if (decorationId == null || decorationId === '') {
            // eslint-disable-next-line no-console
            console.warn('setVariantColorway: decorationId is required')
            return false
        }
        if (variantId == null || variantId === '') {
            // eslint-disable-next-line no-console
            console.warn('setVariantColorway: variantId is required')
            return false
        }
        if (!Number.isInteger(colorwayId)) {
            // eslint-disable-next-line no-console
            console.warn('setVariantColorway: colorwayId must be an integer')
            return false
        }
        if (!Number.isInteger(stopCount) || stopCount < 0) {
            // eslint-disable-next-line no-console
            console.warn(
                'setVariantColorway: stopCount must be a non-negative integer',
            )
            return false
        }

        // A pending row has no server id for the URL to name. Posting would
        // 404, and a closed-oracle 404 reads to the user like a permissions
        // problem rather than "save the product first".
        if (String(variantId).startsWith('tmp-')) {
            // eslint-disable-next-line no-console
            console.warn(
                'setVariantColorway: cannot bind a colorway to an unsaved '
                + 'variant; save the product first',
            )
            return false
        }

        const url =
            `/api/placements/${decorationId}/variants/${variantId}`
            + '/set_colorway'
        const body = {
            colorway_id: colorwayId,
            stop_count: stopCount,
            // Explicit null rather than an omitted key: the endpoint
            // distinguishes "no garment colour known" (null, legal) from a
            // malformed body, and an absent key would read as the latter to a
            // future stricter validator.
            garment_hex: garmentHex ?? null,
        }

        try {
            const resp = await axiosInstance.post(url, body)
            const snapshot = resp?.data ?? null

            // Adopt the SERVER's snapshot. See the docstring for why this is
            // read from the response rather than predicted.
            const included = Array.isArray(serverState.value?.included)
                ? serverState.value.included
                : []
            const target = String(variantId)
            const idx = included.findIndex(
                e =>
                    (e?.type === 'Variant' || e?.type === 'variants')
                    && String(e.id) === target,
            )
            if (idx !== -1 && serverState.value && snapshot) {
                const entry = included[idx]
                const nextIncluded = included.slice()
                nextIncluded[idx] = {
                    ...entry,
                    attributes: {
                        ...(entry.attributes ?? {}),
                        colorway_id: snapshot.selected_colorway_id ?? null,
                        colorway_threads_json: snapshot.threads ?? null,
                    },
                }
                serverState.value.included = nextIncluded
            }
            return true
        } catch (err) {
            // Nothing to undo -- the endpoint is atomic on refusal.
            saveErrors.value.push({
                message:
                    `Failed to set thread colors for variant ${variantId} on `
                    + `decoration ${decorationId}: `
                    + `${err?.message ?? err}`,
            })
            return false
        }
    }

    // ---------------------------------------------------------------------
    // Split / merge shade routes
    // ---------------------------------------------------------------------
    //
    // Card: Decoration routing + colorway UX rework (product editor)
    //       https://trello.com/c/vc8U5340
    //
    // THE MODEL (Jake, 2026-08-29). Two kinds, not three:
    //
    //   "Light with no dark sibling IS 'all colors', so I can split to light
    //    and dark or merge back to all. If I split then the colorway editor
    //    needs to be aware of this, and create a new colorway for the newly
    //    added route(s). If I merge back to one route for all colors I still
    //    need to be able to choose or edit the colorway, and it shouldn't
    //    delete any colorways -- we'd do that manually on the decoration
    //    detail page."
    //
    // WHY NOT single_catch_all. There IS a kind that literally means "all
    // colours", and it is deliberately not used here: set_kind refuses it as
    // a target ("single_catch_all is not a selectable target",
    // decoration_variants.py:764) and the delete path actively demotes a lone
    // one back to catch_all_light (the FREE THE COMPLEMENT block). A lone
    // catch_all_light IS the all-colours state; the resolver falls through to
    // the light tier for every colour when no dark row claims one. Adding a
    // third kind to record a fact the sibling set already carries would be a
    // second answer to one question (design-philosophy principle 4).
    //
    // So split CREATES the dark sibling and merge DELETES it. Neither
    // re-kinds anything, and both are consistent with what delete already
    // does.

    /**
     * Helper: the placement's live server variant rows, newest state first
     * read from ``serverState.included``. Returns ``[]`` when nothing is
     * loaded. Pending (tmp-*) rows are deliberately excluded -- split/merge
     * both talk to the server about rows that HAVE ids.
     */
    function _serverVariantEntries() {
        return Array.isArray(serverState.value?.included)
            ? serverState.value.included.filter(
                e => e?.type === 'Variant' || e?.type === 'variants',
            )
            : []
    }

    /**
     * Copy one serialized variant row (``_serialize_variant``: artwork
     * NESTED, colorway as a snapshot summary) onto ``attributes`` in the
     * shape the load path writes into ``serverState.included``.
     */
    function _variantAttrsFromServerRow(row) {
        const art = row?.artwork ?? {}
        const colorway = row?.colorway ?? null
        return {
            kind: row.kind,
            color_skus: Array.isArray(row.color_skus) ? row.color_skus : [],
            name: row.name ?? null,
            display_order: Number.isFinite(row.display_order)
                ? row.display_order
                : 0,
            artwork_file_id: art.artwork_file_id ?? null,
            artwork_filename: art.filename ?? null,
            ink_color_hex: art.ink_color_hex ?? null,
            colorway_id: colorway?.selected_colorway_id ?? null,
            colorway_threads_json: colorway?.threads ?? null,
        }
    }

    /**
     * Split a placement into a light route and a dark route, applying its
     * existing decoration to the shades the user chose.
     *
     * Jake, 2026-10-21: "When I do that the existing decoration is applied to
     * light, and there's no decoration for dark. I want you to pop a dialog
     * asking which colors (light/dark) the existing decoration should be
     * applied to." The dialog (DecorationInspector + SplitShadesDialog) owns
     * the question; this action owns the write.
     *
     * Wire contract (``post_split_shades`` in
     * api/src/endpoints/decoration_variants.py):
     *
     *   POST /api/placements/<decorationId>/split_shades
     *     body: { shades: ["light"] | ["dark"] | ["light", "dark"] }
     *     -> 200 { variants: [<light>, <dark>] }
     *
     * ONE atomic request: the server leaves exactly one light and one dark
     * row, a chosen shade carrying the decoration and an unchosen one empty.
     * The returned rows are adopted into ``serverState.included`` because
     * THAT is the projection every surface reads (PR #3084).
     *
     * Then the colorway (embroidery only -- other methods have none): each
     * chosen shade sews the colorway DECLARED for it when one exists (Jake:
     * "the same decoration for both light and dark, but with the matching
     * colorway"), otherwise the light row's colorway from before the split
     * (his earlier rule: "copy the light one unless a dark colorway already
     * exists"). A bind is skipped when the row already sews that colorway,
     * because every bind buys a paid Artifi render. A bind failure is NOT a
     * failed split -- setVariantColorway has already banner'd it, and the
     * routes exist.
     *
     * @param {Object} args
     * @param {string|number} args.decorationId - placement id in the URL
     * @param {Array<'light'|'dark'>} args.shades - where the decoration goes
     * @param {Array} [args.colorways] - the placement's saved colorways
     * @param {Object} [args.shadeContext] - per shade
     *        ``{garmentColors, garmentHex}``: the product colours of that
     *        shade (to rank declared colorways by contrast) and one
     *        representative colour (to resolve garment-dependent slots)
     * @returns {Promise<{ok: boolean, lightVariantId: (string|number|null),
     *          darkVariantId: (string|number|null)}>}
     */
    // Rows whose colorway a split is binding RIGHT NOW (variant ids, as
    // strings). While a row is here the split is its only colorway writer:
    // VariantCard's one-shot auto-default holds off (it does not latch), so
    // a card the split creates or expands cannot fire a second, paid
    // set_colorway for the same row. If the split binds nothing for a shade,
    // the row leaves this set unbound and the auto-default runs as before.
    const colorwayBindsPending = ref(new Set())

    function isColorwayBindPending(variantId) {
        return variantId != null && colorwayBindsPending.value.has(String(variantId))
    }

    function _setColorwayBindsPending(ids, pending) {
        const next = new Set(colorwayBindsPending.value)
        for (const id of ids) {
            if (id == null) continue
            if (pending) next.add(String(id))
            else next.delete(String(id))
        }
        colorwayBindsPending.value = next
    }

    async function splitPlacementShades(
        { decorationId, shades, colorways, shadeContext } = {},
    ) {
        // Sticky authoring intent: a user reached this action. Latched
        // BEFORE any early return -- an attempted authoring act still means
        // a human was here, and keeping the row is the safe direction.
        _markAuthoringIntent()

        const fail = { ok: false, lightVariantId: null, darkVariantId: null }
        if (decorationId == null || decorationId === '') {
            // eslint-disable-next-line no-console
            console.warn('splitPlacementShades: decorationId is required')
            return fail
        }
        // A pending placement has no server id for the URL to name.
        if (String(decorationId).startsWith('tmp-')) {
            // eslint-disable-next-line no-console
            console.warn(
                'splitPlacementShades: cannot split an unsaved placement; '
                + 'save the product first',
            )
            return fail
        }
        const SHADES = ['light', 'dark']
        if (
            !Array.isArray(shades)
            || shades.length === 0
            || shades.some(s => !SHADES.includes(s))
        ) {
            // eslint-disable-next-line no-console
            console.warn(
                'splitPlacementShades: shades must name light and/or dark',
            )
            return fail
        }

        let rows = null
        try {
            const resp = await axiosInstance.post(
                `/api/placements/${decorationId}/split_shades`,
                { shades },
            )
            rows = Array.isArray(resp?.data?.variants) ? resp.data.variants : null
        } catch (err) {
            saveErrors.value.push({
                message:
                    `Failed to split light and dark on decoration `
                    + `${decorationId}: ${err?.response?.data?.error ?? err?.message ?? err}`,
            })
            return fail
        }
        const light = rows?.find(r => r?.kind === 'catch_all_light') ?? null
        const dark = rows?.find(r => r?.kind === 'catch_all_dark') ?? null
        if (!light || !dark) {
            saveErrors.value.push({
                message:
                    'Failed to split light and dark: the server did not '
                    + 'return both routes.',
            })
            return fail
        }

        // The split owns these rows' colorway from here until its binds
        // settle -- marked BEFORE the rows reach the projection, so no card
        // can mount (or expand) unbound-and-unguarded in between.
        _setColorwayBindsPending([light.id, dark.id], true)
        try {
            return await _adoptSplitAndBind({
                decorationId, shades, colorways, shadeContext, light, dark,
            })
        } finally {
            _setColorwayBindsPending([light.id, dark.id], false)
        }
    }

    async function _adoptSplitAndBind(
        { decorationId, shades, colorways, shadeContext, light, dark },
    ) {
        // What the light row sewed BEFORE the split -- the colorway a route
        // copies when none is declared for its shade.
        const entries = _serverVariantEntries()
        const lightEntry = entries.find(e => String(e.id) === String(light.id))
        const lightColorwayBefore = lightEntry?.attributes?.colorway_id ?? null

        // Adopt the server's rows into the PROJECTION.
        if (serverState.value) {
            const next = Array.isArray(serverState.value.included)
                ? serverState.value.included.slice()
                : []
            for (const row of [light, dark]) {
                const attrs = _variantAttrsFromServerRow(row)
                const idx = next.findIndex(
                    e =>
                        (e?.type === 'Variant' || e?.type === 'variants')
                        && String(e.id) === String(row.id),
                )
                if (idx !== -1) {
                    next[idx] = {
                        ...next[idx],
                        attributes: { ...(next[idx].attributes ?? {}), ...attrs },
                    }
                    continue
                }
                // A row the server just created. getVariantParentId reads the
                // relationship FIRST and these attributes only as fallbacks,
                // and which one a payload carries varies (placement-rooted is
                // canonical, decoration-rooted the legacy alias) -- so copy
                // the light row's answer for both. The sibling is by
                // definition on the same placement; rebuilding the pointer
                // would have to re-guess its shape.
                next.push({
                    type: 'variants',
                    id: String(row.id),
                    attributes: {
                        ...attrs,
                        placement_id: lightEntry?.attributes?.placement_id ?? null,
                        decoration_id: lightEntry?.attributes?.decoration_id ?? null,
                    },
                    relationships: { ...(lightEntry?.relationships ?? {}) },
                })
            }
            serverState.value.included = next
        }

        const list = Array.isArray(colorways) ? colorways : []
        if (list.length > 0) {
            for (const [shade, row] of [['light', light], ['dark', dark]]) {
                if (!shades.includes(shade)) continue
                const ctx = shadeContext?.[shade] ?? {}
                const target = resolveShadeColorwayId({
                    shade,
                    colorways: list,
                    garmentColors: ctx.garmentColors,
                    fallbackId: lightColorwayBefore,
                })
                if (target == null) continue
                if (target === (row.colorway?.selected_colorway_id ?? null)) continue
                const pick = list.find(c => c && c.id === target)
                await setVariantColorway({
                    decorationId,
                    variantId: row.id,
                    colorwayId: target,
                    stopCount: Array.isArray(pick?.threads) ? pick.threads.length : 0,
                    garmentHex: ctx.garmentHex ?? null,
                })
            }
        }

        return { ok: true, lightVariantId: light.id, darkVariantId: dark.id }
    }

    /**
     * Merge a light + dark pair back to a single light route standing for
     * every colour.
     *
     * Deletes the DARK row only. The surviving ``catch_all_light`` then
     * resolves for every colour (the resolver falls through to the light
     * tier), which is exactly the all-colours state -- no re-kind needed, and
     * consistent with the delete path's own FREE THE COMPLEMENT handling.
     *
     * NEVER DELETES A COLORWAY. Jake: "it shouldn't delete any colorways --
     * we'd do that manually on the decoration detail page." A colorway is an
     * artwork-version asset shared across products; removing a ROUTE must not
     * destroy one.
     *
     * The survivor's colorway: if an "all"-substrate colorway exists (one
     * whose ``substrate_target`` is null -- NULL means all substrates, which
     * the DB CHECK already permits), switch to it, because it is a declared
     * answer rather than an assumption. Otherwise the survivor keeps what it
     * already sews and the result carries ``colorwayWasAssumed: true`` so the
     * caller can say WHICH one was picked and how to change it.
     *
     * @param {Object} args
     * @param {string|number} args.decorationId - placement id in the URL
     * @param {string|number} args.variantId - the LIGHT row (the survivor)
     * @param {Array} [args.colorways] - the placement's saved colorways
     * @param {string|null} [args.garmentHex]
     * @returns {Promise<{ok: boolean, colorwayId: (number|null),
     *          colorwayName: (string|null), colorwayWasAssumed: boolean}>}
     */
    async function mergeVariantShades(
        { decorationId, variantId, colorways, garmentHex } = {},
    ) {
        // Sticky authoring intent: a user reached this action. Latched
        // BEFORE any early return -- an attempted authoring act still means
        // a human was here, and keeping the row is the safe direction.
        _markAuthoringIntent()

        const fail = {
            ok: false,
            colorwayId: null,
            colorwayName: null,
            colorwayWasAssumed: false,
        }
        if (decorationId == null || decorationId === '') {
            // eslint-disable-next-line no-console
            console.warn('mergeVariantShades: decorationId is required')
            return fail
        }
        if (variantId == null || variantId === '') {
            // eslint-disable-next-line no-console
            console.warn('mergeVariantShades: variantId is required')
            return fail
        }

        const entries = _serverVariantEntries()
        const target = String(variantId)
        const survivor = entries.find(e => String(e.id) === target)
        if (!survivor || survivor.attributes?.kind !== 'catch_all_light') {
            // eslint-disable-next-line no-console
            console.warn(
                'mergeVariantShades: the survivor must be a loaded '
                + 'catch_all_light row',
            )
            return fail
        }
        const dark = entries.find(
            e => e.attributes?.kind === 'catch_all_dark',
        )
        if (!dark) {
            // eslint-disable-next-line no-console
            console.warn(
                'mergeVariantShades: no dark route to merge back',
            )
            return fail
        }

        // deleteVariant owns the optimistic prune + rollback + error banner.
        const ok = await deleteVariant({
            decorationId,
            variantId: dark.id,
        })
        if (!ok) return fail

        // Which colorway the survivor is left sewing.
        const list = Array.isArray(colorways) ? colorways : []
        const currentId = survivor.attributes?.colorway_id ?? null
        // substrate_target NULL means ALL substrates -- an explicit "works on
        // any garment" declaration, not a missing one, so it is the honest
        // choice for a route that now covers every colour.
        const allColorway = list.find(
            c => c && (c.substrate_target ?? null) === null,
        )
        if (allColorway && allColorway.id !== currentId) {
            const bound = await setVariantColorway({
                decorationId,
                variantId,
                colorwayId: allColorway.id,
                stopCount: Array.isArray(allColorway.threads)
                    ? allColorway.threads.length
                    : 0,
                garmentHex: garmentHex ?? null,
            })
            if (bound) {
                return {
                    ok: true,
                    colorwayId: allColorway.id,
                    colorwayName: allColorway.name ?? null,
                    // A declared all-substrate colorway is an answer, not a
                    // guess -- nothing to warn about.
                    colorwayWasAssumed: false,
                }
            }
            // The bind failed (setVariantColorway already banner'd it). The
            // merge itself stood, and the survivor still sews what it did
            // before -- which IS now an assumption, so say so.
        }
        if (allColorway && allColorway.id === currentId) {
            return {
                ok: true,
                colorwayId: allColorway.id,
                colorwayName: allColorway.name ?? null,
                colorwayWasAssumed: false,
            }
        }

        const kept = currentId != null
            ? list.find(c => c && c.id === currentId) ?? null
            : null
        return {
            ok: true,
            colorwayId: currentId,
            colorwayName: kept?.name ?? null,
            // No colorway declares itself for all substrates, so the one the
            // survivor carries was chosen for LIGHT garments and is now
            // sewing dark ones too. That is an assumption the user has to be
            // told about -- and it is only an assumption when there is
            // actually a binding to assume about.
            colorwayWasAssumed: currentId != null,
        }
    }

    /**
     * Internal (Slice 4): apply an ``affected variants`` array from an
     * assign_color / reset_color / set_colors response to serverState.
     * Patches survivors (shrunk color_skus / changed kind|name) and prunes
     * cards marked ``deleted:true`` (the emptied-custom-card auto-delete).
     * The server is authoritative for the affected set. No-op when there
     * is no serverState or no affected rows.
     *
     * @param {Array} affected - the response `variants` array
     */
    function _applyAffectedVariants(affected) {
        if (!serverState.value || !Array.isArray(affected) || !affected.length) {
            return
        }
        const cur = Array.isArray(serverState.value.included)
            ? serverState.value.included.slice()
            : []
        for (const a of affected) {
            if (a == null || a.id == null) continue
            const aid = String(a.id)
            const j = cur.findIndex(
                e =>
                    (e?.type === 'Variant' || e?.type === 'variants')
                    && String(e.id) === aid,
            )
            if (a.deleted) {
                if (j !== -1) cur.splice(j, 1)
                continue
            }
            if (j !== -1) {
                cur[j] = {
                    ...cur[j],
                    attributes: {
                        ...(cur[j].attributes ?? {}),
                        ...(a.kind !== undefined ? { kind: a.kind } : {}),
                        ...(Array.isArray(a.color_skus)
                            ? { color_skus: a.color_skus }
                            : {}),
                        ...(a.name !== undefined ? { name: a.name } : {}),
                    },
                }
            }
        }
        serverState.value.included = cur
    }

    /**
     * Internal (Slice 4): land a per-product routing map
     * (``color_overrides`` / ``manual_ld``) directly on the canonical
     * serverState attribute view WITHOUT marking it dirty. Mirrors
     * ``setHideFromStorefront``: the assign_color / reset_color endpoint
     * already persisted the map server-side, so this is the committed
     * post-write value, not a buffered edit. Landing it here (rather than
     * via ``updateField``) keeps the resolver -- which reads
     * ``getEffectiveAttribute('color_overrides')`` -- in sync immediately
     * AND avoids a spurious dirty flag that a later CP PATCH would
     * re-flush. No-op before serverState has loaded.
     *
     * @param {string} key - 'color_overrides' or 'manual_ld'
     * @param {(prev: object) => object} updater - pure map transform
     */
    function _landRoutingMap(key, updater) {
        const attrs = serverState.value?.data?.attributes
        if (!attrs) return
        const prev = (attrs[key] && typeof attrs[key] === 'object')
            ? attrs[key]
            : {}
        attrs[key] = updater({ ...prev })
    }

    /**
     * Slice 4 (editable variant cards) -- assign ONE color to a chosen
     * variant from the ColorFilmstrip's per-variant menu, steal-silently.
     *
     * Wire contract (matches the assign_color action in
     * api/src/endpoints/decoration_variants.py):
     *
     *   POST /api/placements/<placementId>/variants/<variantId>/assign_color
     *     body: { color_sku: str }
     *     -> 200 { variants: [ {id, kind, color_skus, name}, ...,
     *                          {id, deleted:true} ] }
     *     -> 400 stray/malformed color_sku
     *     -> 404 missing/foreign placement or variant
     *
     * The BACKEND owns the routing-input writes atomically (money path):
     *   - catch-all target -> writes color_overrides[sku]=light|dark +
     *     clears the sku from any specific owner (auto-delete emptied).
     *   - specific target  -> steal-transfer (add sku, steal from prior
     *     owner, clear catch-all pin, auto-delete emptied).
     *   - single catch-all -> clears all explicit routing for the sku.
     *
     * The frontend reflects the committed post-write state locally so the
     * badge + Stage re-render immediately:
     *   - patch every affected variant from the response.
     *   - land the per-product color_overrides / manual_ld deltas on
     *     serverState (the map the resolver reads) matching what the
     *     backend wrote, so no reload is needed.
     *
     * @param {Object} args
     * @param {string|number} args.decorationId - placement id in the URL
     * @param {string|number} args.variantId    - the target variant id
     * @param {string} args.colorSku            - the color to assign
     * @param {string} args.targetKind          - the target variant's kind
     *        (drives which local routing-map delta to reflect; the backend
     *        is authoritative, this only keeps the client view in sync)
     * @returns {Promise<boolean>}
     */
    async function assignColor(
        { decorationId, variantId, colorSku, targetKind } = {},
    ) {
        // Sticky authoring intent: a user reached this action. Latched
        // BEFORE any early return -- an attempted authoring act still means
        // a human was here, and keeping the row is the safe direction.
        _markAuthoringIntent()

        if (decorationId == null || decorationId === '') {
            // eslint-disable-next-line no-console
            console.warn('assignColor: decorationId is required')
            return false
        }
        if (variantId == null || variantId === '') {
            // eslint-disable-next-line no-console
            console.warn('assignColor: variantId is required')
            return false
        }
        if (!colorSku) {
            // eslint-disable-next-line no-console
            console.warn('assignColor: colorSku is required')
            return false
        }

        // Pending (tmp-*) variants have no server row; the real transfer
        // happens once they persist. Mutate the pending target locally for
        // the specific case; catch-all pending targets are pre-save scratch
        // and need no override write yet.
        const idStr = String(variantId)
        if (idStr.startsWith('tmp-')) {
            if (targetKind === 'specific_colors') {
                const idx = pendingVariants.value.findIndex(
                    v => v._tempId === variantId,
                )
                if (idx === -1) return false
                const next = pendingVariants.value.slice()
                const cur = Array.isArray(next[idx].color_skus)
                    ? next[idx].color_skus
                    : []
                if (!cur.includes(colorSku)) {
                    next[idx] = {
                        ...next[idx],
                        color_skus: [...cur, colorSku],
                    }
                    pendingVariants.value = next
                }
            }
            return true
        }

        const url =
            `/api/placements/${decorationId}/variants/${variantId}/assign_color`
        try {
            const resp = await axiosInstance.post(url, { color_sku: colorSku })
            _applyAffectedVariants(resp?.data?.variants)

            // Reflect the per-product routing-map deltas the backend wrote,
            // so the resolver-driven badge updates without a reload.
            if (targetKind === 'catch_all_light'
                || targetKind === 'catch_all_dark') {
                const flag = targetKind === 'catch_all_light'
                    ? 'light'
                    : 'dark'
                _landRoutingMap('color_overrides', (m) => {
                    m[colorSku] = flag
                    return m
                })
            } else {
                // specific / single targets: the backend cleared any pin on
                // this sku (steal-transfer / single fall-through).
                _landRoutingMap('color_overrides', (m) => {
                    delete m[colorSku]
                    return m
                })
                if (targetKind === 'single_catch_all') {
                    _landRoutingMap('manual_ld', (m) => {
                        delete m[colorSku]
                        return m
                    })
                }
            }
            return true
        } catch (err) {
            saveErrors.value.push({
                message:
                    `Failed to assign color ${colorSku} to variant `
                    + `${variantId} on decoration ${decorationId}: `
                    + `${err?.message ?? err}`,
            })
            return false
        }
    }

    /**
     * Slice 4 (editable variant cards) -- reset ONE color to full hex
     * auto-classification from the ColorFilmstrip's per-variant menu.
     *
     * Wire contract (matches the reset_color action in
     * api/src/endpoints/decoration_variants.py):
     *
     *   POST /api/placements/<placementId>/reset_color
     *     body: { color_sku: str }
     *     -> 200 { variants: [ {id, color_skus}, ..., {id, deleted:true} ] }
     *     -> 400 stray/malformed color_sku
     *     -> 404 missing/foreign placement
     *
     * The backend clears the color from ALL THREE routing-input sources
     * (color_overrides + manual_ld + any specific card's color_skus,
     * auto-deleting an emptied card). The frontend reflects the committed
     * state: patch affected variants + clear both maps for the sku locally.
     *
     * Placement-scoped (no variant id) -- reset removes the color from
     * everything on the placement.
     *
     * @param {Object} args
     * @param {string|number} args.decorationId - placement id in the URL
     * @param {string} args.colorSku
     * @returns {Promise<boolean>}
     */
    async function resetColorToAuto({ decorationId, colorSku } = {}) {
        // Sticky authoring intent: a user reached this action. Latched
        // BEFORE any early return -- an attempted authoring act still means
        // a human was here, and keeping the row is the safe direction.
        _markAuthoringIntent()

        if (decorationId == null || decorationId === '') {
            // eslint-disable-next-line no-console
            console.warn('resetColorToAuto: decorationId is required')
            return false
        }
        if (!colorSku) {
            // eslint-disable-next-line no-console
            console.warn('resetColorToAuto: colorSku is required')
            return false
        }

        // Pending decoration: no server row to reset; the maps + pending
        // variant lists are cleared locally.
        const decoStr = String(decorationId)
        if (decoStr.startsWith('tmp-')) {
            const next = pendingVariants.value.map((v) => {
                if (!Array.isArray(v.color_skus)) return v
                if (!v.color_skus.includes(colorSku)) return v
                return {
                    ...v,
                    color_skus: v.color_skus.filter(s => s !== colorSku),
                }
            })
            pendingVariants.value = next
            _landRoutingMap('color_overrides', (m) => {
                delete m[colorSku]
                return m
            })
            _landRoutingMap('manual_ld', (m) => {
                delete m[colorSku]
                return m
            })
            return true
        }

        const url = `/api/placements/${decorationId}/reset_color`
        try {
            const resp = await axiosInstance.post(url, { color_sku: colorSku })
            _applyAffectedVariants(resp?.data?.variants)
            _landRoutingMap('color_overrides', (m) => {
                delete m[colorSku]
                return m
            })
            _landRoutingMap('manual_ld', (m) => {
                delete m[colorSku]
                return m
            })
            return true
        } catch (err) {
            saveErrors.value.push({
                message:
                    `Failed to reset color ${colorSku} on decoration `
                    + `${decorationId}: ${err?.message ?? err}`,
            })
            return false
        }
    }

    /**
     * Internal: collect the set of server-loaded skip-color skus for a given
     * decoration id. Returns an empty Set if the decoration has no server
     * skip_colors loaded (or if the decoration is pending, i.e. _tempId
     * prefix — pending decorations cannot have server skip_colors).
     */
    function _serverSkipSkusFor(decorationIdOrTempId) {
        const result = new Set()
        if (
            typeof decorationIdOrTempId === 'string'
            && decorationIdOrTempId.startsWith('tmp-')
        ) {
            return result
        }
        const included = serverState.value?.included
        if (!Array.isArray(included)) return result
        const decoIdStr = String(decorationIdOrTempId)
        for (const e of included) {
            if (e?.type !== 'decoration_skip_colors') continue
            if (getVariantParentId(e) !== decoIdStr) continue
            const sku = e?.attributes?.color_sku
            if (sku != null) result.add(sku)
        }
        return result
    }

    /**
     * Stage a skip-color toggle. Pebble 12 — routes to the correct bucket
     * based on whether the color is server-loaded:
     *
     * State machine (current effective state → action):
     *   - server-loaded ON, pendingSkipColors does NOT have it:
     *     toggle OFF → record DELETE in pendingSkipColorDeletes
     *   - server-loaded ON, pendingSkipColorDeletes has it (delete staged):
     *     toggle ON → remove from pendingSkipColorDeletes (restore)
     *   - server-loaded OFF, pendingSkipColors does NOT have it:
     *     toggle ON → add to pendingSkipColors
     *   - server-loaded OFF, pendingSkipColors has it:
     *     toggle OFF → remove from pendingSkipColors
     */
    function toggleSkipColor(decorationIdOrTempId, colorSku) {
        // Sticky authoring intent: a user reached this action. Latched
        // BEFORE any early return -- an attempted authoring act still means
        // a human was here, and keeping the row is the safe direction.
        _markAuthoringIntent()

        const key = decorationIdOrTempId
        const serverSkus = _serverSkipSkusFor(key)
        const isServerLoaded = serverSkus.has(colorSku)
        const pendingDeletes = pendingSkipColorDeletes.value.get(key)
        const isPendingDelete = !!pendingDeletes?.has(colorSku)
        const pendingAdds = pendingSkipColors.value.get(key)
        const isPendingAdd = !!pendingAdds?.has(colorSku)
        // Effective "on" iff (server-loaded AND not pending-delete) OR pending-add.
        const isEffectivelyOn
            = (isServerLoaded && !isPendingDelete) || isPendingAdd

        if (isEffectivelyOn) {
            // Toggle OFF.
            if (isServerLoaded && !isPendingAdd) {
                // Record a server-side delete.
                const newDeletes = new Map(pendingSkipColorDeletes.value)
                const set = newDeletes.get(key)
                    ? new Set(newDeletes.get(key))
                    : new Set()
                set.add(colorSku)
                newDeletes.set(key, set)
                pendingSkipColorDeletes.value = newDeletes
            } else if (isPendingAdd) {
                // Remove from pending adds.
                const newAdds = new Map(pendingSkipColors.value)
                const set = new Set(newAdds.get(key))
                set.delete(colorSku)
                if (set.size === 0) newAdds.delete(key)
                else newAdds.set(key, set)
                pendingSkipColors.value = newAdds
            }
        } else {
            // Toggle ON.
            if (isPendingDelete) {
                // Restore by removing the pending delete.
                const newDeletes = new Map(pendingSkipColorDeletes.value)
                const set = new Set(newDeletes.get(key))
                set.delete(colorSku)
                if (set.size === 0) newDeletes.delete(key)
                else newDeletes.set(key, set)
                pendingSkipColorDeletes.value = newDeletes
            } else {
                // Add to pending adds.
                const newAdds = new Map(pendingSkipColors.value)
                const set = newAdds.get(key)
                    ? new Set(newAdds.get(key))
                    : new Set()
                set.add(colorSku)
                newAdds.set(key, set)
                pendingSkipColors.value = newAdds
            }
        }
    }

    // PEBBLE 12 ENTRY POINT: addSkipColor is the idempotent version of
    // toggleSkipColor — always adds, never removes. Used by pebble 9's
    // "Skip these colors" contrast warning button where the caller
    // guarantees the color isn't already skipped, but doesn't want to
    // depend on toggle semantics (bar-raiser P2-3).
    function addSkipColor(decorationIdOrTempId, colorSku) {
        // Sticky authoring intent: a user reached this action. Latched
        // BEFORE any early return -- an attempted authoring act still means
        // a human was here, and keeping the row is the safe direction.
        _markAuthoringIntent()

        const key = decorationIdOrTempId
        const current = pendingSkipColors.value.get(key)
        if (current && current.has(colorSku)) {
            // Already present — no-op (skip the clone-on-write churn).
            return
        }
        const newMap = new Map(pendingSkipColors.value)
        const next = current ? new Set(current) : new Set()
        next.add(colorSku)
        newMap.set(key, next)
        pendingSkipColors.value = newMap
    }

    /**
     * Pebble 8b PR 1 — reorder a decoration to a new display_order.
     *
     * For SERVER decorations: records the edit in
     * `pendingDecorationOrderEdits` (clone-on-write). Save will emit a
     * PATCH per row.
     *
     * For PENDING decorations (idOrTempId starts with `tmp-`): mutates the
     * pending entry's `display_order` in place via clone-on-write. The
     * POST body sent at save time will carry the new value; no separate
     * PATCH is needed.
     *
     * Called by the per-row up/down buttons in DecorationCard. The
     * neighbor swap is orchestrated by the parent (DecorationsSection),
     * which calls this once for each side of the swap.
     *
     * @param {string|number} idOrTempId
     * @param {number} newOrder
     */
    function reorderDecoration(idOrTempId, newOrder) {
        // Sticky authoring intent: a user reached this action. Latched
        // BEFORE any early return -- an attempted authoring act still means
        // a human was here, and keeping the row is the safe direction.
        _markAuthoringIntent()

        if (
            typeof idOrTempId === 'string'
            && idOrTempId.startsWith('tmp-')
        ) {
            // Bar-raiser fix P1 #4: physically reorder the
            // pendingDecorations array so the change is observable in the
            // `decorations` getter (which assigns display_order = array
            // index for pending rows). Previously the action only stamped
            // display_order on the entry, but the getter overwrote it.
            const idx = pendingDecorations.value.findIndex(
                d => d._tempId === idOrTempId
            )
            if (idx === -1) return
            const next = pendingDecorations.value.slice()
            const [moved] = next.splice(idx, 1)
            // Clamp target index into [0, next.length] after the splice.
            const target = Math.max(
                0,
                Math.min(Number.isFinite(newOrder) ? newOrder : 0, next.length)
            )
            next.splice(target, 0, moved)
            pendingDecorations.value = next
            return
        }
        // Server row — record the edit.
        const newMap = new Map(pendingDecorationOrderEdits.value)
        newMap.set(idOrTempId, newOrder)
        pendingDecorationOrderEdits.value = newMap
    }

    /**
     * Pebble 8b PR 1 — reorder a variant to a new display_order. Same
     * server-vs-pending routing as reorderDecoration.
     *
     * @param {string|number} idOrTempId
     * @param {number} newOrder
     */
    function reorderVariant(idOrTempId, newOrder) {
        // Sticky authoring intent: a user reached this action. Latched
        // BEFORE any early return -- an attempted authoring act still means
        // a human was here, and keeping the row is the safe direction.
        _markAuthoringIntent()

        if (
            typeof idOrTempId === 'string'
            && idOrTempId.startsWith('tmp-')
        ) {
            const idx = pendingVariants.value.findIndex(
                v => v._tempId === idOrTempId
            )
            if (idx === -1) return
            const next = pendingVariants.value.slice()
            next[idx] = { ...next[idx], display_order: newOrder }
            pendingVariants.value = next
            return
        }
        const newMap = new Map(pendingVariantOrderEdits.value)
        newMap.set(idOrTempId, newOrder)
        pendingVariantOrderEdits.value = newMap
    }

    /**
     * Pebble 8b PR 1 — duplicate a decoration into the pending bucket.
     * Shape-only: copies placement_id, name, description, and
     * display_order onto a new entry with a fresh `_tempId`. Does NOT
     * duplicate variants — the user re-uploads artwork if they want
     * different art on the copy. Source row is unchanged.
     *
     * Source may be a server decoration (looked up in
     * serverState.included) or a pending decoration (looked up in
     * pendingDecorations).
     *
     * Bar-raiser fix P1 #5: display_order is now actually carried onto
     * the new pending entry (was promised in the original docstring but
     * dropped on the floor). The pending row is appended to
     * pendingDecorations; on save the carried display_order is sent in
     * the POST body so the new row lands next to the source server-side.
     *
     * @param {string|number} idOrTempId
     */
    function duplicateDecoration(idOrTempId) {
        // Sticky authoring intent: a user reached this action. Latched
        // BEFORE any early return -- an attempted authoring act still means
        // a human was here, and keeping the row is the safe direction.
        _markAuthoringIntent()

        let source = null
        if (
            typeof idOrTempId === 'string'
            && idOrTempId.startsWith('tmp-')
        ) {
            source = pendingDecorations.value.find(
                d => d._tempId === idOrTempId
            )
        } else {
            const included = serverState.value?.included ?? []
            const entry = included.find(
                e => isEntityType(e, 'decorations') && e?.id === idOrTempId
            )
            if (entry) {
                const attrs = entry.attributes ?? {}
                source = {
                    placement_id: attrs.placement_id,
                    name: attrs.name ?? '',
                    description: attrs.description ?? null,
                    decoration_type: attrs.decoration_type ?? 'embroidery',
                    display_order: Number.isFinite(attrs.display_order)
                        ? attrs.display_order
                        : 0,
                }
            }
        }
        if (!source) return
        const newEntry = {
            _tempId: nextTempId(),
            placement_id: source.placement_id,
            name: source.name ?? '',
            description: source.description ?? null,
            decoration_type: source.decoration_type ?? 'embroidery',
            display_order: source.display_order ?? 0,
        }
        pendingDecorations.value = [...pendingDecorations.value, newEntry]
    }

    /**
     * Pebble 8b PR 1 -- duplicate a variant into the pending bucket.
     *
     * Duplicate semantics are keyed on (kind, color_skus). The new
     * entry copies
     * ``parent``, ``kind``, ``color_skus``, ``ink_color_hex``, and
     * ``display_order`` onto a fresh ``_tempId``. Artwork fields are NOT
     * carried over -- the pebble 8a ``_revokeAllPendingVariantUrls``
     * logic assumes one URL per pending variant, so sharing a blob URL
     * between source and copy would corrupt revoke discipline. The user
     * re-uploads artwork if they want it on the duplicate.
     *
     * Dedup: the same routing rules as ``addVariant`` apply. For
     * catch-all kinds the source kind is, by definition, already present
     * (it IS the source) -- duplicating would create two variants of
     * the same kind on the same decoration. The duplicate is refused
     * (the function is a no-op). The legitimate user intent here is
     * "I want a second variant of the same routing identity" which the
     * (kind, color_skus) model rejects by construction.
     *
     * For specific_colors a duplicate WITHOUT changing color_skus also
     * collides (the source SKUs are already routed by the source). We
     * keep the function symmetric: the duplicate is staged with empty
     * color_skus, leaving the user to pick fresh SKUs in the inspector.
     * In practice today's UI doesn't expose "duplicate specific_colors";
     * the helper covers it for shape symmetry.
     *
     * Source may be a server variant (looked up in serverState.included)
     * or a pending variant (looked up in pendingVariants).
     *
     * @param {string|number} idOrTempId
     */
    function duplicateVariant(idOrTempId) {
        // Sticky authoring intent: a user reached this action. Latched
        // BEFORE any early return -- an attempted authoring act still means
        // a human was here, and keeping the row is the safe direction.
        _markAuthoringIntent()

        let source = null
        if (
            typeof idOrTempId === 'string'
            && idOrTempId.startsWith('tmp-')
        ) {
            source = pendingVariants.value.find(
                v => v._tempId === idOrTempId
            )
        } else {
            const included = serverState.value?.included ?? []
            const entry = included.find(
                e => isEntityType(e, 'variants') && e?.id === idOrTempId
            )
            if (entry) {
                const attrs = entry.attributes ?? {}
                const parentId = getVariantParentId(entry)
                source = {
                    parent: parentId,
                    kind: attrs.kind ?? 'catch_all_light',
                    color_skus: Array.isArray(attrs.color_skus)
                        ? attrs.color_skus.slice()
                        : [],
                    ink_color_hex: attrs.ink_color_hex ?? null,
                    display_order: Number.isFinite(attrs.display_order)
                        ? attrs.display_order
                        : 0,
                }
            }
        }
        if (!source) return null

        const sourceKind = source.kind ?? 'catch_all_light'
        // Always stage with empty color_skus. (Until 2026-08-24 this was
        // a ternary on sourceKind whose two arms were both `[]` -- dead
        // code from when the specific_colors arm differed.) Dup engages
        // addVariant's same-kind guard: pending source returns the
        // existing tempId (no new entry); server source returns null.
        // The dedup discipline lives in addVariant; duplicateVariant is
        // a thin shim.
        const sourceTempId = source._tempId ?? null
        const tempId = addVariant({
            decorationId: source.parent,
            kind: sourceKind,
            color_skus: [],
        })
        if (!tempId) return null
        // If addVariant short-circuited and returned the EXISTING source
        // tempId (pending-side dedup), we did not stage a new entry --
        // do not mutate the source's ink_color_hex / display_order.
        if (tempId === sourceTempId) return tempId
        // Carry the non-routing-identity bits onto the newly staged
        // entry only.
        const idx = pendingVariants.value.findIndex(
            v => v._tempId === tempId,
        )
        if (idx !== -1) {
            const next = pendingVariants.value.slice()
            next[idx] = {
                ...next[idx],
                ink_color_hex: source.ink_color_hex ?? null,
                display_order: source.display_order ?? 0,
            }
            pendingVariants.value = next
        }
        return tempId
    }

    // PEBBLE 8b PR 3 — Personalization field actions.
    //
    /**
     * Stage a new personalization field under a decoration. `parent` is
     * either a server-side decoration id string OR a pending decoration
     * `_tempId`. Save resolves _tempId parents via tempIdToServerId.
     *
     * `display_order` defaults to the count of existing fields (server
     * + pending) for the same parent — keeps new rows at the bottom by
     * default; reorder is a P2 follow-up.
     *
     * @param {string|number} decorationIdOrTempId
     * @param {{label?: string, field_type?: string, required?: boolean,
     *          max_length?: number|null, options?: Array<string>|null}} fields
     * @returns {string} the new field's `_tempId`
     */
    function addPersonalizationField(decorationIdOrTempId, fields = {}) {
        // Sticky authoring intent: a user reached this action. Latched
        // BEFORE any early return -- an attempted authoring act still means
        // a human was here, and keeping the row is the safe direction.
        _markAuthoringIntent()

        // Count existing fields for this parent to pick a sensible
        // display_order default.
        let max = -1
        const included = serverState.value?.included
        if (Array.isArray(included)) {
            for (const entry of included) {
                if (!isEntityType(entry, 'personalization_fields')) continue
                const parentId = getVariantParentId(entry)
                if (parentId !== decorationIdOrTempId) continue
                if (pendingPersonalizationFieldDeletes.value.has(entry.id)) {
                    continue
                }
                const order = entry.attributes?.display_order
                if (Number.isFinite(order) && order > max) max = order
            }
        }
        for (const pf of pendingPersonalizationFields.value) {
            if (pf.parent !== decorationIdOrTempId) continue
            if (Number.isFinite(pf.display_order) && pf.display_order > max) {
                max = pf.display_order
            }
        }
        const newEntry = {
            _tempId: nextTempId(),
            parent: decorationIdOrTempId,
            label: fields.label ?? '',
            field_type: fields.field_type ?? 'text',
            required: !!fields.required,
            max_length: fields.max_length ?? null,
            options: fields.options ?? null,
            display_order: max + 1,
        }
        pendingPersonalizationFields.value = [
            ...pendingPersonalizationFields.value,
            newEntry,
        ]
        return newEntry._tempId
    }

    /**
     * Update a personalization field. Routes by id shape:
     *   - `_tempId` (string starting with `tmp-`): updates the pending
     *     entry in place (clone-on-write) so the getter sees the change.
     *   - Anything else (server id): merges the patch into the
     *     pendingPersonalizationFieldEdits Map; later edits to the same id
     *     merge into the same patch object (last-writer-wins per attribute).
     *
     * The patch is a partial attribute set — caller passes only the
     * changed fields. Pebble 8b PR 3 does not yet diff against
     * serverState to drop no-op edits; if the UI sends identical values
     * the Map entry sticks until save / revert clears it.
     *
     * @param {string|number} idOrTempId
     * @param {object} patch — partial attribute patch
     */
    function updatePersonalizationField(idOrTempId, patch) {
        // Sticky authoring intent: a user reached this action. Latched
        // BEFORE any early return -- an attempted authoring act still means
        // a human was here, and keeping the row is the safe direction.
        _markAuthoringIntent()

        if (!patch || typeof patch !== 'object') return
        if (
            typeof idOrTempId === 'string'
            && idOrTempId.startsWith('tmp-')
        ) {
            const idx = pendingPersonalizationFields.value.findIndex(
                pf => pf._tempId === idOrTempId
            )
            if (idx === -1) return
            const next = pendingPersonalizationFields.value.slice()
            next[idx] = { ...next[idx], ...patch }
            pendingPersonalizationFields.value = next
            return
        }
        // Server-side row — merge patch into the edit Map.
        const newMap = new Map(pendingPersonalizationFieldEdits.value)
        const existing = newMap.get(idOrTempId) ?? {}
        newMap.set(idOrTempId, { ...existing, ...patch })
        pendingPersonalizationFieldEdits.value = newMap
    }

    /**
     * Remove a personalization field. Routes by id shape:
     *   - `_tempId`: drops the pending entry from
     *     pendingPersonalizationFields entirely (never sent to server).
     *   - Server id: adds the id to pendingPersonalizationFieldDeletes;
     *     save emits a DELETE; the getter filters it out. Also drops any
     *     pending edit for the same id (deletion supersedes edits).
     *
     * @param {string|number} idOrTempId
     */
    function removePersonalizationField(idOrTempId) {
        // Sticky authoring intent: a user reached this action. Latched
        // BEFORE any early return -- an attempted authoring act still means
        // a human was here, and keeping the row is the safe direction.
        _markAuthoringIntent()

        if (
            typeof idOrTempId === 'string'
            && idOrTempId.startsWith('tmp-')
        ) {
            pendingPersonalizationFields.value
                = pendingPersonalizationFields.value.filter(
                    pf => pf._tempId !== idOrTempId
                )
            return
        }
        // Server row — stage a DELETE and drop any in-flight edit.
        const newDeletes = new Set(pendingPersonalizationFieldDeletes.value)
        newDeletes.add(idOrTempId)
        pendingPersonalizationFieldDeletes.value = newDeletes
        if (pendingPersonalizationFieldEdits.value.has(idOrTempId)) {
            const newEdits = new Map(pendingPersonalizationFieldEdits.value)
            newEdits.delete(idOrTempId)
            pendingPersonalizationFieldEdits.value = newEdits
        }
    }

    // PR-N4: variant_key is gone. Dedup is keyed on
    // (kind, color_skus); see addVariant + duplicateVariant.

    return {
        // state
        campaignProductId,
        serverState,
        dirty,
        loading,
        loadError,
        saveInFlight,
        saveError,
        saveErrors,
        supplierMeta,
        unsellableColorNotice,
        supplierKey,
        supplierKeyResolved,
        supplierMetaLoading,
        supplierMetaFailed,
        pendingSwapCleanup,
        skuClearedForSwitch,
        supplierSwitchSnapshot,
        setSupplierSwitchSnapshot,
        supplierMetaReady,
        supplierMetaSettled,
        priceBreaks,
        priceBreaksKey,
        priceBreaksError,
        fieldAutoSaveState,
        saveCount,
        placementSaveCount,
        notePlacementSaved,
        pendingDecorations,
        pendingVariants,
        pendingSkipColors,
        pendingSkipColorDeletes,
        pendingDecorationOrderEdits,
        pendingVariantOrderEdits,
        pendingDecorationDeletes,
        pendingPersonalizationFields,
        pendingPersonalizationFieldEdits,
        pendingPersonalizationFieldDeletes,
        // getters
        isDirty,
        dirtyFieldCount,
        unsavedChangeCount,
        hasUnsavedChanges,
        hasUnsavedStructuralChanges,
        hasAuthoringIntent,
        hasEverHadSku,
        clearEverHadSku,
        canSave,
        decorations,
        // actions
        openEditor,
        closeEditor,
        loadServerState,
        updateField,
        deselectSize,
        restoreFields,
        getEffectiveAttribute,
        setHideFromStorefront,
        setStoreCategoryId,
        revert,
        save,
        saveDirtyFields,
        flushFieldAutoSave,
        flushFieldAutoSaveOnUnload,
        uploadColorPreview,
        removeColorPreview,
        colorPreviewStatus,
        isFieldEditQueuedBehindFlight,
        setSupplierMeta,
        setSupplierKey,
        loadPriceBreaks,
        saveManualBlankCost,
        acknowledgeSupplierCosts,
        setSupplierMetaLoading,
        setSupplierMetaFailed,
        setPendingSwapCleanup,
        setSkuClearedForSwitch,
        addDecoration,
        removeDecoration,
        deletePlacement,
        addVariant,
        removeVariant,
        updateVariantArtwork,
        updateVariantInk,
        bindVariantArtwork,
        fetchArtworkRebindScope,
        replaceArtwork,
        setPerColorApproval,
        deleteVariant,
        renameVariant,
        setVariantKind,
        setVariantColorway,
        splitPlacementShades,
        isColorwayBindPending,
        mergeVariantShades,
        assignColor,
        resetColorToAuto,
        toggleSkipColor,
        addSkipColor,
        reorderDecoration,
        reorderVariant,
        duplicateDecoration,
        duplicateVariant,
        addPersonalizationField,
        updatePersonalizationField,
        removePersonalizationField,
        // PR-N4: nextFreeVariantKey export removed; variant_key column dropped.
    }
})
